import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaBluetoothB,
  FaConciergeBell,
  FaPrint,
  FaTrash,
  FaBolt,
  FaSave,
  FaUsb,
} from "react-icons/fa";

/* ---------- Firebase (web, modular) ---------- */
import { initializeApp, getApps } from "firebase/app";
import { getDatabase, ref, get, set } from "firebase/database";
import {
  resetUsbPrinterConnection,
  writeUsb,
} from "../utils/webUsbPrinter";
import {
  APP_ESCAPE_EVENT,
  APP_SHORTCUT_EVENT,
  formatShortcutCombo,
  focusAndSelect,
  focusFirstVisible,
  getShortcutById,
} from "../utils/shortcuts";

/* ---------- helpers (image + price fields like mobile) ---------- */
const BASE_PUBLIC = "https://qr.billsarthi.in/public/";
const buildImageUrl = (p) => {
  const candidate =
    p?.image_path ||
    p?.photo_path ||
    p?.imagepath ||
    p?.image_url ||
    p?.imageUrl ||
    p?.image ||
    p?.photo ||
    p?.imageurl ||
    p?.img ||
    "";
  const val = String(candidate || "").trim();
  if (!val || val === "null" || val === "undefined") return "";
  if (/^https?:\/\//i.test(val)) {
    // force https for known domain to avoid mixed content
    if (val.startsWith("http://qr.billsarthi.in")) {
      return val.replace("http://", "https://");
    }
    return val;
  }
  return `${BASE_PUBLIC}${val.replace(/^\/?/, "")}`;
};
const toSafeNumber = (value) => {
  if (value == null) return NaN;
  if (typeof value === "number") return value;
  const raw = String(value).trim();
  if (!raw) return NaN;
  const direct = Number(raw);
  if (Number.isFinite(direct)) return direct;
  const cleaned = raw.replace(/[^0-9.-]/g, "");
  if (!cleaned) return NaN;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : NaN;
};

// Mobile parity: use tax-inclusive price first, with broad fallback coverage.
const priceOf = (p) => {
  const directCandidates = [
    p?.taxedprice,
    p?.priceaftertax,
    p?.price,
    p?.price_after_tax,
    p?.rate,
    p?.unit_price,
    p?.item_price,
  ];
  for (const candidate of directCandidates) {
    const n = toSafeNumber(candidate);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const amount = toSafeNumber(p?.amount ?? p?.line_total ?? p?.total);
  const qty = toSafeNumber(p?.quantity ?? p?.qty);
  if (Number.isFinite(amount) && amount > 0 && Number.isFinite(qty) && qty > 0) {
    return amount / qty;
  }
  return 0;
};
const parsePct = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const readStorageBool = (key, fallback) => {
  const raw = localStorage.getItem(key);
  if (raw === null) return fallback;
  return raw === "true";
};
const readStorageString = (key, fallback = "") =>
  (localStorage.getItem(key) || fallback || "").toString();
const rememberBleDevice = (device) => {
  try {
    const id = device?.id || "";
    const label = String(device?.name || id || "Connected Printer");
    if (id) {
      localStorage.setItem("ble_last_device_id", id);
      localStorage.setItem("printerConnectedId", id);
    }
    localStorage.setItem("printerConnected", label);
  } catch {
    // ignore storage errors
  }
};
const sizeToQrPixels = (size, fallback = 220) => {
  if (size === "small") return 120;
  if (size === "large") return 176;
  return Math.min(Number(fallback) || 150, 150);
};
const sizeToLogoPixels = (size, fallback = 320) => {
  if (size === "small") return 110;
  if (size === "large") return 170;
  return Math.min(Number(fallback) || 140, 140);
};

const DEFAULT_KOT_TAGS = [
  "Less Spicy",
  "Extra Spicy",
  "No Onion",
  "No Garlic",
  "Jain",
  "Parcel",
];

const parseQuickTags = (value) => {
  if (Array.isArray(value)) {
    return value.map((t) => String(t || "").trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(/[,\n|]+/g)
      .map((t) => t.trim())
      .filter(Boolean);
  }
  return DEFAULT_KOT_TAGS;
};

const partyCompanyOf = (party = {}) =>
  String(
    party?.companyname ??
      party?.company_name ??
      party?.partycompanyname ??
      party?.partycompany ??
      party?.companyName ??
      party?.company ??
      ""
  ).trim();

const splitNotesTokens = (notes) => {
  return String(notes || "")
    .split(/[\n,]+/g)
    .map((t) => t.trim())
    .filter(Boolean);
};

const hasTagInNotes = (notes, tag) => {
  const needle = String(tag || "").trim().toLowerCase();
  if (!needle) return false;
  return splitNotesTokens(notes).some((t) => t.toLowerCase() === needle);
};

const addTagToNotes = (notes, tag) => {
  const cleanTag = String(tag || "").trim();
  if (!cleanTag) return notes || "";
  if (hasTagInNotes(notes, cleanTag)) return notes || "";
  const base = String(notes || "").trim();
  if (!base) return cleanTag;
  if (base.endsWith(",")) return `${base} ${cleanTag}`;
  return `${base}, ${cleanTag}`;
};

const removeTagFromNotes = (notes, tag) => {
  const needle = String(tag || "").trim().toLowerCase();
  if (!needle) return notes || "";
  const remaining = splitNotesTokens(notes).filter(
    (t) => t.toLowerCase() !== needle
  );
  return remaining.join(", ");
};

const normalizeSplitPayload = (raw) => {
  if (!raw) return null;
  let obj = raw;
  if (typeof raw === "string") {
    try {
      obj = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!obj || typeof obj !== "object") return null;
  const cash = Number(obj.cash || 0);
  const upi = Number(obj.upi || 0);
  const card = Number(obj.card || 0);
  const credit = Number(obj.credit || 0);
  const total = Number(
    obj.total != null ? obj.total : cash + upi + card + credit
  );
  return { cash, upi, card, credit, total, methods: obj.methods || [] };
};

const waiterLabel = (w) =>
  w?.name || w?.waitername || w?.waiter_name || `Waiter ${w?.id || ""}`;

/* ---------- variants + addons (mobile parity) ---------- */
const buildLineKey = (productId, variantId, addonIds = []) => {
  const addonKey =
    Array.isArray(addonIds) && addonIds.length
      ? addonIds.slice().sort((a, b) => a - b).join("_")
      : "none";
  const variantKey = variantId ? `v${variantId}` : "base";
  return `p${productId}-${variantKey}-a${addonKey}`;
};

const parseProductIdFromKey = (key) => {
  if (!key) return null;
  const match = String(key).match(/p(\d+)/i);
  if (!match) return null;
  const id = Number(match[1]);
  return Number.isFinite(id) ? id : null;
};

const getAddonTotals = (addons = []) => {
  return addons.reduce(
    (acc, addon) => {
      const price = Number(addon?.price ?? 0);
      const priceaftertax = Number(addon?.priceaftertax ?? addon?.price ?? 0);
      const taxedprice = Number(
        addon?.taxedprice ?? addon?.priceaftertax ?? addon?.price ?? 0
      );
      const taxamount = Number(addon?.taxamount ?? 0);
      const sgst = Number(addon?.sgst ?? 0);
      const cgst = Number(addon?.cgst ?? 0);
      acc.price += price;
      acc.priceaftertax += priceaftertax;
      acc.taxedprice += taxedprice;
      acc.taxamount += taxamount;
      acc.sgst += sgst;
      acc.cgst += cgst;
      return acc;
    },
    {
      price: 0,
      priceaftertax: 0,
      taxedprice: 0,
      taxamount: 0,
      sgst: 0,
      cgst: 0,
    }
  );
};

const buildVariantLine = (product, variant, addons = []) => {
  const variantPrice = Number(variant?.price ?? product?.price ?? 0);
  const variantPriceAfterTax = Number(
    variant?.priceaftertax ?? product?.priceaftertax ?? variantPrice
  );
  const variantTaxedPrice = Number(
    variant?.taxedprice ?? product?.taxedprice ?? variantPriceAfterTax
  );
  const variantTaxAmount = Number(variant?.taxamount ?? product?.taxamount ?? 0);
  const variantSgst = Number(variant?.sgst ?? product?.sgst ?? 0);
  const variantCgst = Number(variant?.cgst ?? product?.cgst ?? 0);
  const addonTotals = getAddonTotals(addons);

  const lineKey = buildLineKey(
    product?.id,
    variant?.id ?? null,
    addons.map((addon) => addon.id)
  );

  const variantLabel = variant?.label ? String(variant.label) : "";
  const addonsLabel = addons.length
    ? addons.map((addon) => addon.name).join(", ")
    : "";
  const nameSuffix = variantLabel ? ` (${variantLabel})` : "";

  return {
    lineKey,
    productid: product?.id,
    variantid: variant?.id ?? null,
    addon_ids: addons.map((addon) => addon.id),
    addon_label: addonsLabel,
    quantity: 1,
    price: variantPrice + addonTotals.price,
    priceaftertax: variantPriceAfterTax + addonTotals.priceaftertax,
    taxedprice: variantTaxedPrice + addonTotals.taxedprice,
    taxamount: variantTaxAmount + addonTotals.taxamount,
    sgst: variantSgst + addonTotals.sgst,
    cgst: variantCgst + addonTotals.cgst,
    name: `${product?.name ?? "Item"}${nameSuffix}`,
    base_name: product?.name ?? "Item",
    variant_label: variantLabel,
    addons,
    product,
    isVariantLine: true,
  };
};

const splitLineItems = (lineItems = []) => {
  const baseMap = {};
  const optionLines = [];
  lineItems.forEach((line) => {
    if (!line) return;
    const productId = Number(line.productid || line.product_id);
    const qty = Number(line.quantity ?? line.qty ?? 0);
    if (!productId || qty <= 0) return;
    const addonIds = Array.isArray(line.addon_ids)
      ? line.addon_ids
      : Array.isArray(line.addons)
      ? line.addons
      : [];
    const hasOptions = line.variantid || addonIds.length;
    if (!hasOptions) {
      baseMap[productId] = (Number(baseMap[productId]) || 0) + qty;
      return;
    }
    const lineKey =
      line.lineKey || buildLineKey(productId, line.variantid ?? null, addonIds);
    optionLines.push({
      ...line,
      lineKey,
      productid: productId,
      quantity: qty,
      addon_ids: addonIds,
      isVariantLine: true,
    });
  });
  return { baseMap, optionLines };
};

const buildLineItemsPayload = (lines = []) => {
  return lines
    .map((line) => ({
      lineKey: line.lineKey,
      productid: line.productid,
      variantid: line.variantid ?? null,
      addon_ids: line.addon_ids || [],
      quantity: Number(line.quantity || 0),
      price: Number(line.price || 0),
      priceaftertax: Number(line.priceaftertax ?? line.price ?? 0),
      taxedprice: Number(line.taxedprice ?? line.priceaftertax ?? line.price ?? 0),
      taxamount: Number(line.taxamount ?? 0),
      sgst: Number(line.sgst ?? 0),
      cgst: Number(line.cgst ?? 0),
      name: line.name,
      addon_label: line.addon_label || "",
      variant_label: line.variant_label || "",
    }))
    .filter((line) => Number(line.quantity || 0) > 0);
};

/* ---------- shared parity helpers ---------- */
const DEFAULT_TAKEAWAY_NAME = "TakeAway-1";
const SELECTED_CATEGORY_ID = "__selected";
const ORDER_CREATED_TONE_KEY = "pos_order_created_tone_enabled";
const isTakeawayName = (name) =>
  typeof name === "string" && name.toLowerCase().startsWith("takeaway");
// Extract a numeric table identifier when the label contains digits (e.g., "Room no 190")
const extractTableNumber = (value) => {
  if (value === undefined || value === null) return null;
  const match = String(value).match(/(\d+)/);
  if (!match) return null;
  const num = Number(match[1]);
  return Number.isFinite(num) ? num : null;
};

// Normalize any table identifier into the integer expected by the API.
const normalizeTableIdForApi = (value) => {
  if (isTakeawayName(value)) return 0;
  const direct = Number(value);
  if (Number.isFinite(direct)) return direct;
  const extracted = extractTableNumber(value);
  return extracted ?? 0;
};
const extractTakeawayIndex = (name) => {
  if (!name) return null;
  const lower = String(name).toLowerCase().trim();
  if (lower === "takeaway") return 1;
  const m = lower.match(/takeaway[\s-]*(\d+)/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isNaN(n) ? null : n;
};
const canonicalizeTakeawayName = (name) => {
  if (!isTakeawayName(name)) return DEFAULT_TAKEAWAY_NAME;
  const idx = extractTakeawayIndex(name);
  return `TakeAway-${idx && idx > 0 ? idx : 1}`;
};

// Build a lookup map for tables (key -> meta with apiId/type/display)
const buildTableMetaMap = (list = []) => {
  const map = {};
  if (!Array.isArray(list)) return map;
  list.forEach((entry) => {
    const key =
      entry?.table_key ??
      entry?.tableKey ??
      entry?.key ??
      entry?.name ??
      entry?.table ??
      entry?.tableid ??
      entry?.tableId ??
      entry?.id;
    const displayName =
      entry?.display_name ??
      entry?.displayName ??
      entry?.name ??
      entry?.table ??
      entry?.table_key ??
      entry?.tableKey ??
      entry?.tableid ??
      entry?.tableId ??
      entry?.id ??
      "";
    const apiId =
      entry?.tableid ??
      entry?.tableId ??
      entry?.table_id ??
      entry?.id ??
      extractTableNumber(entry?.name);
    const typeRaw =
      entry?.table_type ??
      entry?.tableType ??
      entry?.tabletype ??
      entry?.type;
    const type = String(typeRaw || "").toLowerCase() === "room" ? "room" : "table";
    const meta = { displayName, apiId, type };
    const addKey = (k) => {
      if (k === null || k === undefined || k === "") return;
      map[String(k)] = meta;
    };
    addKey(key);
    addKey(entry?.id);
    addKey(entry?.tableid);
    addKey(entry?.tableId);
    addKey(entry?.table_id);
  });
  return map;
};
/* ---------- Web Bluetooth helpers for direct print ---------- */
const ESC = 0x1b;
const GS = 0x1d;
const INIT_CMD = new Uint8Array([ESC, 0x40]);
const CUT_CMD = new Uint8Array([GS, 0x56, 0x00]);
const ALIGN_LEFT = new Uint8Array([ESC, 0x61, 0x00]);
const ALIGN_CENTER = new Uint8Array([ESC, 0x61, 0x01]);
const BLE_LINE_WIDTH = 32; // default width for 58mm printers
const NAME_W = 14;
const QTY_W = 3;
const RATE_W = 6;
const AMT_W = 7;
const DEL_NAME_W = 26;
const DEL_QTY_W = 6;
const PRINT_PROFILES = {
  "2in": {
    lineWidth: 32,
    nameW: 14,
    qtyW: 3,
    rateW: 6,
    amtW: 7,
    delNameW: 26,
    delQtyW: 6,
    logoWidth: 140,
    qrSize: 150,
    taxCols: { taxable: 8, cgst: 7, sgst: 7, total: 9 },
  },
  "3in": {
    lineWidth: 48,
    nameW: 24,
    qtyW: 4,
    rateW: 8,
    amtW: 11,
    delNameW: 38,
    delQtyW: 8,
    logoWidth: 170,
    qrSize: 176,
    taxCols: { taxable: 12, cgst: 12, sgst: 12, total: 12 },
  },
  "4in": {
    lineWidth: 64,
    nameW: 32,
    qtyW: 5,
    rateW: 10,
    amtW: 13,
    delNameW: 50,
    delQtyW: 10,
    logoWidth: 200,
    qrSize: 208,
    taxCols: { taxable: 16, cgst: 16, sgst: 16, total: 16 },
  },
  a4: {
    lineWidth: 72,
    nameW: 38,
    qtyW: 6,
    rateW: 12,
    amtW: 15,
    delNameW: 58,
    delQtyW: 12,
    logoWidth: 220,
    qrSize: 224,
    taxCols: { taxable: 18, cgst: 18, sgst: 18, total: 18 },
  },
};
const normalizePrinterWidthPreference = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return null;
  if (raw === "a4" || raw === "a4-size" || raw === "a4size") {
    return "a4";
  }
  if (
    raw === "4" ||
    raw === "4in" ||
    raw === "4inch" ||
    raw === "4-inch" ||
    raw === "4 in" ||
    raw === "112mm" ||
    raw === "104mm" ||
    raw === "102mm" ||
    raw === "100mm"
  ) {
    return "4in";
  }
  if (
    raw === "3" ||
    raw === "3in" ||
    raw === "3inch" ||
    raw === "3-inch" ||
    raw === "80mm" ||
    raw === "76mm" ||
    raw === "72mm" ||
    raw === "80"
  ) {
    return "3in";
  }
  if (
    raw === "2" ||
    raw === "2in" ||
    raw === "2inch" ||
    raw === "2-inch" ||
    raw === "58mm" ||
    raw === "58"
  ) {
    return "2in";
  }
  return null;
};
const getPaperSpec = (widthKey) => {
  const normalized = normalizePrinterWidthPreference(widthKey) || "2in";
  if (normalized === "a4") {
    return { key: "a4", size: "A4", bodyWidth: "190mm", margin: "10mm", thermal: false };
  }
  if (normalized === "4in") {
    return { key: "4in", size: "104mm auto", bodyWidth: "104mm", margin: "4mm", thermal: true };
  }
  if (normalized === "3in") {
    return { key: "3in", size: "80mm auto", bodyWidth: "80mm", margin: "4mm", thermal: true };
  }
  return { key: "2in", size: "58mm auto", bodyWidth: "58mm", margin: "4mm", thermal: true };
};
const SIZE_ON = "\x1b\x21\x30";
const SIZE_OFF = "\x1b\x21\x00";
const buildSizedCenteredLines = (text, width, sizeKey) => {
  const trimmed = (text || "").toString().trim();
  if (!trimmed) return [];
  const isLarge = sizeKey === "large";
  const lineWidth = isLarge ? Math.max(1, Math.floor(width / 2)) : width;
  const lines = wrapText(trimmed, lineWidth);
  if (!isLarge) return lines.map((line) => centerLine(line, width));
  return lines.map((line) => `${SIZE_ON}${centerLine(line, width)}${SIZE_OFF}`);
};
const chunkBytes = (bytes, size = 20) => {
  const out = [];
  for (let i = 0; i < bytes.length; i += size) out.push(bytes.slice(i, i + size));
  return out;
};
const centerLine = (text = "", width = BLE_LINE_WIDTH) => {
  const s = String(text || "").trim();
  if (!s) return "";
  if (s.length >= width) return s.slice(0, width);
  const gap = width - s.length;
  const left = Math.floor(gap / 2);
  return " ".repeat(left) + s + " ".repeat(gap - left);
};
const padR = (txt, w) => {
  const s = String(txt ?? "");
  return (s + " ".repeat(Math.max(0, w - s.length))).slice(0, w);
};
const padL = (txt, w) => {
  const s = String(txt ?? "");
  return (" ".repeat(Math.max(0, w - s.length)) + s).slice(-w);
};
const wrapNameCol = (name, width = NAME_W) => {
  const s = String(name || "-");
  const out = [];
  let remaining = s.trim();
  while (remaining.length) {
    if (remaining.length <= width) {
      out.push(remaining);
      break;
    }
    // prefer breaking on last space within width
    const slice = remaining.slice(0, width + 1);
    const lastSpace = slice.lastIndexOf(" ");
    if (lastSpace > 0) {
      out.push(slice.slice(0, lastSpace));
      remaining = remaining.slice(lastSpace + 1);
    } else {
      out.push(remaining.slice(0, width));
      remaining = remaining.slice(width);
    }
    remaining = remaining.trimStart();
  }
  return out.length ? out : ["-"];
};
const wrapText = (text, width) => {
  const s = String(text || "-");
  const out = [];
  let remaining = s.trim();
  while (remaining.length) {
    if (remaining.length <= width) {
      out.push(remaining);
      break;
    }
    const slice = remaining.slice(0, width + 1);
    const lastSpace = slice.lastIndexOf(" ");
    if (lastSpace > 0) {
      out.push(slice.slice(0, lastSpace));
      remaining = remaining.slice(lastSpace + 1);
    } else {
      out.push(remaining.slice(0, width));
      remaining = remaining.slice(width);
    }
    remaining = remaining.trimStart();
  }
  return out.length ? out : ["-"];
};
const col4 = (name, qty, rate, amt, widths = {}) => {
  const nameW = widths.nameW ?? NAME_W;
  const qtyW = widths.qtyW ?? QTY_W;
  const rateW = widths.rateW ?? RATE_W;
  const amtW = widths.amtW ?? AMT_W;
  const gap = widths.gap ?? " ";
  return (
    padR(String(name ?? ""), nameW) +
    padL(String(qty ?? ""), qtyW) +
    gap +
    padL(String(rate ?? ""), rateW) +
    padL(String(amt ?? ""), amtW)
  );
};
const col2 = (name, qty, widths = {}) => {
  const nameW = widths.nameW ?? DEL_NAME_W;
  const qtyW = widths.qtyW ?? DEL_QTY_W;
  return padR(String(name ?? ""), nameW) + padL(String(qty ?? ""), qtyW);
};
const canvasFromImage = async (src, maxWidth = 384, timeoutMs = 2200) => {
  if (!src) return null;
  return new Promise((resolve, reject) => {
    const img = new Image();
    const timer = setTimeout(() => {
      reject(new Error("Image load timeout"));
    }, timeoutMs);
    img.crossOrigin = "anonymous";
    img.onload = () => {
      clearTimeout(timer);
      const ratio = img.width ? Math.min(1, maxWidth / img.width) : 1;
      const w = Math.max(1, Math.round(img.width * ratio));
      const h = Math.max(1, Math.round(img.height * ratio));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas);
    };
    img.onerror = (e) => {
      clearTimeout(timer);
      reject(e);
    };
    img.src = src;
  });
};
const canvasToEscPosRaster = (canvas) => {
  if (!canvas) return null;
  const ctx = canvas.getContext("2d");
  const { width, height } = canvas;
  const imageData = ctx.getImageData(0, 0, width, height).data;
  const bytesPerRow = Math.ceil(width / 8);
  const totalBytes = bytesPerRow * height;
  const raster = new Uint8Array(8 + totalBytes); // header 8 + data
  // GS v 0
  raster[0] = 0x1d;
  raster[1] = 0x76;
  raster[2] = 0x30;
  raster[3] = 0x00; // mode
  raster[4] = bytesPerRow & 0xff;
  raster[5] = (bytesPerRow >> 8) & 0xff;
  raster[6] = height & 0xff;
  raster[7] = (height >> 8) & 0xff;

  for (let y = 0; y < height; y++) {
    for (let xByte = 0; xByte < bytesPerRow; xByte++) {
      let byte = 0;
      for (let bit = 0; bit < 8; bit++) {
        const x = xByte * 8 + bit;
        if (x >= width) continue;
        const idx = (y * width + x) * 4;
        const r = imageData[idx];
        const g = imageData[idx + 1];
        const b = imageData[idx + 2];
        // simple luminance threshold
        const lum = 0.299 * r + 0.587 * g + 0.114 * b;
        if (lum < 160) byte |= 1 << (7 - bit);
      }
      raster[8 + y * bytesPerRow + xByte] = byte;
    }
  }
  return raster;
};
const concatUint8 = (...parts) => {
  const total = parts.reduce((sum, part) => sum + (part?.length || 0), 0);
  const out = new Uint8Array(total);
  let offset = 0;
  parts.forEach((part) => {
    if (!part?.length) return;
    out.set(part, offset);
    offset += part.length;
  });
  return out;
};
const buildEscPosQrCommands = (text, opts = {}) => {
  const payload = String(text || "").trim();
  if (!payload) return [];
  const model = Number(opts?.model) === 1 ? 49 : 50; // 49->model1, 50->model2
  const moduleSize = Math.max(3, Math.min(8, Number(opts?.moduleSize) || 6));
  const ecc = Math.max(48, Math.min(51, Number(opts?.ecc) || 48)); // 48..51 => L/M/Q/H
  const data = new TextEncoder().encode(payload);
  const len = data.length + 3;
  const pL = len & 0xff;
  const pH = (len >> 8) & 0xff;
  const storePrefix = new Uint8Array([GS, 0x28, 0x6b, pL, pH, 0x31, 0x50, 0x30]);

  return [
    new Uint8Array([GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, model, 0x00]),
    new Uint8Array([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, moduleSize]),
    new Uint8Array([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, ecc]),
    concatUint8(storePrefix, data),
    new Uint8Array([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30]),
  ];
};
const qrCanvasFromText = async (text, size = 220) => {
  if (!text) return null;
  const safeSize = Number(size) > 0 ? Number(size) : 220;
  const encoded = encodeURIComponent(text);
  const sources = [
    `https://api.qrserver.com/v1/create-qr-code/?size=${safeSize}x${safeSize}&margin=0&ecc=M&data=${encoded}`,
    `https://quickchart.io/qr?text=${encoded}&size=${safeSize}&ecLevel=M&margin=0&format=png`,
  ];
  for (const src of sources) {
    try {
      const canvas = await canvasFromImage(src, safeSize, 3200);
      if (canvas) return canvas;
    } catch {
      // try next source
    }
  }
  return null;
};
const qrRasterCache = new Map();
const qrRasterFromText = async (text, size = 220) => {
  const payload = String(text || "").trim();
  if (!payload) return null;
  const px = Number(size) > 0 ? Number(size) : 150;
  const key = `${payload}::${px}`;
  if (qrRasterCache.has(key)) return qrRasterCache.get(key);
  const canvas = await qrCanvasFromText(payload, px);
  const raster = canvasToEscPosRaster(canvas);
  if (raster) {
    qrRasterCache.set(key, raster);
  }
  return raster || null;
};
const normalizeLogoCandidates = (url) => {
  if (!url) return [];
  let primary = url;
  if (!/^https?:\/\//i.test(primary)) {
    primary = `https://qr.billsarthi.in/public/${primary.replace(/^\/?/, "")}`;
  }
  const candidates = [primary];
  try {
    const stripped = primary.replace(/^https?:\/\//i, "");
    candidates.push(`https://images.weserv.nl/?url=${encodeURIComponent(stripped)}`);
  } catch {
    // ignore
  }
  return candidates;
};
const logoRasterCache = new Map();
const logoRasterFromUrl = async (url, size = 140) => {
  const input = String(url || "").trim();
  if (!input) return null;
  const safeSize = Number(size) > 0 ? Number(size) : 140;
  const key = `${input}::${safeSize}`;
  if (logoRasterCache.has(key)) return logoRasterCache.get(key);
  const logoChoices = normalizeLogoCandidates(input);
  for (const candidate of logoChoices) {
    try {
      const logoCanvas = await canvasFromImage(candidate, safeSize, 1400);
      const logoCmd = canvasToEscPosRaster(logoCanvas);
      if (logoCmd) {
        logoRasterCache.set(key, logoCmd);
        return logoCmd;
      }
    } catch {
      // try next source
    }
  }
  return null;
};
const padLine = (label, right, width = BLE_LINE_WIDTH) => {
  const rightStr = String(right || "");
  const maxLabel = Math.max(1, width - rightStr.length - 1);
  const safeLabel = (label || "").toString().slice(0, maxLabel);
  const spaces = Math.max(1, width - safeLabel.length - rightStr.length);
  return `${safeLabel}${" ".repeat(spaces)}${rightStr}`;
};
const buildReceiptText = ({
  restaurant,
  addressLines = [],
  phone,
  gstin,
  table,
  tablePrefix = "Table",
  payment,
  items,
  subtotal,
  discount,
  delivery,
  cgst,
  sgst,
  total,
  taxBreakup = [],
  printProfile,
  restaurantNameSize,
  customerName,
  customerNameSize,
  showGstRows = true,
}) => {
  const profile = printProfile || PRINT_PROFILES["2in"];
  const lineWidth = profile.lineWidth || BLE_LINE_WIDTH;
  const isThreeIn = lineWidth >= 48;
  const nameW = profile.nameW || NAME_W;
  const qtyW = profile.qtyW || QTY_W;
  const rateW = profile.rateW || RATE_W;
  const amtW = profile.amtW || AMT_W;
  const taxCols = profile.taxCols || PRINT_PROFILES["2in"].taxCols;
  const lines = [];
  const discountValue = Number(discount || 0);
  const cgstValue = Number(cgst || 0);
  const sgstValue = Number(sgst || 0);
  const now = new Date();
  const datePart = now.toLocaleDateString("en-IN");
  const timePart = now.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const dateTimeOneLine = `${datePart} ${timePart}`;
  const headerName = restaurant || "Bill Sarthi";
  lines.push(...buildSizedCenteredLines(headerName, lineWidth, restaurantNameSize));
  addressLines
    .filter(Boolean)
    .forEach((ln) => lines.push(centerLine(ln, lineWidth)));
  if (phone) lines.push(centerLine(`Phone: ${phone}`, lineWidth));
  if (gstin) lines.push(centerLine(`GSTIN: ${gstin}`, lineWidth));
  if (isThreeIn) {
    lines.push(
      padLine(
        `${tablePrefix}: ${table || "-"}`,
        payment ? `Payment: ${payment}` : "",
        lineWidth
      )
    );
    lines.push(padLine("Date/Time", dateTimeOneLine, lineWidth));
  } else {
    lines.push(centerLine(`${tablePrefix}: ${table || "-"}`, lineWidth));
    if (payment) lines.push(centerLine(`Payment: ${payment}`, lineWidth));
    lines.push(centerLine(dateTimeOneLine, lineWidth));
  }
  if (customerName) {
    const label = `Customer: ${customerName}`;
    lines.push(...buildSizedCenteredLines(label, lineWidth, customerNameSize));
  }
  lines.push("=".repeat(lineWidth));
  lines.push(col4("ITEM", "Q", "RATE", "AMT", { nameW, qtyW, rateW, amtW }));
  lines.push("-".repeat(lineWidth));
  items.forEach((it) => {
    const nameParts = wrapNameCol(it.name, nameW);
    const rateStr = (it.rate || 0).toFixed(2);
    const amtStr = (it.total || 0).toFixed(2);
    if (nameParts.length) {
      lines.push(col4(nameParts[0], it.qty, rateStr, amtStr, { nameW, qtyW, rateW, amtW }));
      nameParts.slice(1).forEach((part) => {
        lines.push(col4(part, "", "", "", { nameW, qtyW, rateW, amtW }));
      });
    }
  });
  lines.push("-".repeat(lineWidth));
  lines.push(padLine("Subtotal", subtotal.toFixed(2), lineWidth));
  if (discountValue > 0) {
    lines.push(padLine("Discount", `-${discountValue.toFixed(2)}`, lineWidth));
  }
  if (Number(delivery || 0) > 0) {
    lines.push(padLine("Delivery", Number(delivery).toFixed(2), lineWidth));
  }
  const hasBreakup =
    Array.isArray(taxBreakup) &&
    taxBreakup.some((r) => Number(r?.total || 0) !== 0);
  const hasGst = cgstValue > 0 || sgstValue > 0 || hasBreakup;
  if (showGstRows && hasGst) {
    lines.push(padLine("CGST (incl.)", cgstValue.toFixed(2), lineWidth));
    lines.push(padLine("SGST (incl.)", sgstValue.toFixed(2), lineWidth));
  }
  lines.push(padLine("Grand Total", total.toFixed(2), lineWidth));
  if (hasBreakup) {
    lines.push("-".repeat(lineWidth));
    lines.push(centerLine("Tax Breakup", lineWidth));
    lines.push(
      "Taxable".padEnd(taxCols.taxable, " ") +
        "CGST".padStart(taxCols.cgst, " ") +
        "SGST".padStart(taxCols.sgst, " ") +
        "Total".padStart(taxCols.total, " ")
    );
    taxBreakup.forEach((r) => {
      const taxable = (Number(r?.taxableamount ?? r?.taxable ?? r?.taxableAmount) || 0).toFixed(2);
      const cgstRow = (Number(r?.cgst ?? r?.cgstAmount ?? 0) || 0).toFixed(2);
      const sgstRow = (Number(r?.sgst ?? r?.sgstAmount ?? 0) || 0).toFixed(2);
      const totalRow = (Number(r?.total ?? Number(cgstRow) + Number(sgstRow)) || 0).toFixed(2);
      const line =
        taxable.toString().padStart(taxCols.taxable, " ") +
        cgstRow.toString().padStart(taxCols.cgst, " ") +
        sgstRow.toString().padStart(taxCols.sgst, " ") +
        totalRow.toString().padStart(taxCols.total, " ");
      lines.push(line.slice(0, lineWidth));
    });
  }
  lines.push("=".repeat(lineWidth));
  lines.push(centerLine("Thank you. Visit again!", lineWidth));
  return lines.join("\n");
};
const buildDeliveryCopyText = ({
  items = [],
  orderId,
  table,
  tablePrefix = "Table",
  printProfile,
}) => {
  const profile = printProfile || PRINT_PROFILES["2in"];
  const lineWidth = profile.lineWidth || BLE_LINE_WIDTH;
  const delNameW = profile.delNameW || DEL_NAME_W;
  const delQtyW = profile.delQtyW || DEL_QTY_W;
  const lines = [];
  lines.push(centerLine("DELIVERY COPY", lineWidth));
  if (orderId) lines.push(centerLine(`Order: ${orderId}`, lineWidth));
  if (table) lines.push(centerLine(`${tablePrefix}: ${table}`, lineWidth));
  lines.push(centerLine(new Date().toLocaleString(), lineWidth));
  lines.push("-".repeat(lineWidth));
  lines.push(col2("ITEM", "QTY", { nameW: delNameW, qtyW: delQtyW }));
  lines.push("-".repeat(lineWidth));
  items.forEach((it) => {
    const name = it?.name || "Item";
    const qty = Number(it?.qty ?? it?.quantity ?? 0);
    const nameParts = wrapText(name, delNameW);
    if (!nameParts.length) return;
    lines.push(col2(nameParts[0], qty, { nameW: delNameW, qtyW: delQtyW }));
    nameParts.slice(1).forEach((part) => {
      lines.push(col2(part, "", { nameW: delNameW, qtyW: delQtyW }));
    });
  });
  lines.push("-".repeat(lineWidth));
  return lines.join("\n");
};
const buildKotReceiptText = ({
  restaurant,
  table,
  tablePrefix = "Table",
  waiterName,
  items = [],
  notes,
  printProfile,
}) => {
  const profile = printProfile || PRINT_PROFILES["2in"];
  const lineWidth = profile.lineWidth || BLE_LINE_WIDTH;
  const qtyW = Math.max(4, profile.delQtyW || DEL_QTY_W);
  const nameW = Math.max(10, lineWidth - qtyW);
  const lines = [];
  const now = new Date();
  const datePart = now.toLocaleDateString("en-IN");
  const timePart = now.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });

  lines.push(...buildSizedCenteredLines(restaurant || "Restaurant", lineWidth));
  lines.push(centerLine("KOT", lineWidth));
  lines.push("-".repeat(lineWidth));
  if (table) lines.push(centerLine(`${tablePrefix}: ${table}`, lineWidth));
  if (waiterName) lines.push(centerLine(`Waiter: ${waiterName}`, lineWidth));
  lines.push(centerLine(`${datePart} ${timePart}`, lineWidth));
  lines.push("=".repeat(lineWidth));
  lines.push(col2("ITEM", "QTY", { nameW, qtyW }));
  lines.push("-".repeat(lineWidth));

  items.forEach((item) => {
    const qtyValue = Number(item?.qty ?? item?.quantity ?? 0);
    if (qtyValue <= 0) return;
    const qtyLabel = Number.isInteger(qtyValue) ? String(qtyValue) : qtyValue.toFixed(2);
    const nameParts = wrapText(item?.name || item?.product_name || "Item", nameW);
    lines.push(col2(nameParts[0], qtyLabel, { nameW, qtyW }));
    nameParts.slice(1).forEach((part) => {
      lines.push(col2(part, "", { nameW, qtyW }));
    });
  });

  if (String(notes || "").trim()) {
    lines.push("-".repeat(lineWidth));
    wrapText(`Notes: ${String(notes).trim()}`, lineWidth).forEach((line) => {
      lines.push(line);
    });
  }

  lines.push("=".repeat(lineWidth));
  return lines.join("\n");
};

const buildTaxBreakupFromLines = (lines = []) => {
  const rows = [];
  (lines || []).forEach((line) => {
    const qty = Number(line?.quantity ?? line?.qty ?? 0) || 0;
    if (qty <= 0) return;

    const priceAfterTax = Number(
      line?.priceaftertax ?? line?.taxedprice ?? line?.price ?? 0
    );
    const cgstPerUnit = Number(line?.cgst ?? 0);
    const sgstPerUnit = Number(line?.sgst ?? 0);
    const totalGstPerUnit = Number(
      line?.taxamount ?? cgstPerUnit + sgstPerUnit
    );

    rows.push({
      taxableamount: priceAfterTax * qty,
      cgst: cgstPerUnit * qty,
      sgst: sgstPerUnit * qty,
      total: totalGstPerUnit * qty,
    });
  });
  return rows;
};

const RNfirebaseConfig = {
  apiKey: "AIzaSyDbfZS3ZazugHXoSIvBk--P6coVstYX7R8",
  projectId: "estro-mobile-app",
  storageBucket: "restro-mobile-app.firebasestorage.app",
  messagingSenderId: "7236168554",
  appId: "1:7236168554:ios:d5114808c53aadb4671313",
  databaseURL:
    "https://restro-mobile-app-default-rtdb.asia-southeast1.firebasedatabase.app",
};

function ensureFirebase() {
  if (getApps().length === 0) initializeApp(RNfirebaseConfig);
  return getDatabase();
}

const KOT_KEY = (table) => `KOTData${table || ""}`;

const parseOrderObj = (value) => {
  try {
    const obj =
      typeof value === "string" ? JSON.parse(value || "{}") : value || {};
    const clean = {};
    Object.entries(obj || {}).forEach(([k, v]) => {
      const qty = Number(v);
      if (!Number.isNaN(qty) && qty > 0) clean[k] = qty;
    });
    return clean;
  } catch {
    return {};
  }
};

const getOrderSlotKey = (table) => {
  if (!table) return "";
  const candidate =
    table?.table_key ??
    table?.tableKey ??
    table?.key ??
    table?.name ??
    table?.table ??
    table?.tableid ??
    table?.tableId ??
    table?.id ??
    "";
  return candidate === null || candidate === undefined ? "" : String(candidate);
};

const getOrderSlotDisplayName = (table) => {
  if (!table) return "";
  const candidate =
    table?.display_name ??
    table?.displayName ??
    table?.name ??
    table?.table ??
    table?.table_key ??
    table?.tableKey ??
    table?.tableid ??
    table?.tableId ??
    table?.id ??
    "";
  return candidate === null || candidate === undefined ? "" : String(candidate);
};

const getOrderSlotType = (table) => {
  const raw = String(
    table?.table_type ?? table?.tableType ?? table?.type ?? "table"
  ).toLowerCase();
  return raw === "room" ? "room" : "table";
};

const normalizeOrderSlotLookup = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const resolveOrderLocationMeta = (tableId, routeState) => {
  if (isTakeawayName(tableId)) {
    return {
      type: "takeaway",
      prefix: "TakeAway",
      value: canonicalizeTakeawayName(tableId),
    };
  }

  const routeType = String(routeState?.tableType || "").toLowerCase();
  const routeLabel = String(routeState?.tableLabel || "").trim();
  const routeName = String(routeState?.tableName || "").trim();
  const routeDisplay = routeLabel || routeName;
  if (routeType === "room" || routeType === "table") {
    return {
      type: routeType,
      prefix: routeType === "room" ? "Room" : "Table",
      value: routeDisplay || String(tableId || "-"),
    };
  }

  try {
    const cached = JSON.parse(localStorage.getItem("tablelistx") || "[]");
    const target = normalizeOrderSlotLookup(tableId);
    const match = (Array.isArray(cached) ? cached : []).find((entry) => {
      const key = normalizeOrderSlotLookup(getOrderSlotKey(entry));
      const display = normalizeOrderSlotLookup(getOrderSlotDisplayName(entry));
      return key === target || display === target;
    });
    if (match) {
      const type = getOrderSlotType(match);
      return {
        type,
        prefix: type === "room" ? "Room" : "Table",
        value: getOrderSlotDisplayName(match) || String(tableId || "-"),
      };
    }
  } catch {
    // ignore cache parse errors
  }

  return {
    type: "table",
    prefix: "Table",
    value: String(tableId || "-"),
  };
};

const mapItemsToOrder = (items) => {
  const out = {};
  (items || []).forEach((it) => {
    const pid = it?.productid ?? it?.product_id ?? it?.id;
    const qty = Number(it?.quantity ?? it?.qty ?? it?.itemquantity ?? 0);
    if (pid && qty > 0) out[pid] = qty;
  });
  return out;
};

const OrderPage = () => {
  const { tableId: routeTableId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const routeTableName = useMemo(
    () => String(location.state?.tableName || "").trim(),
    [location.state]
  );

  const tableId = useMemo(() => {
    if (!routeTableId) return DEFAULT_TAKEAWAY_NAME;
    try {
      return decodeURIComponent(routeTableId);
    } catch {
      return routeTableId;
    }
  }, [routeTableId]);
  const orderLocationMeta = useMemo(
    () => resolveOrderLocationMeta(tableId, location.state),
    [tableId, location.state]
  );
  const orderLocationLabel = orderLocationMeta.value;
  const orderLocationPrefix = orderLocationMeta.prefix;

  const [categories, setCategories] = useState([
    { id: "All", name: "All" },
    { id: SELECTED_CATEGORY_ID, name: "Selected" },
  ]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [orderItems, setOrderItems] = useState({});
  const [variantLines, setVariantLines] = useState([]);
  const [variantsByProduct, setVariantsByProduct] = useState({});
  const [addonsByProduct, setAddonsByProduct] = useState({});
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [optionsProduct, setOptionsProduct] = useState(null);
  const [optionsVariants, setOptionsVariants] = useState([]);
  const [optionsAddons, setOptionsAddons] = useState([]);
  const [optionsSelectedVariantId, setOptionsSelectedVariantId] = useState(null);
  const [optionsSelectedAddonIds, setOptionsSelectedAddonIds] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [waiters, setWaiters] = useState([]);
  const [selectedWaiter, setSelectedWaiter] = useState("");
  const [showAddWaiterModal, setShowAddWaiterModal] = useState(false);
  const [newWaiterName, setNewWaiterName] = useState("");
  const [newWaiterPhone, setNewWaiterPhone] = useState("");
  const [newWaiterEmail, setNewWaiterEmail] = useState("");
  const [newWaiterPassword, setNewWaiterPassword] = useState("");
  const [savingWaiter, setSavingWaiter] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerCompany, setCustomerCompany] = useState("");
  const [mobile, setMobile] = useState("");
  const [discountModal, setDiscountModal] = useState(false);
  const [customerModal, setCustomerModal] = useState(false);
  const [discountMode, setDiscountMode] = useState("percent"); // percent | amount
  const [discountValue, setDiscountValue] = useState(0);
  const [appliedDiscount, setAppliedDiscount] = useState({
    mode: "percent",
    value: 0,
  });
  const [partyList, setPartyList] = useState([]);
  const [selectedParty, setSelectedParty] = useState("");
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [orderStartTime, setOrderStartTime] = useState("");
  const [hydrated, setHydrated] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [kotModalOpen, setKotModalOpen] = useState(false);
  const [kotNotes, setKotNotes] = useState("");
  const [kotPreviewItems, setKotPreviewItems] = useState([]);
  const [kotAlreadySent, setKotAlreadySent] = useState([]);
  const [kotSelectedMap, setKotSelectedMap] = useState({});
  const [sendingKOT, setSendingKOT] = useState(false);
  const [status, setStatus] = useState("");
  const [billCreatedInfo, setBillCreatedInfo] = useState(null);
  const [postBillModal, setPostBillModal] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerTab, setCustomerTab] = useState("existing"); // existing | add
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [qtyOverrides, setQtyOverrides] = useState({});
  const [showAllSummaryItems, setShowAllSummaryItems] = useState(false);
  const [showSummary, setShowSummary] = useState(true);
  const [categoryLayout, setCategoryLayout] = useState("vertical");
  const [deliveryEnabledSetting, setDeliveryEnabledSetting] = useState(false);
  const [deliveryDefaultCharge, setDeliveryDefaultCharge] = useState("0");
  const [deliverySelected, setDeliverySelected] = useState(false);
  const [deliveryChargeInput, setDeliveryChargeInput] = useState("");
  const [showTaxBreakupOnBillSetting, setShowTaxBreakupOnBillSetting] = useState(true);
  const [printerWidthSetting, setPrinterWidthSetting] = useState("2in");
  const [restaurantNameSizeSetting, setRestaurantNameSizeSetting] = useState("small");
  const [customerNameSizeSetting, setCustomerNameSizeSetting] = useState("small");
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [pendingPaymentMode, setPendingPaymentMode] = useState("");
  const splitApplyAfterCustomerRef = useRef(false);
  const [paymentMode, setPaymentMode] = useState("Cash");
  const [showSplitPaymentModal, setShowSplitPaymentModal] = useState(false);
  const [splitCashInput, setSplitCashInput] = useState("");
  const [splitUpiInput, setSplitUpiInput] = useState("");
  const [splitCardInput, setSplitCardInput] = useState("");
  const [splitCreditInput, setSplitCreditInput] = useState("");
  const splitCreditAutoRef = useRef(true);
  const [bleStatus, setBleStatus] = useState("");
  const [blePrinting, setBlePrinting] = useState(false);
  const [usbStatus, setUsbStatus] = useState("");
  const [usbPrinting, setUsbPrinting] = useState(false);
  const [orderCreatedToneEnabled, setOrderCreatedToneEnabled] = useState(true);
  const printerCharRef = useRef(null);
  const orderToneCtxRef = useRef(null);
  const lastBleDeviceIdRef = useRef(localStorage.getItem("ble_last_device_id") || "");
  const splitFetchOnOpenRef = useRef(false);
  const leftCatRefs = useRef({});
  const topCatRefs = useRef({});
  const waiterSelectRef = useRef(null);
  const paymentSelectRef = useRef(null);
  const discountInputRef = useRef(null);
  const customerSearchRef = useRef(null);
  const customerNameRef = useRef(null);
  const splitCashInputRef = useRef(null);
  const kotNotesRef = useRef(null);
  const [tableMetaMap, setTableMetaMap] = useState({});

  const userData = JSON.parse(localStorage.getItem("userData"))?.data;
  const restaurantId = userData?.id;
  const focusProductSearch = useCallback(() => {
    return focusFirstVisible('[data-shortcut-search="order-products"]');
  }, []);
  const buttonShortcutLabels = useMemo(() => {
    const read = (id) => formatShortcutCombo(getShortcutById(id)?.combo || "");
    return {
      search: read("order.search"),
      customer: read("order.customer"),
      discount: read("order.discount"),
      save: read("order.save"),
      print: read("order.print"),
      usbPrint: read("order.usb-print"),
      kot: read("order.kot"),
    };
  }, []);
  const productNameById = useMemo(() => {
    const map = new Map();
    (products || []).forEach((p) => {
      if (!p?.id) return;
      const name =
        p?.name || p?.productname || p?.product_name || `#${p?.id}`;
      map.set(Number(p.id), name);
    });
    return map;
  }, [products]);
  const kotQuickTags = useMemo(
    () =>
      parseQuickTags(
        userData?.kot_tags ??
          userData?.kottags ??
          userData?.quick_tags ??
          userData?.quicktags
      ),
    [
      userData?.kot_tags,
      userData?.kottags,
      userData?.quick_tags,
      userData?.quicktags,
    ]
  );
  const orderFromDashboard =
    location.state?.orderForEdit || location.state?.order || null;
  const orderIdForEdit =
    orderFromDashboard?.id ||
    orderFromDashboard?.orderid ||
    location.state?.orderid ||
    location.state?.orderId ||
    null;
  const openedFromQrNotification = Boolean(
    location.state?.openFromQrNotification || location.state?.qrorderid
  );
  const openedAsQuickBill = Boolean(location.state?.quickBill);
  const qrHydratedRef = useRef(false);
  const isEditingExisting = !!orderIdForEdit && !openedFromQrNotification;

  // Load table metadata (cached + fresh)
  useEffect(() => {
    const loadTables = async () => {
      try {
        const cached = localStorage.getItem("tablelistx");
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed)) {
            setTableMetaMap(buildTableMetaMap(parsed));
          }
        }
      } catch {}

      if (!restaurantId) return;
      try {
        const res = await fetch(
          "https://qr.billsarthi.in/public/api/list/table",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ restaurantid: restaurantId }),
          }
        );
        const json = await res.json();
        if (res.ok && Array.isArray(json?.list)) {
          setTableMetaMap(buildTableMetaMap(json.list));
          localStorage.setItem("tablelistx", JSON.stringify(json.list));
        }
      } catch (e) {
        console.log("Table list load failed:", e?.message || e);
      }
    };
    loadTables();
  }, [restaurantId]);

  const tableApiId = useMemo(() => {
    // API should receive the human-readable table name when provided (matches mobile behaviour)
    if (isTakeawayName(tableId)) return 0;
    if (routeTableName) return routeTableName;

    const meta =
      tableMetaMap[String(tableId)] ||
      tableMetaMap[tableId] ||
      tableMetaMap[normalizeTableIdForApi(tableId)];

    if (meta?.apiId) return meta.apiId;
    return tableId;
  }, [tableId, tableMetaMap, routeTableName]);

  const normalizeMoneyInput = useCallback((text) => {
    const cleaned = String(text || "").replace(/[^0-9.]/g, "");
    if (!cleaned) return "";
    const [whole, ...rest] = cleaned.split(".");
    const decimals = rest.join("").slice(0, 2);
    return decimals ? `${whole}.${decimals}` : whole;
  }, []);

  const parseMoney = useCallback((value) => {
    const n = Number(String(value ?? "").trim() || 0);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Number(n.toFixed(2)));
  }, []);

  const formatNumberForInput = useCallback((num) => {
    if (!Number.isFinite(num) || num === 0) return "";
    const fixed = Number(num.toFixed(2));
    return Number.isInteger(fixed) ? String(fixed) : fixed.toString();
  }, []);

  const buildSplitPaymentPayload = useCallback(() => {
    const cash = parseMoney(splitCashInput);
    const upi = parseMoney(splitUpiInput);
    const card = parseMoney(splitCardInput);
    const credit = parseMoney(splitCreditInput);
    const total = Number((cash + upi + card + credit).toFixed(2));
    const methods = [
      cash > 0 ? "Cash" : null,
      upi > 0 ? "UPI" : null,
      card > 0 ? "Card" : null,
      credit > 0 ? "Credit" : null,
    ].filter(Boolean);
    return { cash, upi, card, credit, total, methods };
  }, [parseMoney, splitCashInput, splitUpiInput, splitCardInput, splitCreditInput]);

  const clampSplitInput = useCallback(
    (nextRaw, otherTotal, billTotal) => {
      const nextVal = parseMoney(nextRaw);
      const maxAllowed = Math.max(0, Number((billTotal - otherTotal).toFixed(2)));
      if (nextVal > maxAllowed + 0.001) {
        return formatNumberForInput(maxAllowed);
      }
      return nextRaw;
    },
    [parseMoney, formatNumberForInput]
  );

  const openSplitPaymentModal = useCallback(({ reset = true } = {}) => {
    if (reset) {
      setSplitCashInput("");
      setSplitUpiInput("");
      setSplitCardInput("");
      setSplitCreditInput("");
    }
    splitCreditAutoRef.current = false;
    splitFetchOnOpenRef.current = false;
    setShowSplitPaymentModal(true);
  }, []);

  const maybeFetchSplitFromOrder = useCallback(async () => {
    if (!isEditingExisting || !orderIdForEdit || !restaurantId) return;
    const existingTotal =
      parseMoney(splitCashInput) +
      parseMoney(splitUpiInput) +
      parseMoney(splitCardInput) +
      parseMoney(splitCreditInput);
    if (existingTotal > 0) return;
    if (splitFetchOnOpenRef.current) return;
    splitFetchOnOpenRef.current = true;
    try {
      const res = await fetch(
        "https://qr.billsarthi.in/public/api/getorderdetail",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: restaurantId, orderid: orderIdForEdit }),
        }
      );
      const result = await res.json().catch(() => ({}));
      if (!res.ok) return;
      const details = result?.orderdetails || result?.order || {};
      const splitRaw =
        details?.split_payment ||
        details?.splitPayment ||
        details?.splitpayment ||
        details?.split_details;
      const splitPayload = normalizeSplitPayload(splitRaw);
      if (splitPayload && typeof splitPayload === "object") {
        setSplitCashInput(formatNumberForInput(splitPayload.cash || 0));
        setSplitUpiInput(formatNumberForInput(splitPayload.upi || 0));
        setSplitCardInput(formatNumberForInput(splitPayload.card || 0));
        setSplitCreditInput(formatNumberForInput(splitPayload.credit || 0));
        splitCreditAutoRef.current = false;
      }
    } catch {
      // ignore
    }
  }, [
    isEditingExisting,
    orderIdForEdit,
    restaurantId,
    splitCashInput,
    splitUpiInput,
    splitCardInput,
    splitCreditInput,
    parseMoney,
    formatNumberForInput,
  ]);

  const applyExistingOrder = useCallback(
    (orderPayload = {}, itemsList = []) => {
      const { baseMap, optionLines } = splitLineItems(itemsList);
      setOrderItems(Object.keys(baseMap).length ? baseMap : {});
      setVariantLines(optionLines || []);
      const created =
        orderPayload?.created_at ||
        orderPayload?.createdAt ||
        orderPayload?.orderdate ||
        "";
      if (created) setOrderStartTime(created);
      if (orderPayload?.waiterid || orderPayload?.waiter_id) {
        setSelectedWaiter(
          String(orderPayload?.waiterid ?? orderPayload?.waiter_id)
        );
      }
      if (orderPayload?.partyid || orderPayload?.party_id) {
        setSelectedParty(
          String((orderPayload?.partyid ?? orderPayload?.party_id) || "")
        );
      }
      const mode = orderPayload?.mode || orderPayload?.paymentmode || "";
      if (mode) setPaymentMode(mode);
      const splitRaw =
        orderPayload?.split_payment ||
        orderPayload?.splitPayment ||
        orderPayload?.splitpayment ||
        orderPayload?.split_details;
      const splitPayload = normalizeSplitPayload(splitRaw);
      if (splitPayload && typeof splitPayload === "object") {
        setSplitCashInput(formatNumberForInput(splitPayload.cash || 0));
        setSplitUpiInput(formatNumberForInput(splitPayload.upi || 0));
        setSplitCardInput(formatNumberForInput(splitPayload.card || 0));
        setSplitCreditInput(formatNumberForInput(splitPayload.credit || 0));
        splitCreditAutoRef.current = false;
        if (!mode || String(mode).toLowerCase() === "split") {
          setPaymentMode("Split");
        }
      }
      const discountPct =
        Number(orderPayload?.discount) || Number(orderPayload?.discountpercent);
      if (discountPct > 0) {
        setAppliedDiscount({ mode: "percent", value: discountPct });
      }
      setHydrated(true);
      setStatus(
        `Loaded order #${orderPayload?.ordercode || orderPayload?.id || ""}`
      );
    },
    [formatNumberForInput]
  );

  const handleDeliveryToggle = useCallback(
    (nextValue) => {
      if (!deliveryEnabledSetting) {
        setDeliverySelected(false);
        return;
      }
      setDeliverySelected(nextValue);
      if (nextValue) {
        setDeliveryChargeInput((prev) => {
          if (prev && prev.trim() !== "") {
            return prev;
          }
          return deliveryDefaultCharge || "0";
        });
      }
    },
    [deliveryDefaultCharge, deliveryEnabledSetting]
  );

  const handleDeliveryChargeChange = useCallback(
    (text) => {
      setDeliveryChargeInput(normalizeMoneyInput(text));
    },
    [normalizeMoneyInput]
  );

  const handleDeliveryChargeBlur = useCallback(() => {
    if (!deliverySelected) return;
    if (deliveryChargeInput && deliveryChargeInput.trim() !== "") return;
    setDeliveryChargeInput(deliveryDefaultCharge || "0");
  }, [deliveryChargeInput, deliveryDefaultCharge, deliverySelected]);

  const aggregatedItems = useMemo(() => {
    const aggregated = { ...(orderItems || {}) };
    variantLines.forEach((line) => {
      const productId = Number(line.productid);
      const qty = Number(line.quantity || 0);
      if (!productId || qty <= 0) return;
      aggregated[productId] = (Number(aggregated[productId]) || 0) + qty;
    });
    return aggregated;
  }, [orderItems, variantLines]);

  const cartLines = useMemo(() => {
    if (!Array.isArray(products)) return variantLines || [];
    const baseLines = Object.keys(orderItems || {})
      .map((key) => {
        const productId = Number(key);
        const qty = Number(orderItems?.[key] || 0);
        if (!productId || qty <= 0) return null;
        const product = products.find((item) => Number(item.id) === productId);
        if (!product) return null;
        const priceaftertax = Number(
          product?.priceaftertax ?? product?.price ?? 0
        );
        const taxedprice = Number(
          product?.taxedprice ?? product?.priceaftertax ?? product?.price ?? 0
        );
        const taxamount = Number(product?.taxamount ?? 0);
        const sgst = Number(product?.sgst ?? 0);
        const cgst = Number(product?.cgst ?? 0);
        return {
          lineKey: `p-${productId}`,
          productid: productId,
          variantid: null,
          addon_ids: [],
          addon_label: "",
          quantity: qty,
          price: Number(product?.price ?? 0),
          priceaftertax,
          taxedprice,
          taxamount,
          sgst,
          cgst,
          name: product?.name ?? "Item",
          base_name: product?.name ?? "Item",
          variant_label: "",
          addons: [],
          product,
          isVariantLine: false,
        };
      })
      .filter(Boolean);

    const optionLines = (variantLines || [])
      .filter((line) => Number(line?.quantity || 0) > 0)
      .map((line) => {
        const productId = Number(line?.productid);
        const product =
          line?.product || products.find((item) => Number(item.id) === productId);
        const baseName =
          line?.base_name || product?.name || line?.name || "Item";
        const variantCount = variantsByProduct?.[productId]?.length || 0;
        const shouldShowVariant = variantCount > 1;
        const variantLabel = shouldShowVariant
          ? String(line?.variant_label || "").trim()
          : "";
        const displayName =
          shouldShowVariant && variantLabel
            ? `${baseName} (${variantLabel})`
            : baseName;
        return {
          ...line,
          name: displayName,
          base_name: baseName,
          variant_label: variantLabel,
          product: product || line?.product,
          isVariantLine: true,
        };
      });
    return [...baseLines, ...optionLines];
  }, [products, orderItems, variantLines, variantsByProduct]);

  const optionsPreview = useMemo(() => {
    if (!optionsProduct) return null;
    const variants = optionsVariants || [];
    const variant =
      variants.find(
        (item) => String(item?.id) === String(optionsSelectedVariantId)
      ) || variants[0];
    const addons = optionsAddons.filter((addon) =>
      optionsSelectedAddonIds.includes(addon.id)
    );
    const line = buildVariantLine(optionsProduct, variant, addons);
    const unit = Number(line.priceaftertax ?? line.price ?? 0);
    const payable = Number(line.taxedprice ?? line.priceaftertax ?? line.price ?? 0);
    return {
      unit,
      payable,
      variantLabel: line.variant_label,
      addonLabel: line.addon_label,
    };
  }, [
    optionsProduct,
    optionsVariants,
    optionsSelectedVariantId,
    optionsAddons,
    optionsSelectedAddonIds,
  ]);

  /* ---------- filtered list (category + search) ---------- */
  const filteredProducts = useMemo(() => {
    let list;
    if (selectedCategory === SELECTED_CATEGORY_ID) {
      const addedIds = Object.entries(aggregatedItems)
        .filter(([, qty]) => Number(qty) > 0)
        .map(([id]) => id);
      list = products.filter((p) => addedIds.includes(String(p.id)));
      list = [...list].sort((a, b) =>
        (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase())
      );
    } else {
      list =
        selectedCategory === "All"
          ? products
          : products.filter(
              (p) => String(p.productcategoryid) === String(selectedCategory)
            );
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) => (p.name || "").toLowerCase().includes(q));
    }
    return list;
  }, [products, selectedCategory, search, aggregatedItems]);

  /* ---------- loaders (safe JSON parse like you wrote) ---------- */
  const fetchLists = useCallback(async () => {
    if (!restaurantId) return;
    const body = { restaurantid: restaurantId };

    try {
      const [catRes, prodRes, waiterRes] = await Promise.all([
        fetch("https://qr.billsarthi.in/public/api/list/productcategory", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
        fetch("https://qr.billsarthi.in/public/api/list/getproductlist", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
        fetch("https://qr.billsarthi.in/public/api/list/waiter", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
      ]);

      const [catText, prodText, waiterText] = await Promise.all([
        catRes.text(),
        prodRes.text(),
        waiterRes.text(),
      ]);

      const safeParse = (text, label) => {
        const raw = String(text || "").trim();
        if (!raw) return null;
        if (raw.startsWith("<")) {
          console.error(`HTML response from ${label}:`, raw.slice(0, 240));
          return null;
        }
        try {
          return JSON.parse(raw);
        } catch (err) {
          console.error(`Invalid JSON from ${label}:`, err, raw.slice(0, 240));
          return null;
        }
      };

      const catJson = safeParse(catText, "productcategory");
      const prodJson = safeParse(prodText, "getproductlist");
      const waiterJson = safeParse(waiterText, "waiter");

      if (!catJson || !prodJson || !waiterJson) {
        toast.warn("Server data issue. Loaded cached data where available.");
      }

      const cachedProd = JSON.parse(localStorage.getItem("productlistx") || "[]");
      const cachedCats = JSON.parse(localStorage.getItem("categorylistx") || "[]");
      const cachedWaiters = JSON.parse(localStorage.getItem("waiterlistx") || "[]");

      const catList = [
        { id: "All", name: "All" },
        { id: SELECTED_CATEGORY_ID, name: "Selected" },
        ...((catJson?.list || cachedCats || []).map((c) => ({
          id: c.id,
          name: c.productcategoryname || c.name || "",
        })) || []),
      ];

      setCategories(catList);
      setProducts(prodJson?.list || cachedProd || []);
      setWaiters(waiterJson?.list || cachedWaiters || []);

      // cache for offline parity with mobile
      if (Array.isArray(prodJson?.list)) {
        localStorage.setItem("productlistx", JSON.stringify(prodJson.list));
      }
      localStorage.setItem("categorylistx", JSON.stringify(catList.slice(1)));
      if (Array.isArray(waiterJson?.list)) {
        localStorage.setItem("waiterlistx", JSON.stringify(waiterJson.list));
      }
    } catch (e) {
      console.error(e);
      // offline fallback caches
      try {
        const cachedProd = JSON.parse(localStorage.getItem("productlistx")) || [];
        const cachedCat = JSON.parse(localStorage.getItem("categorylistx")) || [];
        const cachedWait = JSON.parse(localStorage.getItem("waiterlistx")) || [];
        setCategories([{ id: "All", name: "All" }, ...(cachedCat || [])]);
        setProducts(cachedProd || []);
        setWaiters(cachedWait || []);
        setStatus("Offline mode — using last saved product list.");
      } catch {
        toast.error("Failed to load lists.");
      }
    }
  }, [restaurantId]);

  /* ---------- variants + addons (mobile parity) ---------- */
  const fetchAllVariants = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const res = await fetch(
        "https://qr.billsarthi.in/public/api/product/variants/list",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: restaurantId }),
        }
      );
      const result = await res.json();
      if (res.ok && result?.success) {
        const map = (result.variants || []).reduce((acc, variant) => {
          const productId = Number(variant?.productid);
          if (!productId) return acc;
          acc[productId] = acc[productId] || [];
          acc[productId].push(variant);
          return acc;
        }, {});
        Object.keys(map).forEach((key) => {
          map[key] = map[key].sort((a, b) => {
            const orderDiff =
              Number(a?.displayorder || 0) - Number(b?.displayorder || 0);
            if (orderDiff !== 0) return orderDiff;
            return String(a?.label || "").localeCompare(String(b?.label || ""));
          });
        });
        setVariantsByProduct(map);
      }
    } catch (e) {
      console.log("Failed to load variants", e?.message || e);
    }
  }, [restaurantId]);

  const fetchAllAddonsMap = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const res = await fetch(
        "https://qr.billsarthi.in/public/api/product/addons/map",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: restaurantId }),
        }
      );
      const result = await res.json();
      if (res.ok && result?.success) {
        const map = result?.addons_by_product || {};
        setAddonsByProduct(map);
      }
    } catch (e) {
      console.log("Failed to load addons map", e?.message || e);
    }
  }, [restaurantId]);

  const fetchAddonsForProduct = useCallback(
    async (productId) => {
      if (!restaurantId || !productId) return [];
      try {
        const res = await fetch(
          "https://qr.billsarthi.in/public/api/product/addons/by-product",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ restaurantid: restaurantId, productid: productId }),
          }
        );
        const result = await res.json();
        if (res.ok && result?.success) {
          const addons = result?.addons || [];
          setAddonsByProduct((prev) => ({ ...prev, [productId]: addons }));
          return addons;
        }
      } catch (e) {
        console.log("Failed to load addons", e?.message || e);
      }
      return [];
    },
    [restaurantId]
  );

  const warmAddonsCache = useCallback(
    async (productId) => {
      if (addonsByProduct[productId]) return;
      await fetchAddonsForProduct(productId);
    },
    [addonsByProduct, fetchAddonsForProduct]
  );

  useEffect(() => {
    if (!restaurantId) return;
    fetchAllVariants();
    fetchAllAddonsMap();
  }, [restaurantId, fetchAllVariants, fetchAllAddonsMap]);

  useEffect(() => {
    fetchLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  const fetchPartyList = useCallback(async () => {
    if (!restaurantId) return [];
    try {
      const res = await fetch(
        "https://qr.billsarthi.in/public/api/list/partylistfororders",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ restaurantid: restaurantId }),
        }
      );
      const json = await res.json();
      if (Array.isArray(json?.list)) {
        const mapped = json.list.map((party) => ({
          ...party,
          companyname: partyCompanyOf(party),
        }));
        setPartyList(mapped);
        return mapped;
      }
    } catch {
      // ignore
    }
    return [];
  }, [restaurantId]);

  const resetQuickWaiterForm = useCallback(() => {
    setNewWaiterName("");
    setNewWaiterPhone("");
    setNewWaiterEmail("");
    setNewWaiterPassword("");
  }, []);

  const handleWaiterAdded = useCallback(
    async (createdWaiter = null) => {
      await fetchLists();
      const nextId =
        createdWaiter?.id ??
        createdWaiter?.staffid ??
        createdWaiter?.waiterid ??
        createdWaiter?.data?.id ??
        "";
      if (nextId) {
        setSelectedWaiter(String(nextId));
      }
    },
    [fetchLists]
  );

  const handleQuickAddWaiter = useCallback(async () => {
    const name = String(newWaiterName || "").trim();
    const phone = String(newWaiterPhone || "").replace(/\D/g, "").slice(0, 10);
    const email = String(newWaiterEmail || "").trim();
    const password = String(newWaiterPassword || "");

    if (!name) {
      toast.info("Waiter name is required.");
      return;
    }
    if (!/^\d{10}$/.test(phone)) {
      toast.info("Enter a valid 10-digit phone number.");
      return;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.info("Enter a valid email address.");
      return;
    }
    if (password.length < 6) {
      toast.info("Password must be at least 6 characters.");
      return;
    }
    if (!restaurantId) {
      toast.error("Missing restaurant information.");
      return;
    }

    try {
      setSavingWaiter(true);
      const res = await fetch(
        "https://qr.billsarthi.in/public/api/owner/add-staff",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            restaurantid: Number(restaurantId),
            name,
            phone,
            email,
            password,
            role: "waiter",
          }),
        }
      );
      const result = await res.json().catch(() => ({}));
      if (!res.ok && result?.success === false) {
        throw new Error(result?.message || "Unable to add waiter");
      }
      toast.success("Waiter added");
      await handleWaiterAdded(result?.waiter || result?.data || result?.staff || null);
      setShowAddWaiterModal(false);
      resetQuickWaiterForm();
    } catch (error) {
      toast.error(error?.message || "Unable to add waiter");
    } finally {
      setSavingWaiter(false);
    }
  }, [
    newWaiterName,
    newWaiterPhone,
    newWaiterEmail,
    newWaiterPassword,
    restaurantId,
    handleWaiterAdded,
    resetQuickWaiterForm,
  ]);

  useEffect(() => {
    fetchPartyList();
  }, [fetchPartyList]);

  useEffect(() => {
    if (!selectedParty) return;
    const selected = (partyList || []).find(
      (p) => String(p?.id) === String(selectedParty)
    );
    if (!selected) return;
    setCustomerCompany(partyCompanyOf(selected));
  }, [selectedParty, partyList]);

  const resetAfterOrderCompletion = useCallback(() => {
    setSearch("");
    setCustomerSearch("");
    setCustomerName("");
    setCustomerCompany("");
    setMobile("");
    setSelectedParty("");
    setPendingPaymentMode("");
    setPaymentMode("Cash");
    setDiscountMode("percent");
    setDiscountValue(0);
    setAppliedDiscount({ mode: "percent", value: 0 });
    setDeliverySelected(false);
    setDeliveryChargeInput("");
    setCustomerTab("existing");
  }, []);

  // Load existing order from dashboard (for edit)
  useEffect(() => {
    if (!restaurantId || !orderIdForEdit || !isEditingExisting) return;

    const itemsFromState =
      (Array.isArray(orderFromDashboard?.itemslist) && orderFromDashboard.itemslist.length
        ? orderFromDashboard.itemslist
        : null) ||
      (Array.isArray(orderFromDashboard?.orderitems) && orderFromDashboard.orderitems.length
        ? orderFromDashboard.orderitems
        : null) ||
      (Array.isArray(orderFromDashboard?.items) && orderFromDashboard.items.length
        ? orderFromDashboard.items
        : null);

    if (itemsFromState) {
      applyExistingOrder(orderFromDashboard, itemsFromState);
      return;
    }

    const fetchDetail = async () => {
      setLoadingExisting(true);
      setStatus("Loading order...");
      try {
        const body = {
          restaurantid: restaurantId,
          orderid: orderIdForEdit,
        };
        const res = await fetch(
          "https://qr.billsarthi.in/public/api/getorderdetail",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          }
        );
        const json = await res.json();
        if (!res.ok) throw new Error(json?.message || "Failed to load order");

        const apiOrder = json?.orderdetails || json?.order || {};
        const merged = { ...(orderFromDashboard || {}), ...apiOrder };
        const itemsList = Array.isArray(json?.itemslist)
          ? json.itemslist
          : Array.isArray(merged.orderitems)
          ? merged.orderitems
          : Array.isArray(merged.items)
          ? merged.items
          : [];

        applyExistingOrder(merged, itemsList);
      } catch (err) {
        console.error(err);
        toast.error(err?.message || "Failed to load order");
        setStatus("Failed to load order.");
      } finally {
        setLoadingExisting(false);
      }
    };

    fetchDetail();
  }, [
    restaurantId,
    orderIdForEdit,
    orderFromDashboard,
    applyExistingOrder,
    isEditingExisting,
  ]);

  // QR-accept flow should hold the table like a fresh live order (not edit mode).
  useEffect(() => {
    if (!openedFromQrNotification || qrHydratedRef.current) return;

    const itemsFromState =
      (Array.isArray(orderFromDashboard?.itemslist) &&
      orderFromDashboard.itemslist.length
        ? orderFromDashboard.itemslist
        : null) ||
      (Array.isArray(orderFromDashboard?.orderitems) &&
      orderFromDashboard.orderitems.length
        ? orderFromDashboard.orderitems
        : null) ||
      (Array.isArray(orderFromDashboard?.items) && orderFromDashboard.items.length
        ? orderFromDashboard.items
        : null);

    if (!itemsFromState) return;

    qrHydratedRef.current = true;
    const qrMergeMode = String(location.state?.qrMergeMode || "replace").toLowerCase();
    if (qrMergeMode === "append") {
      try {
        const existingItemsRaw = localStorage.getItem(`ordersitems1${tableId}`);
        const existingLinesRaw = localStorage.getItem(`orderlines1${tableId}`);
        const existingItems = existingItemsRaw ? JSON.parse(existingItemsRaw) : {};
        const existingLines = existingLinesRaw ? JSON.parse(existingLinesRaw) : [];
        const { baseMap: existingBase, optionLines: existingOption } = splitLineItems(existingLines);
        const { baseMap: qrBase, optionLines: qrOption } = splitLineItems(itemsFromState);

        const mergedBase = { ...(existingItems || {}) };
        Object.entries(existingBase || {}).forEach(([k, qty]) => {
          mergedBase[k] = (Number(mergedBase[k]) || 0) + Number(qty || 0);
        });
        Object.entries(qrBase || {}).forEach(([k, qty]) => {
          mergedBase[k] = (Number(mergedBase[k]) || 0) + Number(qty || 0);
        });

        const optionMap = new Map();
        [...(existingOption || []), ...(qrOption || [])].forEach((line) => {
          const key = String(line?.lineKey || "");
          if (!key) return;
          const prev = optionMap.get(key);
          if (!prev) {
            optionMap.set(key, {
              ...line,
              quantity: Number(line?.quantity || 0),
            });
            return;
          }
          optionMap.set(key, {
            ...prev,
            quantity: Number(prev.quantity || 0) + Number(line?.quantity || 0),
          });
        });

        setOrderItems(mergedBase);
        setVariantLines(Array.from(optionMap.values()).filter((line) => Number(line?.quantity || 0) > 0));
        setHydrated(true);
        setStatus("QR items added to existing order.");
        return;
      } catch {
        // fallback to replace mode
      }
    }

    applyExistingOrder(orderFromDashboard || {}, itemsFromState);
    setStatus("QR order loaded.");
  }, [openedFromQrNotification, orderFromDashboard, applyExistingOrder, location.state?.qrMergeMode, tableId]);

  /* ---------- cart ops ---------- */
  const ensureStartTime = useCallback(() => {
    setOrderStartTime((prev) => prev || new Date().toISOString());
  }, []);

  const handleAdd = (id) => {
    ensureStartTime();
    setOrderItems((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
  };

  const hasProductOptions = useCallback(
    (productId) => {
      const variantCount = variantsByProduct?.[productId]?.length || 0;
      const addonCount = addonsByProduct?.[productId]?.length || 0;
      const hasVariantLines = variantLines.some(
        (line) => Number(line.productid) === Number(productId)
      );
      return variantCount > 0 || addonCount > 0 || hasVariantLines;
    },
    [variantsByProduct, addonsByProduct, variantLines]
  );

  const getProductQty = useCallback(
    (productId) => {
      const baseQty = Number(orderItems?.[productId] || 0);
      const variantQty = variantLines.reduce((sum, line) => {
        return Number(line.productid) === Number(productId)
          ? sum + Number(line.quantity || 0)
          : sum;
      }, 0);
      return baseQty + variantQty;
    },
    [orderItems, variantLines]
  );

  const openOptionsModal = useCallback(
    async (product) => {
      if (!product?.id) return;
      setOptionsLoading(true);
      setOptionsProduct(product);
      const productId = Number(product.id);
      let variants = variantsByProduct?.[productId] || [];
      if (!variants.length) {
        variants = [
          {
            id: null,
            label: "Default",
            price: product?.price,
            priceaftertax: product?.priceaftertax,
            taxedprice: product?.taxedprice,
            taxamount: product?.taxamount,
            sgst: product?.sgst,
            cgst: product?.cgst,
            is_default: 1,
          },
        ];
      }
      setOptionsVariants(variants);
      const defaultVariant =
        variants.find((variant) => Number(variant?.is_default) === 1) ||
        variants[0];
      setOptionsSelectedVariantId(defaultVariant?.id ?? null);

      let addons = addonsByProduct?.[productId];
      if (!Array.isArray(addons)) {
        addons = await fetchAddonsForProduct(productId);
      }
      setOptionsAddons(Array.isArray(addons) ? addons : []);
      setOptionsSelectedAddonIds([]);
      setOptionsOpen(true);
      setOptionsLoading(false);
    },
    [variantsByProduct, addonsByProduct, fetchAddonsForProduct]
  );

  const closeOptionsModal = useCallback(() => {
    setOptionsOpen(false);
    setOptionsProduct(null);
    setOptionsVariants([]);
    setOptionsAddons([]);
    setOptionsSelectedVariantId(null);
    setOptionsSelectedAddonIds([]);
  }, []);

  const toggleOptionAddon = (addonId) => {
    setOptionsSelectedAddonIds((prev) => {
      const exists = prev.includes(addonId);
      if (exists) return prev.filter((id) => id !== addonId);
      return [...prev, addonId];
    });
  };

  const confirmOptionsAdd = useCallback(() => {
    if (!optionsProduct) return;
    ensureStartTime();
    const variants = optionsVariants || [];
    const variant =
      variants.find(
        (item) => String(item?.id) === String(optionsSelectedVariantId)
      ) || variants[0];
    const addons = optionsAddons.filter((addon) =>
      optionsSelectedAddonIds.includes(addon.id)
    );
    const line = buildVariantLine(optionsProduct, variant, addons);
    setVariantLines((prev) => {
      const existing = prev.find((entry) => entry.lineKey === line.lineKey);
      if (existing) {
        return prev.map((entry) =>
          entry.lineKey === line.lineKey
            ? { ...entry, quantity: Number(entry.quantity || 0) + 1 }
            : entry
        );
      }
      return [...prev, line];
    });
    closeOptionsModal();
  }, [
    optionsProduct,
    optionsVariants,
    optionsSelectedVariantId,
    optionsAddons,
    optionsSelectedAddonIds,
    ensureStartTime,
    closeOptionsModal,
  ]);

  const handleAddOrCustomize = useCallback(
    async (product) => {
      if (!product?.id) return;
      const productId = Number(product.id);
      const variants = variantsByProduct?.[productId] || [];
      const hasMultipleVariants = variants.length > 1;
      if (hasMultipleVariants) {
        await openOptionsModal(product);
        return;
      }
      const cachedAddons = addonsByProduct?.[productId];
      if (Array.isArray(cachedAddons) && cachedAddons.length) {
        await openOptionsModal(product);
        return;
      }
      warmAddonsCache(productId);

      if (variants.length === 1) {
        ensureStartTime();
        const variant =
          variants.find((entry) => Number(entry?.is_default) === 1) ||
          variants[0];
        const line = buildVariantLine(product, variant, []);
        setVariantLines((prev) => {
          const existing = prev.find((entry) => entry.lineKey === line.lineKey);
          if (existing) {
            return prev.map((entry) =>
              entry.lineKey === line.lineKey
                ? { ...entry, quantity: Number(entry.quantity || 0) + 1 }
                : entry
            );
          }
          return [...prev, line];
        });
        return;
      }
      handleAdd(product.id);
    },
    [
      variantsByProduct,
      addonsByProduct,
      warmAddonsCache,
      openOptionsModal,
      handleAdd,
      ensureStartTime,
    ]
  );

  const handleRemove = (productId) => {
    const baseQty = Number(orderItems?.[productId] || 0);
    if (baseQty > 0) {
      setOrderItems((prev) => {
        const q = (prev[productId] || 0) - 1;
        if (q <= 0) {
          const { [productId]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [productId]: q };
      });
      return;
    }
    const line = variantLines.find(
      (entry) => Number(entry.productid) === Number(productId)
    );
    if (!line) return;
    setVariantLines((prev) =>
      prev
        .map((entry) =>
          entry.lineKey === line.lineKey
            ? { ...entry, quantity: Number(entry.quantity || 0) - 1 }
            : entry
        )
        .filter((entry) => Number(entry.quantity || 0) > 0)
    );
  };

  const handleClearItem = (productId) => {
    setOrderItems((prev) => {
      if (!(productId in prev)) return prev;
      const { [productId]: _, ...rest } = prev;
      return rest;
    });
    setVariantLines((prev) =>
      prev.filter((entry) => Number(entry.productid) !== Number(productId))
    );
  };

  const clearEntireOrder = useCallback(async () => {
    setOrderItems({});
    setVariantLines([]);
    setOrderStartTime("");
    setSelectedWaiter("");
    setSelectedParty("");

    // When editing an existing order, do not touch firebase/local cache.
    if (isEditingExisting) return;

    localStorage.removeItem(`ordersitems1${tableId}`);
    localStorage.removeItem(`orderlines1${tableId}`);
    localStorage.removeItem(`orderAmount1${tableId}`);
    localStorage.removeItem(`orderStartTime1${tableId}`);
    localStorage.removeItem(KOT_KEY(tableId));

    try {
      if (restaurantId) {
        const db = ensureFirebase();
        await set(ref(db, `/restaurants/${restaurantId}/tables/${tableId}`), null);
        await set(
          ref(db, `/restaurants/${restaurantId}/tableswaiters/${tableId}`),
          null
        );
      }
    } catch (err) {
      console.error(err);
    }
  }, [restaurantId, tableId, isEditingExisting]);

  /* ---------- totals (GST 9+9 like your page) ---------- */
  // Mobile parity: totals are tax-inclusive (taxedprice). Discount applies on the total.
  const subtotal = cartLines.reduce((sum, line) => {
    const unit = Number(
      line?.taxedprice ?? line?.priceaftertax ?? line?.price ?? 0
    );
    return sum + unit * (Number(line?.quantity || 0) || 0);
  }, 0);

  const discountAmt =
    appliedDiscount.mode === "amount"
      ? Math.min(Math.max(0, Number(appliedDiscount.value || 0)), subtotal)
      : Math.max(
          0,
          (subtotal *
            Math.min(100, Math.max(0, Number(appliedDiscount.value || 0)))) /
            100
        );

  const discountedTotal = Math.max(0, subtotal - discountAmt);

  const deliveryChargeValue = useMemo(() => {
    if (!deliveryEnabledSetting || !deliverySelected) {
      return 0;
    }
    const raw =
      deliveryChargeInput !== ""
        ? deliveryChargeInput
        : deliveryDefaultCharge;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  }, [
    deliveryEnabledSetting,
    deliverySelected,
    deliveryChargeInput,
    deliveryDefaultCharge,
  ]);

  const payableTotal = useMemo(
    () => Math.max(0, discountedTotal + deliveryChargeValue),
    [discountedTotal, deliveryChargeValue]
  );

  const getSplitPayloadForRequest = useCallback(() => {
    const payload = buildSplitPaymentPayload();
    const billTotal = Number(Number(payableTotal || 0).toFixed(2));
    if (payload.total <= 0) return null;
    if (payload.methods.length < 2) return null;
    if (Math.abs(payload.total - billTotal) > 0.01) return null;
    return payload;
  }, [buildSplitPaymentPayload, payableTotal]);

  const validateSplitPayment = useCallback(() => {
    if (paymentMode !== "Split") return true;
    const payload = buildSplitPaymentPayload();
    const billTotal = Number(Number(payableTotal || 0).toFixed(2));
    if (payload.total <= 0 || payload.methods.length < 2) {
      toast.error("Enter split amounts for at least 2 modes.");
      return false;
    }
    if (payload.total > billTotal + 0.01) {
      toast.error(
        `Split total ₹${payload.total.toFixed(2)} exceeds bill total ₹${billTotal.toFixed(2)}.`
      );
      return false;
    }
    if (Math.abs(payload.total - billTotal) > 0.01) {
      toast.error(
        `Split total ₹${payload.total.toFixed(2)} must match bill total ₹${billTotal.toFixed(2)}.`
      );
      return false;
    }
    if (payload.credit > 0 && !selectedParty) {
      toast.info("Select a customer for credit payment.");
      setCustomerModal(true);
      return false;
    }
    return true;
  }, [paymentMode, buildSplitPaymentPayload, payableTotal, selectedParty]);

  const applySplitPayment = useCallback(({ partyIdOverride } = {}) => {
    const activeParty = partyIdOverride ?? selectedParty;
    const payload = buildSplitPaymentPayload();
    const billTotal = Number(Number(payableTotal || 0).toFixed(2));
    if (payload.methods.length < 2) {
      toast.error("Select at least 2 payment modes for split.");
      return;
    }
    if (payload.total > billTotal + 0.01) {
      toast.error(
        `Split total ₹${payload.total.toFixed(2)} exceeds bill total ₹${billTotal.toFixed(2)}.`
      );
      return;
    }
    if (Math.abs(payload.total - billTotal) > 0.01) {
      toast.error(
        `Split total ₹${payload.total.toFixed(2)} must match bill total ₹${billTotal.toFixed(2)}.`
      );
      return;
    }
    if (payload.credit > 0 && !activeParty) {
      splitApplyAfterCustomerRef.current = true;
      toast.info("Select a customer for credit payment.");
      setCustomerModal(true);
      return;
    }
    splitApplyAfterCustomerRef.current = false;
    setPaymentMode("Split");
    setPendingPaymentMode("");
    setShowSplitPaymentModal(false);
  }, [buildSplitPaymentPayload, payableTotal, selectedParty]);

  useEffect(() => {
    if (showSplitPaymentModal) {
      maybeFetchSplitFromOrder();
    }
  }, [showSplitPaymentModal, maybeFetchSplitFromOrder]);
  const splitPreview = useMemo(
    () => buildSplitPaymentPayload(),
    [buildSplitPaymentPayload]
  );
  const splitBillTotal = Number(Number(payableTotal || 0).toFixed(2));
  const splitRemaining = Number((splitBillTotal - splitPreview.total).toFixed(2));
  const splitCanApply =
    splitPreview.total > 0 &&
    splitPreview.methods.length >= 2 &&
    Math.abs(splitPreview.total - splitBillTotal) <= 0.01;

  const printProfile = useMemo(
    () => PRINT_PROFILES[printerWidthSetting] || PRINT_PROFILES["2in"],
    [printerWidthSetting]
  );

  const scrollCategoryIntoView = useCallback((categoryId) => {
    const key = String(categoryId);
    const leftEl = leftCatRefs.current[key];
    const topEl = topCatRefs.current[key];
    if (leftEl?.scrollIntoView) {
      leftEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
    if (topEl?.scrollIntoView) {
      topEl.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, []);

  useEffect(() => {
    if (selectedCategory == null) return;
    scrollCategoryIntoView(selectedCategory);
  }, [selectedCategory, scrollCategoryIntoView]);

  const taxBreakupRows = useMemo(
    () => buildTaxBreakupFromLines(cartLines),
    [cartLines]
  );
  const hasTaxBreakup = useMemo(
    () => taxBreakupRows.some((row) => Number(row?.total || 0) > 0),
    [taxBreakupRows]
  );
  const itemSummaryRows = useMemo(() => {
    return (cartLines || [])
      .filter((line) => Number(line?.quantity || 0) > 0)
      .map((line) => {
        const baseName = line?.name || `#${line?.productid || ""}`;
        const addonSuffix = line?.addon_label
          ? ` + ${line.addon_label}`
          : "";
        const rate = Number(
          line?.priceaftertax ?? line?.taxedprice ?? line?.price ?? 0
        );
        const qty = Number(line?.quantity || 0);
        return {
          key: line?.lineKey || `${line?.productid}-${line?.variantid || "base"}`,
          name: `${baseName}${addonSuffix}`,
          qty,
          rate,
          total: rate * qty,
        };
      });
  }, [cartLines]);
  const summaryRowsToShow = showAllSummaryItems
    ? itemSummaryRows
    : itemSummaryRows.slice(0, 10);
  const taxBreakupTotals = useMemo(
    () =>
      taxBreakupRows.reduce(
        (acc, row) => ({
          taxable:
            acc.taxable +
            (Number(row?.taxableamount ?? row?.taxable ?? row?.taxableAmount) || 0),
          cgst: acc.cgst + (Number(row?.cgst) || 0),
          sgst: acc.sgst + (Number(row?.sgst) || 0),
          total: acc.total + (Number(row?.total ?? 0) || 0),
        }),
        { taxable: 0, cgst: 0, sgst: 0, total: 0 }
      ),
    [taxBreakupRows]
  );
  const cgst = useMemo(
    () => taxBreakupRows.reduce((sum, r) => sum + (Number(r?.cgst) || 0), 0),
    [taxBreakupRows]
  );
  const sgst = useMemo(
    () => taxBreakupRows.reduce((sum, r) => sum + (Number(r?.sgst) || 0), 0),
    [taxBreakupRows]
  );
  const grandTotal = payableTotal;

  const discountPercentForPayload = useMemo(() => {
    if (subtotal <= 0) return 0;
    if (appliedDiscount.mode === "amount") {
      const amt = Math.max(0, Number(appliedDiscount.value || 0));
      return Math.min(100, (amt / subtotal) * 100);
    }
    return Math.min(100, Math.max(0, Number(appliedDiscount.value || 0)));
  }, [appliedDiscount, subtotal]);

  const hasItems = cartLines.some((line) => Number(line?.quantity || 0) > 0);
  useEffect(() => {
    if (hasItems && billCreatedInfo) {
      setBillCreatedInfo(null);
    }
  }, [hasItems, billCreatedInfo]);
  const kotActiveTags = useMemo(
    () => kotQuickTags.filter((tag) => hasTagInNotes(kotNotes, tag)),
    [kotQuickTags, kotNotes]
  );

  /* ---------- KOT (Send to kitchen) ---------- */
  const readKOTDone = useCallback(
    (keyTableId) => {
      try {
        const raw = localStorage.getItem(KOT_KEY(keyTableId));
        const parsed = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(parsed)) return [];
        return parsed.map((item) => {
          if (item?.key) return item;
          const fallbackKey = String(
            item?.productid != null ? item.productid : item?.id || ""
          );
          return { ...item, key: fallbackKey };
        });
      } catch {
        return [];
      }
    },
    []
  );

  const saveKOTDone = useCallback((keyTableId, list) => {
    try {
      localStorage.setItem(KOT_KEY(keyTableId), JSON.stringify(list || []));
    } catch {}
  }, []);

  const mergeKOT = useCallback((doneList, newList) => {
    const map = new Map(
      (doneList || []).map((d) => [
        String(d.key),
        { qty: Number(d.qty) || 0, name: d?.name },
      ])
    );
    (newList || []).forEach((n) => {
      const key = String(n.key);
      const prev = map.get(key) || { qty: 0, name: undefined };
      map.set(key, {
        qty: prev.qty + (Number(n.qty) || 0),
        name: prev.name || n?.name,
      });
    });
    return Array.from(map.entries()).map(([key, value]) => ({
      key,
      qty: value.qty,
      name: value.name,
    }));
  }, []);

  const kotSelectedItems = useMemo(
    () =>
      kotPreviewItems.filter(
        (item) => kotSelectedMap[String(item.key)] !== false
      ),
    [kotPreviewItems, kotSelectedMap]
  );
  const allKotSelected =
    kotPreviewItems.length > 0 &&
    kotSelectedItems.length === kotPreviewItems.length;

  const buildKOTPreview = useCallback(() => {
    if (!hasItems) return { delta: [], done: [] };
    const baseList = cartLines.map((line) => {
      const addonSuffix = line?.addon_label ? ` + ${line.addon_label}` : "";
      const unitPrice = priceOf(line);
      return {
        key: line?.lineKey || `${line?.productid}-${line?.variantid || "base"}`,
        productid: Number(line?.productid),
        qty: Number(line?.quantity || 0),
        name: `${line?.name || `#${line?.productid}`}${addonSuffix}`,
        price: unitPrice,
        rate: unitPrice,
        priceaftertax: unitPrice,
        taxedprice: unitPrice,
      };
    });
    const done = readKOTDone(tableId);
    const delta = baseList
      .map((item) => {
        const already = done.find((d) => String(d.key) === String(item.key));
        const remaining = (item.qty || 0) - (already?.qty || 0);
        return { ...item, qty: remaining };
      })
      .filter((i) => i.qty > 0);
    return { delta, done };
  }, [hasItems, cartLines, readKOTDone, tableId]);

  const openKOTModal = useCallback(() => {
    if (!restaurantId) {
      toast.error("Missing restaurant information.");
      return;
    }
    const { delta, done } = buildKOTPreview();
    if (delta.length === 0) {
      toast.info("No new items to send. All items already sent to kitchen.");
      return;
    }
    const nameByKey = new Map(
      (cartLines || []).map((line) => {
        const addonSuffix = line?.addon_label ? ` + ${line.addon_label}` : "";
        const key = line?.lineKey || `${line?.productid}-${line?.variantid || "base"}`;
        const name = `${line?.name || `#${line?.productid}`}${addonSuffix}`;
        return [String(key), name];
      })
    );
    const doneWithNames = (done || []).map((item) => ({
      ...item,
      name: item?.name || nameByKey.get(String(item.key)),
    }));
    const selection = {};
    delta.forEach((item) => {
      selection[String(item.key)] = true;
    });
    setKotPreviewItems(delta);
    setKotAlreadySent(doneWithNames);
    setKotSelectedMap(selection);
    setKotNotes("");
    setKotModalOpen(true);
  }, [restaurantId, buildKOTPreview, cartLines]);

  useEffect(() => {
    if (!discountModal) return undefined;
    const timeoutId = setTimeout(() => {
      focusAndSelect(discountInputRef.current);
    }, 60);
    return () => clearTimeout(timeoutId);
  }, [discountModal, discountMode]);

  useEffect(() => {
    if (!customerModal) return undefined;
    const timeoutId = setTimeout(() => {
      if (customerTab === "existing") {
        focusAndSelect(customerSearchRef.current);
        return;
      }
      focusAndSelect(customerNameRef.current);
    }, 60);
    return () => clearTimeout(timeoutId);
  }, [customerModal, customerTab]);

  useEffect(() => {
    if (!showSplitPaymentModal) return undefined;
    const timeoutId = setTimeout(() => {
      focusAndSelect(splitCashInputRef.current);
    }, 60);
    return () => clearTimeout(timeoutId);
  }, [showSplitPaymentModal]);

  useEffect(() => {
    if (!kotModalOpen) return undefined;
    const timeoutId = setTimeout(() => {
      focusAndSelect(kotNotesRef.current);
    }, 60);
    return () => clearTimeout(timeoutId);
  }, [kotModalOpen]);

  useEffect(() => {
    const onEscape = (event) => {
      if (postBillModal) {
        setPostBillModal(null);
        event.preventDefault();
        return;
      }
      if (optionsOpen) {
        setOptionsOpen(false);
        event.preventDefault();
        return;
      }
      if (showSplitPaymentModal) {
        setShowSplitPaymentModal(false);
        event.preventDefault();
        return;
      }
      if (customerModal) {
        splitApplyAfterCustomerRef.current = false;
        setPendingPaymentMode("");
        setCustomerModal(false);
        event.preventDefault();
        return;
      }
      if (discountModal) {
        setDiscountModal(false);
        event.preventDefault();
        return;
      }
      if (kotModalOpen && !sendingKOT) {
        setKotModalOpen(false);
        event.preventDefault();
      }
    };

    window.addEventListener(APP_ESCAPE_EVENT, onEscape);
    return () => window.removeEventListener(APP_ESCAPE_EVENT, onEscape);
  }, [
    customerModal,
    discountModal,
    kotModalOpen,
    optionsOpen,
    postBillModal,
    sendingKOT,
    showSplitPaymentModal,
  ]);

  const toggleKotItem = useCallback((key) => {
    const safeKey = String(key || "");
    if (!safeKey) return;
    setKotSelectedMap((prev) => ({
      ...prev,
      [safeKey]: !(prev?.[safeKey] !== false),
    }));
  }, []);

  const selectAllKotItems = useCallback(() => {
    const next = {};
    kotPreviewItems.forEach((item) => {
      next[String(item.key)] = true;
    });
    setKotSelectedMap(next);
  }, [kotPreviewItems]);

  const clearKotItems = useCallback(() => {
    const next = {};
    kotPreviewItems.forEach((item) => {
      next[String(item.key)] = false;
    });
    setKotSelectedMap(next);
  }, [kotPreviewItems]);

  const toggleKotTag = useCallback(
    (tag) => {
      if (hasTagInNotes(kotNotes, tag)) {
        setKotNotes((prev) => removeTagFromNotes(prev, tag));
        return;
      }
      setKotNotes((prev) => addTagToNotes(prev, tag));
    },
    [kotNotes]
  );

  const buildOrderPayload = useCallback(() => {
    const tableKey = tableApiId;
    const splitPayload =
      paymentMode === "Split" ? getSplitPayloadForRequest() : null;
    const finalPaymentMode = splitPayload ? "Split" : paymentMode;
    const base = {
      restaurantid: restaurantId,
      tableid: tableKey,
      waiterid: selectedWaiter || 0,
      itemlist: cartLines.map((line) => {
        const rateAfterTax = Number(
          line?.priceaftertax ?? line?.taxedprice ?? line?.price ?? 0
        );
        return {
          productid: Number(line.productid),
          variantid: line?.variantid ?? null,
          addon_ids: line?.addon_ids || [],
          qty: Number(line.quantity) || 0,
          price: line?.price != null ? String(line?.price) : "0",
          priceaftertax: Number(rateAfterTax).toFixed(2),
        };
      }),
      discount: discountPercentForPayload,
      totalPrice: grandTotal,
      delivery_enabled: deliveryEnabledSetting && deliverySelected ? 1 : 0,
      delivery_charge: Number(deliveryChargeValue.toFixed(2)),
      gstdetails: [
        {
          gst: taxBreakupRows
            .reduce((sum, r) => sum + (Number(r?.total ?? 0) || 0), 0)
            .toFixed(2),
          cgst: cgst.toFixed(2),
          sgst: sgst.toFixed(2),
        },
      ],
      gstbreakup: taxBreakupRows.map((row) => ({
        taxableamount: (
          Number(row?.taxableamount ?? row?.taxable ?? row?.taxableAmount) || 0
        ).toFixed(2),
        cgst: (Number(row?.cgst) || 0).toFixed(2),
        sgst: (Number(row?.sgst) || 0).toFixed(2),
        total: (Number(row?.total ?? 0) || 0).toFixed(2),
      })),
      payment_mode: finalPaymentMode,
      mode: finalPaymentMode,
      split_payment: splitPayload,
    };

    if (isEditingExisting && orderIdForEdit) {
      base.orderid = String(orderIdForEdit);
      base.id = String(orderIdForEdit);
    } else {
      base.partyid = Number(selectedParty) || null;
      base.added_by = userData?.staffid || 0;
    }

    return base;
  }, [
    tableApiId,
    restaurantId,
    selectedWaiter,
    cartLines,
    discountPercentForPayload,
    grandTotal,
    deliveryEnabledSetting,
    deliverySelected,
    deliveryChargeValue,
    taxBreakupRows,
    cgst,
    sgst,
    selectedParty,
    userData?.staffid,
    paymentMode,
    getSplitPayloadForRequest,
    isEditingExisting,
    orderIdForEdit,
  ]);

  const persistOrder = useCallback(async () => {
    if (!hasItems) {
      toast.info("Add items before saving order.");
      return null;
    }
    if (!validateSplitPayment()) {
      return null;
    }
    if (paymentMode === "Credit" && !selectedParty) {
      toast.info("Select a customer before using Credit.");
      return null;
    }
    if (!restaurantId) {
      toast.error("Missing restaurant information.");
      return null;
    }
    const payload = buildOrderPayload();
    const endpoint = isEditingExisting
      ? "https://qr.billsarthi.in/public/api/edit/order"
      : "https://qr.billsarthi.in/public/api/create/order";

    setCreatingOrder(true);
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(
          (json?.errors && json?.errors?.userData?.staffid?.[0]) ||
            json?.message ||
            "Failed to save order"
        );
      }
      toast.success(isEditingExisting ? "Order updated successfully" : "Order created successfully");
      setStatus(isEditingExisting ? "Order updated successfully." : "Order created successfully.");
      if (!isEditingExisting && orderCreatedToneEnabled) {
        try {
          const AudioContextClass = window.AudioContext || window.webkitAudioContext;
          if (AudioContextClass) {
            if (!orderToneCtxRef.current) {
              orderToneCtxRef.current = new AudioContextClass();
            }
            const ctx = orderToneCtxRef.current;
            if (ctx.state === "suspended") {
              await ctx.resume().catch(() => {});
            }
            const playNote = (freq, delay = 0, length = 0.18) => {
              const osc = ctx.createOscillator();
              const gain = ctx.createGain();
              osc.type = "triangle";
              osc.frequency.setValueAtTime(freq, ctx.currentTime + delay);
              gain.gain.setValueAtTime(0.0001, ctx.currentTime + delay);
              gain.gain.exponentialRampToValueAtTime(0.22, ctx.currentTime + delay + 0.01);
              gain.gain.exponentialRampToValueAtTime(
                0.0001,
                ctx.currentTime + delay + length
              );
              osc.connect(gain);
              gain.connect(ctx.destination);
              osc.start(ctx.currentTime + delay);
              osc.stop(ctx.currentTime + delay + length + 0.01);
            };
            // short success tune
            playNote(784, 0.0, 0.16);
            playNote(988, 0.16, 0.16);
            playNote(1174, 0.32, 0.2);
          }
        } catch {
          // ignore sound errors
        }
      }
      const savedOrderId =
        json?.orderid ||
        json?.order_id ||
        json?.id ||
        json?.data?.orderid ||
        json?.data?.order_id ||
        json?.data?.id ||
        json?.order?.id ||
        json?.order?.orderid ||
        payload?.orderid ||
        payload?.id ||
        null;
      return {
        savedOrderId: savedOrderId ? String(savedOrderId) : null,
        response: json,
      };
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to save order");
      return null;
    } finally {
      setCreatingOrder(false);
    }
  }, [
    hasItems,
    restaurantId,
    paymentMode,
    validateSplitPayment,
    selectedParty,
    buildOrderPayload,
    isEditingExisting,
    orderCreatedToneEnabled,
  ]);

  const handleSaveOrder = useCallback(async () => {
    const result = await persistOrder();
    if (!result) return;
    const orderId = result?.savedOrderId || `temp-${Date.now()}`;
    await clearEntireOrder();
    resetAfterOrderCompletion();
    setBillCreatedInfo({
      orderId: String(orderId),
      at: new Date().toISOString(),
    });
    setStatus(`Bill #${orderId} created successfully.`);
  }, [persistOrder, clearEntireOrder, resetAfterOrderCompletion]);

  const handlePrintBill = useCallback(async () => {
    const result = await persistOrder();
    if (!result) return;
    const orderIdForPrint =
      result?.savedOrderId || orderIdForEdit || `temp-${Date.now()}`;
    const modalSnapshot = {
      orderId: String(orderIdForPrint),
      table: orderLocationLabel,
      tablePrefix: orderLocationPrefix,
      paymentMode,
      customerName:
        selectedParty
          ? partyList.find((p) => String(p.id) === String(selectedParty))?.partyname ||
            customerName ||
            ""
          : customerName || "",
      customerMobile:
        selectedParty
          ? partyList.find((p) => String(p.id) === String(selectedParty))
              ?.partycontactpersonmobile || mobile || ""
          : mobile || "",
      waiterName:
        waiters.find((w) => String(w.id) === String(selectedWaiter))?.waitername ||
        waiters.find((w) => String(w.id) === String(selectedWaiter))?.name ||
        "",
      createdAt: new Date().toISOString(),
      subtotal: Number(subtotal || 0),
      discount: Number(discountAmt || 0),
      delivery: Number(deliveryChargeValue || 0),
      total: Number(grandTotal || 0),
      items: (cartLines || []).map((line) => ({
        name: line?.name || `#${line?.productid}`,
        qty: Number(line?.quantity || 0),
        rate: Number(line?.priceaftertax ?? line?.taxedprice ?? line?.price ?? 0),
      })),
    };
    modalSnapshot.qrValue = JSON.stringify({
      orderId: modalSnapshot.orderId,
      table: modalSnapshot.table,
      customer: modalSnapshot.customerName,
      total: Number(modalSnapshot.total || 0).toFixed(2),
      at: modalSnapshot.createdAt,
    });
    await clearEntireOrder();
    resetAfterOrderCompletion();
    setBillCreatedInfo({
      orderId: String(orderIdForPrint),
      at: new Date().toISOString(),
    });
    setPostBillModal(modalSnapshot);
    setStatus(`Bill #${orderIdForPrint} printed.`);
  }, [
    persistOrder,
    orderIdForEdit,
    orderLocationLabel,
    orderLocationPrefix,
    grandTotal,
    paymentMode,
    cartLines,
    subtotal,
    discountAmt,
    deliveryChargeValue,
    clearEntireOrder,
    isTakeawayName,
    selectedParty,
    partyList,
    customerName,
    mobile,
    waiters,
    selectedWaiter,
    resetAfterOrderCompletion,
  ]);

  useEffect(() => {
    if (!postBillModal) return;
    const timer = setTimeout(() => {
      setPostBillModal(null);
    }, 10000);
    return () => clearTimeout(timer);
  }, [postBillModal]);

  const printHtmlWithFallback = useCallback((html) => {
    const popup = window.open("", "_blank", "noopener,noreferrer");
    if (popup) {
      popup.document.open();
      popup.document.write(html);
      popup.document.close();
      setTimeout(() => {
        try {
          popup.focus();
          popup.print();
        } catch {
          // ignore
        }
      }, 80);
      return true;
    }

    // Popup blocked fallback: use hidden iframe print in the same page context.
    try {
      const iframe = document.createElement("iframe");
      iframe.setAttribute("aria-hidden", "true");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      const frameDoc = iframe.contentWindow?.document;
      if (!frameDoc) {
        iframe.remove();
        return false;
      }

      frameDoc.open();
      frameDoc.write(html);
      frameDoc.close();

      const runPrint = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch {
          // ignore
        } finally {
          setTimeout(() => {
            try {
              iframe.remove();
            } catch {
              // ignore
            }
          }, 1200);
        }
      };

      iframe.onload = runPrint;
      setTimeout(runPrint, 250);
      toast.info("Popup blocked. Using direct print mode.");
      return true;
    } catch {
      return false;
    }
  }, []);

  const buildKotPrintPayload = useCallback((items) => {
    const waiterName =
      waiters.find((w) => String(w.id) === String(selectedWaiter))?.waitername ||
      waiters.find((w) => String(w.id) === String(selectedWaiter))?.name ||
      "";
    return {
      table: orderLocationLabel,
      tablePrefix: orderLocationPrefix,
      waiterName,
      notes: kotNotes,
      items: (items || [])
        .map((item) => ({
          name: item?.name || `#${item?.productid || "item"}`,
          qty: Number(item?.qty || 0),
        }))
        .filter((item) => item.qty > 0),
    };
  }, [kotNotes, orderLocationLabel, orderLocationPrefix, waiters, selectedWaiter]);

  const printQuickSlip = useCallback((title, data) => {
    if (!data?.items?.length) return;
    const paper = getPaperSpec(printerWidthSetting);
    const rows = data.items
      .map((it) => {
        const qty = Number(it?.qty || 0);
        const rate = Number(it?.rate || 0);
        const amount = qty * rate;
        return `<tr><td>${it.name}</td><td>${qty}</td><td>₹${rate.toFixed(
          2
        )}</td><td>₹${amount.toFixed(2)}</td></tr>`;
      })
      .join("");
    const a4Header = `
      <div class="inv-head">
        <div>
          <h1>${title}</h1>
          <p class="meta">Bill #${data.orderId}</p>
          <p class="meta">Generated: ${new Date().toLocaleString("en-IN")}</p>
        </div>
        <div class="chip-wrap">
          <span class="chip">${data.tablePrefix || "Table"}: ${data.table || "-"}</span>
          <span class="chip">Payment: ${data.paymentMode || "-"}</span>
        </div>
      </div>
    `;
    const html = `<!doctype html><html><head><meta charset="utf-8"/><title>${title}</title><style>
      @page { size: ${paper.size}; margin: ${paper.margin}; }
      html,body{margin:0 auto;padding:0}
      body{
        width:${paper.thermal ? paper.bodyWidth : "auto"};
        max-width:${paper.thermal ? paper.bodyWidth : paper.bodyWidth};
        font-family:Arial,sans-serif;color:#0f172a;background:#fff
      }
      .inv-head{
        display:flex;justify-content:space-between;align-items:flex-start;gap:10px;
        border-bottom:2px solid #0f172a;padding-bottom:8px;margin-bottom:10px
      }
      h1{margin:0 0 4px;font-size:${paper.key === "a4" ? "24px" : "18px"};font-weight:800}
      .meta{margin:0 0 4px;color:#475569;font-size:${paper.key === "a4" ? "13px" : "12px"}}
      .chip-wrap{display:flex;flex-direction:column;gap:6px;align-items:flex-end}
      .chip{
        border:1px solid #cbd5e1;background:#f8fafc;border-radius:999px;
        padding:4px 10px;font-size:${paper.key === "a4" ? "13px" : "11px"};font-weight:700
      }
      table{width:100%;border-collapse:collapse;font-size:${paper.key === "a4" ? "14px" : "13px"}}
      th,td{border:1px solid #d4d4d8;padding:${paper.key === "a4" ? "8px" : "6px"};text-align:left}
      th{background:#f1f5f9;text-transform:uppercase;letter-spacing:.03em}
      td:nth-child(2),td:nth-child(3),td:nth-child(4){text-align:right}
      tfoot td{font-weight:800;background:#f8fafc}
    </style></head><body>
      ${paper.key === "a4" ? a4Header : `<h2>${title}</h2><p class="meta">Bill #${data.orderId} | ${data.tablePrefix || "Table"} ${data.table}</p>`}
      <table><thead><tr><th>Item</th><th>Qty</th><th>Rate</th><th>Amount</th></tr></thead>
      <tbody>${rows}</tbody>
      <tfoot><tr><td colspan="3">Total</td><td>₹${Number(data.total || 0).toFixed(2)}</td></tr></tfoot></table>
    </body></html>`;
    const printed = printHtmlWithFallback(html);
    if (!printed) {
      toast.info("Unable to open print preview. Please try again.");
      return;
    }
  }, [printHtmlWithFallback, printerWidthSetting]);

  const printDeliveryCopySlip = useCallback((data) => {
    if (!data?.items?.length) return;
    const paper = getPaperSpec(printerWidthSetting);
    const restaurantName =
      userData?.restaurantname ||
      userData?.restname ||
      userData?.restaurant_name ||
      userData?.name ||
      "Restaurant";
    const restaurantPhone =
      userData?.mobile_number ||
      userData?.mobile ||
      userData?.phonenumber ||
      userData?.phone ||
      userData?.contact_number ||
      "-";
    const rows = data.items
      .map((it) => {
        const qty = Number(it?.qty || 0);
        return `<tr><td>${it.name}</td><td>${qty}</td></tr>`;
      })
      .join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"/><title>Delivery Copy</title><style>
      @page { size: ${paper.size}; margin: ${paper.margin}; }
      html,body{margin:0 auto;padding:0}
      body{
        width:${paper.thermal ? paper.bodyWidth : "auto"};
        max-width:${paper.thermal ? paper.bodyWidth : paper.bodyWidth};
        font-family:Arial,sans-serif;color:#111
      }
      h2{margin:0 0 4px} .meta{margin:0 0 8px;color:#475569;font-size:${paper.key === "a4" ? "13px" : "12px"}}
      table{width:100%;border-collapse:collapse;font-size:${paper.key === "a4" ? "14px" : "13px"};margin-top:8px}
      th,td{border:1px solid #ddd;padding:${paper.key === "a4" ? "8px" : "6px"};text-align:left}
      .foot{margin-top:12px;font-weight:800;text-align:center}
    </style></head><body>
      <h2>${restaurantName}</h2>
      <p class="meta">Phone: ${restaurantPhone}</p>
      <p class="meta">Bill #${data.orderId} | ${data.tablePrefix || "Table"} ${data.table}</p>
      <table>
        <thead><tr><th>Product</th><th>Qty</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="foot">Delivery copy</div>
    </body></html>`;
    const printed = printHtmlWithFallback(html);
    if (!printed) {
      toast.info("Unable to open print preview. Please try again.");
      return;
    }
  }, [printHtmlWithFallback, printerWidthSetting, userData]);

  const handlePostBillDeliveryCopy = useCallback(() => {
    if (!postBillModal) return;
    printDeliveryCopySlip(postBillModal);
  }, [postBillModal, printDeliveryCopySlip]);

  const handlePostBillKotPrint = useCallback(() => {
    if (!postBillModal) return;
    printQuickSlip("KOT Print", postBillModal);
  }, [postBillModal, printQuickSlip]);

  const handlePostBillPrint = useCallback(() => {
    if (!postBillModal) return;
    printQuickSlip("Bill Copy", postBillModal);
  }, [postBillModal, printQuickSlip]);

  const handlePostBillNewOrder = useCallback(() => {
    setPostBillModal(null);
    setStatus("Ready for new order.");
  }, []);

  const handlePostBillGoBack = useCallback(() => {
    setPostBillModal(null);
    navigate("/tables");
  }, [navigate]);

  /* ---------- Web Bluetooth print (connect + test print) ---------- */
  const connectBlePrinter = useCallback(async (options = {}) => {
    const allowPrompt = options?.allowPrompt !== false;
    if (!navigator?.bluetooth) {
      throw new Error("Web Bluetooth not supported in this browser.");
    }
    const knownServiceIds = [
      "0000ffe0-0000-1000-8000-00805f9b34fb",
      "0000ffe1-0000-1000-8000-00805f9b34fb",
      "0000ff00-0000-1000-8000-00805f9b34fb",
      "0000ff01-0000-1000-8000-00805f9b34fb",
      "0000ff02-0000-1000-8000-00805f9b34fb",
      "000018f0-0000-1000-8000-00805f9b34fb",
      "000018f1-0000-1000-8000-00805f9b34fb",
    ];
    const knownCharIds = [
      "0000ffe1-0000-1000-8000-00805f9b34fb",
      "0000ff02-0000-1000-8000-00805f9b34fb",
      "0000ff01-0000-1000-8000-00805f9b34fb",
    ];
    const resolveCharacteristicFromDevice = async (device) => {
      if (!device?.gatt) return null;
      device.addEventListener?.("gattserverdisconnected", () => {
        printerCharRef.current = null;
        setBleStatus("Printer disconnected. Tap print to reconnect.");
        console.log("[BLE] disconnected");
      });
      if (!device.gatt.connected) {
        setBleStatus("Connecting…");
        await device.gatt.connect();
      }
      const server = device.gatt;

      for (let attempt = 0; attempt < 2; attempt += 1) {
        const services = await server.getPrimaryServices();
        console.log("[BLE] services", services.map((s) => s.uuid));

        for (const svc of services) {
          const chars = await svc.getCharacteristics();
          console.log("[BLE] characteristics", chars?.length);
          for (const c of chars) {
            const { write, writeWithoutResponse } = c.properties || {};
            if (write || writeWithoutResponse) {
              printerCharRef.current = c;
              try {
                lastBleDeviceIdRef.current = device.id || "";
                rememberBleDevice(device);
              } catch {
                // ignore storage errors
              }
              setBleStatus(`Connected: ${device.name || device.id}`);
              console.log("[BLE] writable characteristic found");
              return c;
            }
          }
        }
        await new Promise((r) => setTimeout(r, 240));
      }

      // Fallback: try known services/characteristics (SC588 family often uses FFE0/FFE1 or FF00/FF02)
      for (const sid of knownServiceIds) {
        try {
          const svc = await server.getPrimaryService(sid);
          const chars = await svc.getCharacteristics();
          for (const c of chars) {
            const { write, writeWithoutResponse } = c.properties || {};
            if (write || writeWithoutResponse) {
              printerCharRef.current = c;
              try {
                lastBleDeviceIdRef.current = device.id || "";
                rememberBleDevice(device);
              } catch {
                // ignore storage errors
              }
              setBleStatus(`Connected: ${device.name || device.id}`);
              console.log("[BLE] writable characteristic found (fallback)");
              return c;
            }
          }
          for (const cid of knownCharIds) {
            try {
              const c = await svc.getCharacteristic(cid);
              const { write, writeWithoutResponse } = c.properties || {};
              if (write || writeWithoutResponse) {
                printerCharRef.current = c;
                rememberBleDevice(device);
                setBleStatus(`Connected: ${device.name || device.id}`);
                console.log("[BLE] writable characteristic found (known char)");
                return c;
              }
            } catch {
              // ignore
            }
          }
        } catch {
          // ignore missing service
        }
      }

      return null;
    };

    // First: try already paired devices without opening pair prompt.
    if (navigator?.bluetooth?.getDevices) {
      try {
        const preferred = lastBleDeviceIdRef.current;
        const paired = await navigator.bluetooth.getDevices();
        const ordered = preferred
          ? [
              ...paired.filter((d) => d.id === preferred),
              ...paired.filter((d) => d.id !== preferred),
            ]
          : paired;
        for (const device of ordered) {
          const connected = await resolveCharacteristicFromDevice(device);
          if (connected) return connected;
        }
      } catch {
        // ignore; may still prompt below
      }
    }

    if (!allowPrompt) {
      throw new Error("Paired printer not reachable right now.");
    }

    // Last: open browser picker only when explicit prompt is allowed.
    console.log("[BLE] connect start");
    setBleStatus("Scanning for printers…");
    const device = await navigator.bluetooth.requestDevice({
      acceptAllDevices: true,
      optionalServices: [
        0xffe0,
        0xffe1,
        0xffe5,
        0x18f0,
        0x18f1,
        0xff00,
        0xff01,
        0xff02,
        "0000ffe0-0000-1000-8000-00805f9b34fb",
        "0000ffe1-0000-1000-8000-00805f9b34fb",
        "0000ff00-0000-1000-8000-00805f9b34fb",
        "0000ff01-0000-1000-8000-00805f9b34fb",
        "0000ff02-0000-1000-8000-00805f9b34fb",
        "000018f0-0000-1000-8000-00805f9b34fb",
        "000018f1-0000-1000-8000-00805f9b34fb",
      ],
    });
    console.log("[BLE] device selected", device?.name || device?.id);
    const connected = await resolveCharacteristicFromDevice(device);
    if (connected) return connected;
    setBleStatus("Connected, but no writable characteristic found.");
    throw new Error("No writable characteristic found. Printer might be classic Bluetooth.");
  }, []);

  const writeBle = useCallback(async (bytes) => {
    const char = printerCharRef.current;
    if (!char) throw new Error("Printer not connected");
    console.log("[BLE] write start", bytes?.length);
    const parts = chunkBytes(bytes, 20);
    const canWriteWithResponse = !!char.writeValue;
    const canWriteWithoutResponse = !!char.writeValueWithoutResponse;
    const shouldUseFastMode =
      canWriteWithoutResponse && (parts.length > 80 || !canWriteWithResponse);

    const sendWithAck = async () => {
      if (!canWriteWithResponse) throw new Error("Write-with-response not available");
      for (let i = 0; i < parts.length; i += 1) {
        await char.writeValue(parts[i]);
        await new Promise((r) => setTimeout(r, 1));
      }
    };

    const sendFast = async () => {
      if (!canWriteWithoutResponse) throw new Error("Fast write not available");
      for (let i = 0; i < parts.length; i += 1) {
        await char.writeValueWithoutResponse(parts[i]);
        await new Promise((r) => setTimeout(r, 3));
        if ((i + 1) % 30 === 0) {
          await new Promise((r) => setTimeout(r, 18));
        }
      }
    };

    if (shouldUseFastMode) {
      try {
        await sendFast();
      } catch (fastErr) {
        if (canWriteWithResponse) {
          await sendWithAck();
        } else {
          throw fastErr;
        }
      }
    } else if (canWriteWithResponse) {
      await sendWithAck();
    } else if (canWriteWithoutResponse) {
      await sendFast();
    } else {
      throw new Error("Selected printer characteristic is not writable.");
    }
    console.log("[BLE] write done");
  }, []);

  const tryAutoReconnect = useCallback(async () => {
    if (!navigator?.bluetooth?.getDevices) return false;
    const lastId = lastBleDeviceIdRef.current;
    const knownServiceIds = [
      "0000ffe0-0000-1000-8000-00805f9b34fb",
      "0000ffe1-0000-1000-8000-00805f9b34fb",
      "0000ff00-0000-1000-8000-00805f9b34fb",
      "0000ff01-0000-1000-8000-00805f9b34fb",
      "0000ff02-0000-1000-8000-00805f9b34fb",
      "000018f0-0000-1000-8000-00805f9b34fb",
      "000018f1-0000-1000-8000-00805f9b34fb",
    ];
    const knownCharIds = [
      "0000ffe1-0000-1000-8000-00805f9b34fb",
      "0000ff02-0000-1000-8000-00805f9b34fb",
      "0000ff01-0000-1000-8000-00805f9b34fb",
    ];
    try {
      const devices = await navigator.bluetooth.getDevices();
      if (!devices?.length) return false;

      const orderedDevices = lastId
        ? [
            ...devices.filter((d) => d.id === lastId),
            ...devices.filter((d) => d.id !== lastId),
          ]
        : devices;

      for (const device of orderedDevices) {
        try {
          if (!device?.gatt) continue;
          if (!device.gatt.connected) {
            await device.gatt.connect();
          }

          const services = await device.gatt.getPrimaryServices();
          for (const svc of services) {
            const chars = await svc.getCharacteristics();
            for (const c of chars) {
              const { write, writeWithoutResponse } = c.properties || {};
              if (write || writeWithoutResponse) {
                printerCharRef.current = c;
                lastBleDeviceIdRef.current = device.id || "";
                rememberBleDevice(device);
                setBleStatus(`Connected: ${device.name || device.id}`);
                return true;
              }
            }
          }

          // Fallback known service/char scan for devices that don't expose full service list reliably.
          for (const sid of knownServiceIds) {
            try {
              const svc = await device.gatt.getPrimaryService(sid);
              const chars = await svc.getCharacteristics();
              for (const c of chars) {
                const { write, writeWithoutResponse } = c.properties || {};
                if (write || writeWithoutResponse) {
                  printerCharRef.current = c;
                  lastBleDeviceIdRef.current = device.id || "";
                  rememberBleDevice(device);
                  setBleStatus(`Connected: ${device.name || device.id}`);
                  return true;
                }
              }
              for (const cid of knownCharIds) {
                try {
                  const c = await svc.getCharacteristic(cid);
                  const { write, writeWithoutResponse } = c.properties || {};
                  if (write || writeWithoutResponse) {
                    printerCharRef.current = c;
                    lastBleDeviceIdRef.current = device.id || "";
                    rememberBleDevice(device);
                    setBleStatus(`Connected: ${device.name || device.id}`);
                    return true;
                  }
                } catch {
                  // try next known characteristic
                }
              }
            } catch {
              // try next known service
            }
          }
        } catch {
          // try next paired device
        }
      }
      return false;
    } catch (e) {
      // ignore silent reconnect failures
      return false;
    }
  }, []);

  useEffect(() => {
    tryAutoReconnect();
  }, [tryAutoReconnect]);

  const ensureBlePrinterReady = useCallback(
    async (reconnectLabel = "Printing…") => {
      if (printerCharRef.current) {
        const activeDevice = printerCharRef.current?.service?.device;
        if (activeDevice?.gatt && !activeDevice.gatt.connected) {
          printerCharRef.current = null;
        }
      }

      if (!printerCharRef.current) {
        const autoConnected = await Promise.race([
          tryAutoReconnect(),
          new Promise((resolve) => setTimeout(() => resolve(false), 2200)),
        ]);
        if (!autoConnected || !printerCharRef.current) {
          const recovered = await tryAutoReconnect();
          if (recovered && printerCharRef.current) {
            setBleStatus(`Printer reconnected. ${reconnectLabel}`);
          } else {
            try {
              await connectBlePrinter({ allowPrompt: false });
            } catch {
              let pairedDevices = [];
              try {
                pairedDevices = navigator?.bluetooth?.getDevices
                  ? await navigator.bluetooth.getDevices()
                  : [];
              } catch {
                pairedDevices = [];
              }
              const hasRememberedPrinter = Boolean(
                localStorage.getItem("printerConnectedId") ||
                  localStorage.getItem("ble_last_device_id") ||
                  localStorage.getItem("printerConnected")
              );
              setBleStatus("No paired BLE printer found.");
              if (!pairedDevices.length && !hasRememberedPrinter) {
                toast.info("No paired printer found. Please pair printer first.");
                navigate("/connect-bluetooth");
                return false;
              }
              try {
                setBleStatus("Reconnecting printer…");
                await connectBlePrinter({ allowPrompt: true });
              } catch {
                toast.info(
                  "Printer paired hai, reconnect failed. Connect screen se reconnect karein."
                );
                navigate("/connect-bluetooth");
                return false;
              }
            }
          }
        }
      }
      return !!printerCharRef.current;
    },
    [tryAutoReconnect, connectBlePrinter, navigate]
  );

  const sendBlePayload = useCallback(
    async (payload, successStatus = "Printed via BLE.") => {
      console.log("[BLE] sending payload");
      const sendWithTimeout = () =>
        Promise.race([
          writeBle(payload),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Print timed out")), 35000)
          ),
        ]);

      try {
        await sendWithTimeout();
      } catch (firstErr) {
        printerCharRef.current = null;
        let reconnected = await tryAutoReconnect();
        if (!reconnected || !printerCharRef.current) {
          try {
            await connectBlePrinter({ allowPrompt: false });
            reconnected = !!printerCharRef.current;
          } catch {
            reconnected = false;
          }
        }
        if (!reconnected || !printerCharRef.current) {
          throw firstErr;
        }
        await sendWithTimeout();
      }

      try {
        const usedDevice = printerCharRef.current?.service?.device;
        if (usedDevice) rememberBleDevice(usedDevice);
      } catch {
        // ignore
      }
      setBleStatus(successStatus);
    },
    [writeBle, tryAutoReconnect, connectBlePrinter]
  );

  const printKotViaBle = useCallback(
    async (data) => {
      if (!data?.items?.length) {
        toast.info("No KOT items to print.");
        return false;
      }

      const printerReady = await ensureBlePrinterReady("Printing KOT…");
      if (!printerReady) {
        return false;
      }

      const receiptText = buildKotReceiptText({
        restaurant:
          userData?.restaurantname ||
          userData?.restname ||
          userData?.restaurant_name ||
          userData?.name ||
          "Restaurant",
        table: data.table,
        tablePrefix: data.tablePrefix,
        waiterName: data.waiterName,
        items: data.items,
        notes: data.notes,
        printProfile,
      });
      const enc = new TextEncoder();
      const chunks = [INIT_CMD, ALIGN_LEFT, enc.encode(receiptText + "\n\n\n"), CUT_CMD];
      const totalLen = chunks.reduce((sum, arr) => sum + arr.length, 0);
      const payload = new Uint8Array(totalLen);
      let offset = 0;
      chunks.forEach((arr) => {
        payload.set(arr, offset);
        offset += arr.length;
      });

      setBleStatus("Printing KOT…");
      await sendBlePayload(payload, "KOT printed via BLE.");
      toast.success("Sent to Bluetooth printer");
      return true;
    },
    [ensureBlePrinterReady, printProfile, sendBlePayload, userData]
  );

  const buildBillPrintPayload = useCallback(async () => {
    const receiptItems = cartLines
      .filter((line) => Number(line?.quantity || 0) > 0)
      .map((line) => {
        const qtyNum = Number(line?.quantity || 0) || 0;
        const rate = Number(
          line?.priceaftertax ?? line?.taxedprice ?? line?.price ?? 0
        );
        const addonSuffix = line?.addon_label ? ` + ${line?.addon_label}` : "";
        return {
          name: `${line?.name || `#${line?.productid}`}${addonSuffix}`,
          qty: qtyNum,
          rate,
          total: rate * qtyNum,
        };
      });

    const customerLabel = selectedParty
      ? partyList.find((p) => String(p.id) === String(selectedParty))?.partyname || ""
      : customerName || "";

    const taxRows = taxBreakupRows;
    const printCgst = cgst;
    const printSgst = sgst;

    const showLogoOnBill = readStorageBool(
      "printer_setting_printLogoOnBill",
      true
    );
    const showQrOnBill = readStorageBool(
      "printer_setting_printUpiQrOnBill",
      true
    );
    const alwaysPrintUpiQr = readStorageBool(
      "printer_setting_alwaysPrintUpiQr",
      false
    );
    const printGoogleReviewQr = readStorageBool(
      "printer_setting_printGoogleReviewQr",
      false
    );
    const gstPrintFormat = readStorageString(
      "printer_setting_gstPrintFormat",
      "both"
    );
    const logoSize = readStorageString("printer_setting_logoSize", "medium");
    const upiQrSize = readStorageString("printer_setting_upiQrSize", "large");
    const showPhoneOnBill =
      String(userData?.showphonenumberonprinter ?? "1") === "1";
    const showThanksOnBill =
      String(userData?.showthanksonbill ?? "1") === "1";
    const printProfileWithSizes = {
      ...printProfile,
      logoWidth: sizeToLogoPixels(logoSize, printProfile.logoWidth || 320),
      qrSize: sizeToQrPixels(upiQrSize, printProfile.qrSize || 220),
    };
    const canPrintUpiQr =
      showQrOnBill &&
      (alwaysPrintUpiQr || String(paymentMode || "").toLowerCase() === "upi");
    const showGstRows = gstPrintFormat !== "none";
    const taxBreakupForPrint =
      showGstRows && showTaxBreakupOnBillSetting && hasTaxBreakup ? taxRows : [];

    const receiptText = buildReceiptText({
      restaurant:
        userData?.restaurantname ||
        userData?.restname ||
        userData?.restaurant_name ||
        userData?.name ||
        "Bill Sarthi",
      addressLines: [
        userData?.addressline1 || userData?.address || "",
        userData?.addressline2 || "",
        userData?.addressline3 || "",
      ].filter(Boolean),
      phone: showPhoneOnBill
        ? userData?.mobile_number ||
          userData?.mobile ||
          userData?.phonenumber ||
          userData?.phone ||
          userData?.contact_number ||
          ""
        : "",
      gstin: userData?.gstno || userData?.gstin || userData?.gstnumber || "",
      table: orderLocationLabel,
      tablePrefix: orderLocationPrefix,
      payment: paymentMode,
      items: receiptItems,
      subtotal,
      discount: discountAmt,
      delivery: deliveryChargeValue,
      cgst: showGstRows && hasTaxBreakup ? printCgst : 0,
      sgst: showGstRows && hasTaxBreakup ? printSgst : 0,
      total: grandTotal,
      taxBreakup: taxBreakupForPrint,
      printProfile: printProfileWithSizes,
      restaurantNameSize: restaurantNameSizeSetting,
      customerName: customerLabel,
      customerNameSize: customerNameSizeSetting,
      showGstRows,
    });

    let logoUrl =
      userData?.logo ||
      userData?.restaurant_logo ||
      userData?.avatar_url ||
      userData?.photo ||
      "";
    if (!logoUrl) {
      logoUrl =
        "https://images.weserv.nl/?url=qr.billsarthi.in/public/photos/1/2025/11/af013f69-554a-42fd-a923-6fcb91f784b4.jpg";
    }
    const upi = (userData?.upiaddress || userData?.upiid || "").trim();
    const logoRasterPromise = showLogoOnBill
      ? logoRasterFromUrl(logoUrl, printProfileWithSizes.logoWidth || 120)
      : Promise.resolve(null);
    const upiQrPromise =
      canPrintUpiQr && upi
        ? qrRasterFromText(
            `upi://pay?pa=${encodeURIComponent(upi)}`,
            printProfileWithSizes.qrSize || 150
          )
        : Promise.resolve(null);
    const reviewUrl =
      userData?.googlereviewurl ||
      userData?.google_review_url ||
      userData?.googleReviewUrl ||
      "";
    const reviewQrPromise =
      printGoogleReviewQr && reviewUrl
        ? qrRasterFromText(reviewUrl, printProfileWithSizes.qrSize || 150)
        : Promise.resolve(null);

    const chunks = [INIT_CMD, ALIGN_LEFT];
    if (showLogoOnBill) {
      try {
        const logoCmd = await logoRasterPromise;
        if (logoCmd) {
          chunks.push(ALIGN_CENTER);
          chunks.push(logoCmd);
          chunks.push(ALIGN_LEFT);
        } else {
          console.log("Logo render skipped: all logo sources failed");
        }
      } catch (error) {
        console.log("Logo render failed", error);
      }
    }
    const enc = new TextEncoder();
    chunks.push(enc.encode(receiptText + "\n"));
    chunks.push(ALIGN_CENTER);
    chunks.push(enc.encode(`Total : ${grandTotal.toFixed(2)}\n`));
    chunks.push(ALIGN_LEFT);

    if (canPrintUpiQr) {
      try {
        if (upi) {
          const qrCmd = await upiQrPromise;
          if (qrCmd) {
            chunks.push(enc.encode("\n"));
            chunks.push(ALIGN_CENTER);
            chunks.push(qrCmd);
            chunks.push(enc.encode("\n"));
            chunks.push(ALIGN_LEFT);
          }
        }
      } catch (error) {
        console.log("QR render failed", error);
      }
    }

    if (printGoogleReviewQr && reviewUrl) {
      try {
        const reviewCmd = await reviewQrPromise;
        if (reviewCmd) {
          chunks.push(enc.encode("\n"));
          chunks.push(ALIGN_CENTER);
          chunks.push(enc.encode("Google Review\n"));
          chunks.push(reviewCmd);
          chunks.push(enc.encode("\n"));
          chunks.push(ALIGN_LEFT);
        }
      } catch (error) {
        console.log("Review QR render failed", error);
      }
    }

    if (showThanksOnBill) {
      chunks.push(ALIGN_CENTER);
      chunks.push(enc.encode("\n"));
      chunks.push(enc.encode("THANKS PLEASE VISIT AGAIN\n"));
      chunks.push(ALIGN_LEFT);
    }

    chunks.push(enc.encode("\n\n\n"));
    chunks.push(CUT_CMD);

    const totalLen = chunks.reduce((sum, arr) => sum + arr.length, 0);
    const payload = new Uint8Array(totalLen);
    let offset = 0;
    chunks.forEach((arr) => {
      payload.set(arr, offset);
      offset += arr.length;
    });

    return payload;
  }, [
    cartLines,
    selectedParty,
    partyList,
    customerName,
    taxBreakupRows,
    cgst,
    sgst,
    userData?.restaurantname,
    userData?.restname,
    userData?.restaurant_name,
    userData?.name,
    userData?.addressline1,
    userData?.address,
    userData?.addressline2,
    userData?.addressline3,
    userData?.mobile_number,
    userData?.mobile,
    userData?.phonenumber,
    userData?.phone,
    userData?.contact_number,
    userData?.gstno,
    userData?.gstin,
    userData?.gstnumber,
    userData?.showphonenumberonprinter,
    userData?.showthanksonbill,
    userData?.logo,
    userData?.restaurant_logo,
    userData?.avatar_url,
    userData?.photo,
    userData?.upiaddress,
    userData?.upiid,
    userData?.googlereviewurl,
    userData?.google_review_url,
    userData?.googleReviewUrl,
    orderLocationLabel,
    orderLocationPrefix,
    paymentMode,
    subtotal,
    discountAmt,
    deliveryChargeValue,
    grandTotal,
    printProfile,
    restaurantNameSizeSetting,
    customerNameSizeSetting,
    showTaxBreakupOnBillSetting,
    hasTaxBreakup,
  ]);

  const handleBlePrint = useCallback(async () => {
    console.log("[BLE] print click");
    if (!hasItems) {
      toast.info("Add items before printing bill.");
      return;
    }
    if (paymentMode === "Credit" && !selectedParty) {
      toast.info("Select a customer before using Credit.");
      return;
    }
    setBlePrinting(true);
    try {
      const printerReady = await ensureBlePrinterReady();
      if (!printerReady) {
        return;
      }

      console.log("[BLE] printer ready, building receipt");
      const receiptItems = cartLines
        .filter((line) => Number(line?.quantity || 0) > 0)
        .map((line) => {
          const qtyNum = Number(line?.quantity || 0) || 0;
          const rate = Number(
            line?.priceaftertax ?? line?.taxedprice ?? line?.price ?? 0
          );
          const addonSuffix = line?.addon_label
            ? ` + ${line.addon_label}`
            : "";
          return {
            name: `${line?.name || `#${line?.productid}`}${addonSuffix}`,
            qty: qtyNum,
            rate,
            total: rate * qtyNum,
          };
        });

      const customerLabel = selectedParty
        ? partyList.find((p) => String(p.id) === String(selectedParty))?.partyname || ""
        : customerName || "";

      const taxRows = taxBreakupRows;
      const printCgst = cgst;
      const printSgst = sgst;

      const showLogoOnBill = readStorageBool(
        "printer_setting_printLogoOnBill",
        true
      );
      const showQrOnBill = readStorageBool(
        "printer_setting_printUpiQrOnBill",
        true
      );
      const alwaysPrintUpiQr = readStorageBool(
        "printer_setting_alwaysPrintUpiQr",
        false
      );
      const printGoogleReviewQr = readStorageBool(
        "printer_setting_printGoogleReviewQr",
        false
      );
      const gstPrintFormat = readStorageString(
        "printer_setting_gstPrintFormat",
        "both"
      );
      const logoSize = readStorageString("printer_setting_logoSize", "medium");
      const upiQrSize = readStorageString("printer_setting_upiQrSize", "large");
      const googleReviewQrSize = readStorageString(
        "printer_setting_googleReviewQrSize",
        "small"
      );
      const showPhoneOnBill =
        String(userData?.showphonenumberonprinter ?? "1") === "1";
      const showThanksOnBill =
        String(userData?.showthanksonbill ?? "1") === "1";
      const printProfileWithSizes = {
        ...printProfile,
        logoWidth: sizeToLogoPixels(logoSize, printProfile.logoWidth || 320),
        qrSize: sizeToQrPixels(upiQrSize, printProfile.qrSize || 220),
      };
      const reviewQrModuleSize =
        googleReviewQrSize === "large"
          ? printProfileWithSizes.lineWidth >= 48
            ? 7
            : 5
          : googleReviewQrSize === "small"
          ? 4
          : printProfileWithSizes.lineWidth >= 48
          ? 6
          : 5;
      const canPrintUpiQr =
        showQrOnBill &&
        (alwaysPrintUpiQr || String(paymentMode || "").toLowerCase() === "upi");
      const showGstRows = gstPrintFormat !== "none";
      const taxBreakupForPrint =
        showGstRows && showTaxBreakupOnBillSetting && hasTaxBreakup
          ? taxRows
          : [];

      const receiptText = buildReceiptText({
        restaurant:
          userData?.restaurantname ||
          userData?.restname ||
          userData?.restaurant_name ||
          userData?.name ||
          "Bill Sarthi",
        addressLines: [
          userData?.addressline1 || userData?.address || "",
          userData?.addressline2 || "",
          userData?.addressline3 || "",
        ].filter(Boolean),
        phone: showPhoneOnBill
          ? userData?.mobile_number ||
            userData?.mobile ||
            userData?.phonenumber ||
            userData?.phone ||
            userData?.contact_number ||
            ""
          : "",
        gstin: userData?.gstno || userData?.gstin || userData?.gstnumber || "",
        table: orderLocationLabel,
        tablePrefix: orderLocationPrefix,
        payment: paymentMode,
        items: receiptItems,
        subtotal,
        discount: discountAmt,
        delivery: deliveryChargeValue,
        cgst: showGstRows && hasTaxBreakup ? printCgst : 0,
        sgst: showGstRows && hasTaxBreakup ? printSgst : 0,
        total: grandTotal,
        taxBreakup: taxBreakupForPrint,
        printProfile: printProfileWithSizes,
        restaurantNameSize: restaurantNameSizeSetting,
        customerName: customerLabel,
        customerNameSize: customerNameSizeSetting,
        showGstRows,
      });

      let logoUrl =
        userData?.logo ||
        userData?.restaurant_logo ||
        userData?.avatar_url ||
        userData?.photo ||
        "";
      if (!logoUrl) {
        logoUrl =
          "https://images.weserv.nl/?url=qr.billsarthi.in/public/photos/1/2025/11/af013f69-554a-42fd-a923-6fcb91f784b4.jpg";
      }
      const upi = (userData?.upiaddress || userData?.upiid || "").trim();
      const logoRasterPromise = showLogoOnBill
        ? logoRasterFromUrl(logoUrl, printProfileWithSizes.logoWidth || 120)
        : Promise.resolve(null);
      const upiQrPromise =
        canPrintUpiQr && upi
          ? qrRasterFromText(
              `upi://pay?pa=${encodeURIComponent(upi)}`,
              printProfileWithSizes.qrSize || 150
            )
          : Promise.resolve(null);
      const reviewUrl =
        userData?.googlereviewurl ||
        userData?.google_review_url ||
        userData?.googleReviewUrl ||
        "";
      const reviewQrPromise =
        printGoogleReviewQr && reviewUrl
          ? qrRasterFromText(reviewUrl, printProfileWithSizes.qrSize || 150)
          : Promise.resolve(null);

      const chunks = [INIT_CMD, ALIGN_LEFT];
      if (showLogoOnBill) {
        try {
          const logoCmd = await logoRasterPromise;
          if (logoCmd) {
            chunks.push(ALIGN_CENTER);
            chunks.push(logoCmd);
            chunks.push(ALIGN_LEFT);
          } else {
            console.log("Logo render skipped: all logo sources failed");
          }
        } catch (e) {
          console.log("Logo render failed", e);
        }
      }
      const enc = new TextEncoder();
      chunks.push(enc.encode(receiptText + "\n"));
      chunks.push(ALIGN_CENTER);
      chunks.push(enc.encode(`Total : ${grandTotal.toFixed(2)}\n`));
      chunks.push(ALIGN_LEFT);

      if (canPrintUpiQr) {
        try {
          if (upi) {
            const qrCmd = await upiQrPromise;
            if (qrCmd) {
              chunks.push(enc.encode("\n"));
              chunks.push(ALIGN_CENTER);
              chunks.push(qrCmd);
              chunks.push(enc.encode("\n"));
              chunks.push(ALIGN_LEFT);
            }
          }
        } catch (e) {
          console.log("QR render failed", e);
        }
      }
      if (printGoogleReviewQr) {
        if (reviewUrl) {
          try {
            const reviewCmd = await reviewQrPromise;
            if (reviewCmd) {
              chunks.push(enc.encode("\n"));
              chunks.push(ALIGN_CENTER);
              chunks.push(enc.encode("Google Review\n"));
              chunks.push(reviewCmd);
              chunks.push(enc.encode("\n"));
              chunks.push(ALIGN_LEFT);
            }
          } catch (e) {
            console.log("Review QR render failed", e);
          }
        }
      }

      if (showThanksOnBill) {
        chunks.push(ALIGN_CENTER);
        chunks.push(enc.encode("\n"));
        chunks.push(enc.encode("THANKS PLEASE VISIT AGAIN\n"));
        chunks.push(ALIGN_LEFT);
      }

      chunks.push(enc.encode("\n\n\n"));
      chunks.push(CUT_CMD);

      const totalLen = chunks.reduce((s, arr) => s + arr.length, 0);
      const payload = new Uint8Array(totalLen);
      let offset = 0;
      chunks.forEach((arr) => {
        payload.set(arr, offset);
        offset += arr.length;
      });

      await sendBlePayload(payload);
      toast.success("Sent to Bluetooth printer");
      console.log("[BLE] print success");
      await handleSaveOrder();
    } catch (err) {
      console.error(err);
      setBleStatus(err?.message || "Bluetooth print failed");
      toast.error(err?.message || "Bluetooth print failed");
      console.log("[BLE] print failed", err?.message || err);
    } finally {
      setBlePrinting(false);
    }
  }, [
    hasItems,
    ensureBlePrinterReady,
    cartLines,
    orderLocationLabel,
    orderLocationPrefix,
    subtotal,
    discountAmt,
    deliveryChargeValue,
    cgst,
    sgst,
    grandTotal,
    paymentMode,
    customerName,
    selectedParty,
    partyList,
    restaurantNameSizeSetting,
    customerNameSizeSetting,
    userData?.restaurantname,
    userData?.restname,
    userData?.restaurant_name,
    userData?.name,
    userData?.addressline1,
    userData?.addressline2,
    userData?.addressline3,
    userData?.address,
    userData?.mobile_number,
    userData?.mobile,
    userData?.phonenumber,
    userData?.phone,
    userData?.contact_number,
    userData?.gstno,
    userData?.gstin,
    userData?.gstnumber,
    userData?.logo,
    userData?.restaurant_logo,
    userData?.avatar_url,
    userData?.upiaddress,
    userData?.upiid,
    userData?.showlogoonprinter,
    userData?.showqrcodeonprinter,
    userData?.showphonenumberonprinter,
    userData?.showthanksonbill,
    taxBreakupRows,
    hasTaxBreakup,
    paymentMode,
    showTaxBreakupOnBillSetting,
    printProfile,
    handleSaveOrder,
    sendBlePayload,
  ]);

  const handleUsbPrint = useCallback(async () => {
    console.log("[USB] print click");
    if (!hasItems) {
      toast.info("Add items before printing bill.");
      return;
    }
    if (paymentMode === "Credit" && !selectedParty) {
      toast.info("Select a customer before using Credit.");
      return;
    }

    setUsbPrinting(true);
    try {
      setUsbStatus("Checking USB printer...");
      const payload = await buildBillPrintPayload();
      const sendWithTimeout = (allowPrompt) =>
        Promise.race([
          writeUsb(payload, { allowPrompt, onStatus: setUsbStatus }),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("USB print timed out")), 35000)
          ),
        ]);

      try {
        await sendWithTimeout(false);
      } catch {
        await resetUsbPrinterConnection();
        await sendWithTimeout(true);
      }

      setUsbStatus("Printed via USB.");
      toast.success("Sent to USB printer");
      console.log("[USB] print success");
      await handleSaveOrder();
    } catch (err) {
      console.error(err);
      const message =
        err?.name === "NotFoundError"
          ? "USB printer selection cancelled."
          : err?.message || "USB print failed";
      setUsbStatus(message);
      toast.error(message);
      console.log("[USB] print failed", message);
    } finally {
      setUsbPrinting(false);
    }
  }, [
    hasItems,
    paymentMode,
    selectedParty,
    buildBillPrintPayload,
    handleSaveOrder,
  ]);

  useEffect(() => {
    const onShortcut = (event) => {
      const id = event?.detail?.id;
      if (!String(id || "").startsWith("order.")) return;

      const hasBlockingModal =
        discountModal ||
        customerModal ||
        showSplitPaymentModal ||
        kotModalOpen ||
        optionsOpen ||
        Boolean(postBillModal);
      const isBusy =
        creatingOrder || blePrinting || usbPrinting || sendingKOT || syncing;

      switch (id) {
        case "order.search":
          if (hasBlockingModal) return;
          if (focusProductSearch()) event.preventDefault();
          return;
        case "order.save":
          if (hasBlockingModal || isBusy) return;
          event.preventDefault();
          handleSaveOrder();
          return;
        case "order.print":
          if (hasBlockingModal || isBusy) return;
          event.preventDefault();
          handleBlePrint();
          return;
        case "order.usb-print":
          if (hasBlockingModal || isBusy) return;
          event.preventDefault();
          handleUsbPrint();
          return;
        case "order.kot":
          if (hasBlockingModal || isBusy) return;
          event.preventDefault();
          openKOTModal();
          return;
        case "order.customer":
          if (hasBlockingModal && !customerModal) return;
          event.preventDefault();
          setCustomerModal(true);
          return;
        case "order.discount":
          if (hasBlockingModal && !discountModal) return;
          event.preventDefault();
          setDiscountModal(true);
          return;
        case "order.payment-mode":
          if (hasBlockingModal) return;
          if (focusAndSelect(paymentSelectRef.current)) event.preventDefault();
          return;
        case "order.waiter":
          if (hasBlockingModal) return;
          if (focusAndSelect(waiterSelectRef.current)) event.preventDefault();
          return;
        default:
          return;
      }
    };

    window.addEventListener(APP_SHORTCUT_EVENT, onShortcut);
    return () => window.removeEventListener(APP_SHORTCUT_EVENT, onShortcut);
  }, [
    blePrinting,
    creatingOrder,
    customerModal,
    discountModal,
    focusProductSearch,
    handleBlePrint,
    handleSaveOrder,
    handleUsbPrint,
    kotModalOpen,
    openKOTModal,
    optionsOpen,
    postBillModal,
    sendingKOT,
    showSplitPaymentModal,
    syncing,
    usbPrinting,
  ]);

  const sendKOT = useCallback(async (options = {}) => {
    const shouldPrint = options?.shouldPrint === true;
    if (!restaurantId) {
      toast.error("Missing restaurant information.");
      return;
    }
    if (kotPreviewItems.length === 0) {
      toast.info("No new items to send.");
      return;
    }
    if (kotSelectedItems.length === 0) {
      toast.info("Select at least one item for KOT.");
      return;
    }

    const kotPrintPayload = shouldPrint ? buildKotPrintPayload(kotSelectedItems) : null;
    const payload = {
      restaurantid: restaurantId,
      tableid: tableApiId,
      special_notes: kotNotes,
      itemlist: kotSelectedItems.map((d) => {
        const qty = Number(d?.qty || 0);
        const rate = priceOf(d);
        return {
          name: d.name,
          productid: d.productid,
          qty,
          price: rate,
          unit_price: rate,
          item_price: rate,
          rate,
          priceaftertax: rate,
          taxedprice: rate,
          amount: Number((qty * rate).toFixed(2)),
        };
      }),
      waiterid: selectedWaiter || 0,
      party: selectedParty || null,
    };

    setSendingKOT(true);
    try {
      if (shouldPrint && kotPrintPayload?.items?.length) {
        const printed = await printKotViaBle(kotPrintPayload);
        if (!printed) return;
      }

      const res = await fetch("https://qr.billsarthi.in/public/api/create/kot", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.message || "Failed to send KOT");
      }

      const merged = mergeKOT(kotAlreadySent, kotSelectedItems);
      saveKOTDone(tableId, merged);
      setStatus("KOT sent to kitchen.");
      toast.success("KOT sent to kitchen.");
      setKotModalOpen(false);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to send KOT");
    } finally {
      setSendingKOT(false);
    }
  }, [
    restaurantId,
    kotPreviewItems,
    kotSelectedItems,
    tableApiId,
    kotNotes,
    selectedWaiter,
    selectedParty,
    mergeKOT,
    kotAlreadySent,
    saveKOTDone,
    tableId,
    buildKotPrintPayload,
    printKotViaBle,
  ]);

  const handleSendKOT = useCallback(() => {
    sendKOT();
  }, [sendKOT]);

  const handleSendAndPrintKOT = useCallback(() => {
    sendKOT({ shouldPrint: true });
  }, [sendKOT]);

  /* ---------- hydrate from Firebase / storage ---------- */
  const hydrateOrder = useCallback(async () => {
    if (!restaurantId || !tableId) return;
    setStatus("");
    try {
      const db = ensureFirebase();
      const snap = await get(ref(db, `/restaurants/${restaurantId}/tables/${tableId}`));
      if (snap.exists()) {
        const data = snap.val() || {};
        const items = parseOrderObj(data.products);
        setOrderItems(items);
        if (Array.isArray(data.lineitems) || Array.isArray(data.line_items)) {
          const lineItems = Array.isArray(data.lineitems)
            ? data.lineitems
            : data.line_items;
          const { optionLines } = splitLineItems(lineItems || []);
          setVariantLines(optionLines || []);
          localStorage.setItem(
            `orderlines1${tableId}`,
            JSON.stringify(buildLineItemsPayload(optionLines || []))
          );
        }
        if (data.duration) setOrderStartTime(data.duration);
        else if (Object.keys(items).length > 0) setOrderStartTime(new Date().toISOString());
        if (data.waiterid) setSelectedWaiter(String(data.waiterid));
        if (data.party) setSelectedParty(String(data.party));
        if (data.amount !== undefined)
          localStorage.setItem(`orderAmount1${tableId}`, String(data.amount));
        if (data.duration)
          localStorage.setItem(`orderStartTime1${tableId}`, String(data.duration));
        localStorage.setItem(`ordersitems1${tableId}`, JSON.stringify(items));
        setStatus("Live data loaded from kitchen.");
      } else {
        throw new Error("No live data");
      }
    } catch {
      // fallback to local cache
      const cachedItems = parseOrderObj(localStorage.getItem(`ordersitems1${tableId}`));
      const cachedStart = localStorage.getItem(`orderStartTime1${tableId}`) || "";
      const cachedLinesRaw = localStorage.getItem(`orderlines1${tableId}`);
      let cachedLines = [];
      if (cachedLinesRaw) {
        try {
          cachedLines = JSON.parse(cachedLinesRaw) || [];
        } catch {
          cachedLines = [];
        }
      }
      const cachedWaiter = localStorage.getItem(`orderWaiter1${tableId}`) || "";
      const cachedParty = localStorage.getItem(`orderParty1${tableId}`) || "";
      if (Object.keys(cachedItems).length > 0) {
        setOrderItems(cachedItems);
        const { optionLines } = splitLineItems(cachedLines);
        setVariantLines(optionLines || []);
        setOrderStartTime(cachedStart || new Date().toISOString());
        setSelectedWaiter(cachedWaiter || "");
        setSelectedParty(cachedParty || "");
        setStatus("Offline mode — using saved order.");
      } else {
        setOrderItems({});
        setVariantLines([]);
        setStatus("");
      }
    } finally {
      setHydrated(true);
    }
  }, [restaurantId, tableId]);

  useEffect(() => {
    if (orderIdForEdit) return;
    if (openedAsQuickBill) return;
    hydrateOrder();
  }, [hydrateOrder, orderIdForEdit, openedAsQuickBill]);

  // Quick Order should always start with a fresh empty cart (no hold/hydrated order).
  useEffect(() => {
    if (!openedAsQuickBill) return;
    if (isEditingExisting || openedFromQrNotification) return;
    let cancelled = false;
    (async () => {
      await clearEntireOrder();
      if (cancelled) return;
      resetAfterOrderCompletion();
      setHydrated(true);
      setStatus("");
    })();
    return () => {
      cancelled = true;
    };
  }, [
    openedAsQuickBill,
    isEditingExisting,
    openedFromQrNotification,
    clearEntireOrder,
    resetAfterOrderCompletion,
  ]);

  /* ---------- sync order to local + Firebase (like mobile) ---------- */
  const syncOrder = useCallback(async () => {
    if (!hydrated || !tableId || isEditingExisting) return;

    const cleaned = Object.fromEntries(
      Object.entries(aggregatedItems).filter(([, qty]) => Number(qty) > 0)
    );

    // Empty order => clear everywhere
    if (Object.keys(cleaned).length === 0) {
      const hasLocal =
        localStorage.getItem(`ordersitems1${tableId}`) ||
        localStorage.getItem(`orderStartTime1${tableId}`);
      if (!hasLocal && !orderStartTime) return; // already clear; avoid loop
      await clearEntireOrder();
      return;
    }

    const start = orderStartTime || new Date().toISOString();
    setOrderStartTime(start);

    // Local parity keys for TablePage stats
    localStorage.setItem(`ordersitems1${tableId}`, JSON.stringify(cleaned));
    localStorage.setItem(
      `orderlines1${tableId}`,
      JSON.stringify(buildLineItemsPayload(cartLines))
    );
    localStorage.setItem(`orderAmount1${tableId}`, String(grandTotal));
    localStorage.setItem(`orderStartTime1${tableId}`, start);
    if (selectedWaiter)
      localStorage.setItem(`orderWaiter1${tableId}`, String(selectedWaiter));
    if (selectedParty)
      localStorage.setItem(`orderParty1${tableId}`, String(selectedParty));

    if (!restaurantId) return;

    setSyncing(true);
    try {
      const db = ensureFirebase();
      const payload = {
        products: cleaned,
        updated_by: "web",
        updated_at: Date.now(),
        amount: grandTotal,
        duration: start,
        waiterid: selectedWaiter || null,
        waitername: waiterLabel(
          waiters.find((w) => String(w.id) === String(selectedWaiter))
        ),
        party: selectedParty || null,
      };

      await set(ref(db, `/restaurants/${restaurantId}/tables/${tableId}`), payload);

      if (selectedWaiter) {
        await set(
          ref(db, `/restaurants/${restaurantId}/tableswaiters/${tableId}`),
          {
            waiterid: selectedWaiter,
            waitername: waiterLabel(
              waiters.find((w) => String(w.id) === String(selectedWaiter))
            ),
          }
        );
      }
      setStatus("Saved to kitchen.");
    } catch (err) {
      console.error(err);
      setStatus("Offline — order saved locally.");
    } finally {
      setSyncing(false);
    }
  }, [
    hydrated,
    tableId,
    aggregatedItems,
    cartLines,
    orderStartTime,
    grandTotal,
    restaurantId,
    selectedWaiter,
    waiters,
    clearEntireOrder,
    isEditingExisting,
  ]);

  useEffect(() => {
    syncOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderItems,
    variantLines,
    appliedDiscount,
    selectedWaiter,
    selectedParty,
    tableId,
    grandTotal,
    products.length,
  ]);

  useEffect(() => {
    const layoutKey = "pos_category_layout";
    const deliveryEnabledKey = "pos_delivery_enabled";
    const deliveryChargeKey = "pos_delivery_charge";
    const taxBreakupKey = "pos_show_tax_breakup_on_bill";
    const printerWidthKey = "printerWidth";
    const printerWidthLegacyKey = "printer_width";
    const restaurantNameSizeKey = "pos_restaurant_name_size";
    const customerNameSizeKey = "pos_customer_name_size";
    const savedLayout = localStorage.getItem(layoutKey);
    if (savedLayout === "horizontal" || savedLayout === "vertical") {
      setCategoryLayout(savedLayout);
    }
    const savedDeliveryEnabled = localStorage.getItem(deliveryEnabledKey);
    if (savedDeliveryEnabled !== null) {
      setDeliveryEnabledSetting(savedDeliveryEnabled === "true");
    }
    const savedDeliveryCharge = localStorage.getItem(deliveryChargeKey);
    const normalizedCharge =
      savedDeliveryCharge !== null ? savedDeliveryCharge : "0";
    setDeliveryDefaultCharge(normalizedCharge);
    setDeliveryChargeInput((prev) =>
      prev && prev.trim() !== "" ? prev : normalizedCharge
    );
    const savedTaxBreakup = localStorage.getItem(taxBreakupKey);
    if (savedTaxBreakup !== null) {
      setShowTaxBreakupOnBillSetting(savedTaxBreakup === "true");
    }
    const savedPrinterWidth =
      localStorage.getItem(printerWidthKey) ||
      localStorage.getItem(printerWidthLegacyKey);
    const normalizedWidth = normalizePrinterWidthPreference(savedPrinterWidth);
    if (normalizedWidth) {
      setPrinterWidthSetting(normalizedWidth);
    }
    const savedRestaurantNameSize = localStorage.getItem(restaurantNameSizeKey);
    if (savedRestaurantNameSize === "large" || savedRestaurantNameSize === "small") {
      setRestaurantNameSizeSetting(savedRestaurantNameSize);
    }
    const savedCustomerNameSize = localStorage.getItem(customerNameSizeKey);
    const savedOrderCreatedTone = localStorage.getItem(ORDER_CREATED_TONE_KEY);
    if (savedCustomerNameSize === "large" || savedCustomerNameSize === "small") {
      setCustomerNameSizeSetting(savedCustomerNameSize);
    }
    if (savedOrderCreatedTone !== null) {
      setOrderCreatedToneEnabled(savedOrderCreatedTone === "true");
    }
    const onStorage = (event) => {
      if (event.key === layoutKey) {
        const next = event.newValue;
        setCategoryLayout(next === "horizontal" ? "horizontal" : "vertical");
        return;
      }
      if (event.key === deliveryEnabledKey) {
        setDeliveryEnabledSetting(event.newValue === "true");
        return;
      }
      if (event.key === deliveryChargeKey) {
        const next = event.newValue ?? "0";
        setDeliveryDefaultCharge(next);
        setDeliveryChargeInput((prev) =>
          prev && prev.trim() !== "" ? prev : next
        );
        return;
      }
      if (event.key === taxBreakupKey) {
        setShowTaxBreakupOnBillSetting(event.newValue === "true");
        return;
      }
      if (event.key === printerWidthKey || event.key === printerWidthLegacyKey) {
        const next = normalizePrinterWidthPreference(event.newValue) || "2in";
        setPrinterWidthSetting(next);
        return;
      }
      if (event.key === restaurantNameSizeKey) {
        const next = event.newValue;
        if (next === "large" || next === "small") {
          setRestaurantNameSizeSetting(next);
        }
        return;
      }
      if (event.key === customerNameSizeKey) {
        const next = event.newValue;
        if (next === "large" || next === "small") {
          setCustomerNameSizeSetting(next);
        }
        return;
      }
      if (event.key === ORDER_CREATED_TONE_KEY) {
        setOrderCreatedToneEnabled(event.newValue !== "false");
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (!deliveryEnabledSetting) {
      setDeliverySelected(false);
    }
  }, [deliveryEnabledSetting]);

  /* ---------- guard: if no table present, push back ---------- */
  useEffect(() => {
    if (!routeTableId) {
      navigate("/tables");
    }
  }, [routeTableId, navigate]);

  /* ---------- UI ---------- */
  return (
    <Shell>
      <ToastContainer />
      {/* desktop layout */}
      <DeskLayout $showSummary={showSummary} $layout={categoryLayout}>
        {categoryLayout === "horizontal" ? (
          <LeftRail>
            <RailTitle>Categories</RailTitle>
            <SearchFieldWrap>
              <RailSearch
                data-shortcut-search="order-products"
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <SearchShortcutBadge title="Keyboard shortcut for product search">
                {buttonShortcutLabels.search}
              </SearchShortcutBadge>
            </SearchFieldWrap>
            <RailList>
            {categories.map((cat) => (
              <RailBtn
                key={cat.id}
                $active={String(selectedCategory) === String(cat.id)}
                ref={(el) => {
                  if (el) leftCatRefs.current[String(cat.id)] = el;
                }}
                onClick={() => {
                  setSelectedCategory(cat.id);
                  scrollCategoryIntoView(cat.id);
                }}
                title={cat.name}
              >
                {cat.name}
              </RailBtn>
            ))}
            </RailList>
          </LeftRail>
        ) : null}

        <CenterPane>
          <TableTopRow>
            <TableBadge>
              {orderLocationPrefix}: {orderLocationLabel}
            </TableBadge>
            <TableTopControls>
              <InlineSelectGroup>
                <Select
                  ref={waiterSelectRef}
                  value={selectedWaiter}
                  onChange={(e) => setSelectedWaiter(e.target.value)}
                >
                  <option value=""> Waiter</option>
                  {waiters.map((w) => (
                    <option key={w.id} value={w.id}>
                      {waiterLabel(w)}
                    </option>
                  ))}
                </Select>
                <MiniGhostBtn
                  type="button"
                  title="Quick add waiter"
                  onClick={() => setShowAddWaiterModal(true)}
                >
                  + Waiter
                </MiniGhostBtn>
              </InlineSelectGroup>

              <Select
                ref={paymentSelectRef}
                value={paymentMode}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "Credit" && !selectedParty) {
                    setPendingPaymentMode("Credit");
                    toast.info("Select a customer before using Credit.");
                    setCustomerModal(true);
                    return;
                  }
                  if (val === "Split") {
                    splitApplyAfterCustomerRef.current = false;
                    setPendingPaymentMode("");
                    openSplitPaymentModal({ reset: true });
                    return;
                  }
                  setPendingPaymentMode("");
                  setPaymentMode(val);
                }}
                style={{ minWidth: 130 }}
              >
                <option value="Cash">Cash</option>
                <option value="UPI">UPI</option>
                <option value="Card">Card</option>
                <option value="Credit">Credit</option>
                <option value="Split">Split</option>
              </Select>
              {paymentMode === "Split" ? (
                <Button
                  onClick={() => {
                    splitApplyAfterCustomerRef.current = false;
                    openSplitPaymentModal({ reset: false });
                  }}
                >
                  Edit Split
                </Button>
              ) : null}

              <HoverHintButton onClick={() => setCustomerModal(true)}>
                <span>{selectedParty ? "Customer Selected" : "Customer"}</span>
                <HoverShortcutHint>
                  {buttonShortcutLabels.customer}
                </HoverShortcutHint>
              </HoverHintButton>
              <HoverHintButton onClick={() => setDiscountModal(true)}>
                <span>Discount</span>
                <HoverShortcutHint>
                  {buttonShortcutLabels.discount}
                </HoverShortcutHint>
              </HoverHintButton>
            </TableTopControls>
          </TableTopRow>
          {/* mobile-top categories */}
          <TopBarMobile $force={categoryLayout === "vertical"}>
            <CatScroller>
              {categories.map((c) => (
                <CatPill
                  key={`m-${c.id}`}
                  $active={String(selectedCategory) === String(c.id)}
                  ref={(el) => {
                    if (el) topCatRefs.current[String(c.id)] = el;
                  }}
                  onClick={() => {
                    setSelectedCategory(c.id);
                    scrollCategoryIntoView(c.id);
                  }}
                >
                  {c.name}
                </CatPill>
              ))}
            </CatScroller>

            <SearchFieldWrap>
              <MobileSearch
                data-shortcut-search="order-products"
                placeholder="Search products…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <SearchShortcutBadge title="Keyboard shortcut for product search">
                {buttonShortcutLabels.search}
              </SearchShortcutBadge>
            </SearchFieldWrap>
          </TopBarMobile>

          {/* product grid */}
          <Grid>
            {products.length === 0 ? (
              <EmptyProductsCard>
                <EmptyProductsTitle>No products added yet</EmptyProductsTitle>
                <EmptyProductsText>
                  Is restaurant me abhi koi product nahi hai. Pehle products add karo,
                  phir yahan order banana start kar sakte ho.
                </EmptyProductsText>
                <EmptyProductsActions>
                  <Button type="button" onClick={() => navigate("/add-product")}>
                    Add Products
                  </Button>
                </EmptyProductsActions>
              </EmptyProductsCard>
            ) : filteredProducts.length === 0 ? (
              <EmptyProductsCard>
                <EmptyProductsTitle>No matching products</EmptyProductsTitle>
                <EmptyProductsText>
                  Search ya selected category ke hisaab se koi product nahi mila.
                </EmptyProductsText>
              </EmptyProductsCard>
            ) : (
              filteredProducts.map((p) => {
                const qty = getProductQty(p.id);
                const img = buildImageUrl(p);
                const showImg = !!img;
                const initial =
                  (p?.name || "").trim().charAt(0).toUpperCase() || "P";
                const displayPrice = priceOf(p);
                const showOptions = hasProductOptions(p.id);
                return (
                  <Card
                    key={p.id}
                    $selected={qty > 0}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleAddOrCustomize(p)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleAddOrCustomize(p);
                      }
                    }}
                  >
                    <Thumb>
                      {showImg ? (
                        // eslint-disable-next-line jsx-a11y/img-redundant-alt
                        <img
                          src={img}
                          alt={`${p.name} image`}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            e.currentTarget.nextSibling.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <FallbackAvatar aria-hidden $visible={!showImg}>
                        {initial}
                      </FallbackAvatar>
                    </Thumb>
                    {showOptions ? <OptionBadge>Options</OptionBadge> : null}
                    <h4 title={p.name}>{p.name}</h4>
                    <Price>₹ {displayPrice.toFixed(2)}</Price>
                    <QtyBox>
                      <SmallBtn
                        $variant="minus"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemove(p.id);
                        }}
                      >
                        -
                      </SmallBtn>
                      <QtyInput
                        type="number"
                        min="0"
                        value={
                          qtyOverrides[p.id] !== undefined
                            ? qtyOverrides[p.id]
                            : qty
                        }
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          const raw = e.target.value.replace(/[^\d]/g, "");
                          if (raw === "") {
                            setQtyOverrides((prev) => ({ ...prev, [p.id]: "" }));
                            return;
                          }
                          const nextVal = Math.max(0, Number(raw));
                          setQtyOverrides((prev) => ({
                            ...prev,
                            [p.id]: String(nextVal),
                          }));
                          const current = getProductQty(p.id);
                          if (nextVal > current) {
                            for (let i = current; i < nextVal; i += 1) {
                              handleAddOrCustomize(p);
                            }
                          } else if (nextVal < current) {
                            for (let i = current; i > nextVal; i -= 1) {
                              handleRemove(p.id);
                            }
                          }
                        }}
                        onBlur={() => {
                          setQtyOverrides((prev) => {
                            if (prev[p.id] === "") {
                              const copy = { ...prev };
                              delete copy[p.id];
                              return copy;
                            }
                            return prev;
                          });
                        }}
                      />
                      <SmallBtn
                        $variant="plus"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddOrCustomize(p);
                        }}
                      >
                        +
                      </SmallBtn>
                    </QtyBox>
                    {qty > 0 && (
                      <ClearLine
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearItem(p.id);
                        }}
                      >
                        ✕
                      </ClearLine>
                    )}
                  </Card>
                );
              })
            )}
          </Grid>
        </CenterPane>

        {showSummary ? (
          <RightSummary>
            <Sticky>
              <SummaryHead>
                <h3>Order Summary</h3>
                <ToggleSummary onClick={() => setShowSummary(false)}>
                  Hide
                </ToggleSummary>
              </SummaryHead>
              <SummaryBody>
            <KV>
              <span>Items</span>
              <b>
                {cartLines.reduce(
                  (sum, line) => sum + (Number(line?.quantity || 0) || 0),
                  0
                ) || 0}
              </b>
            </KV>
            <Divider />
            {itemSummaryRows.length ? (
              <ItemTable>
                <ItemHeader>
                  <span>Item</span>
                  <span>Qty</span>
                  <span>Rate</span>
                  <span>Total</span>
                </ItemHeader>
                {summaryRowsToShow.map((row) => (
                  <ItemRow key={row.key}>
                    <ItemName title={row.name}>{row.name}</ItemName>
                    <span>{row.qty}</span>
                    <span>₹ {row.rate.toFixed(2)}</span>
                    <ItemTotal>₹ {row.total.toFixed(2)}</ItemTotal>
                  </ItemRow>
                ))}
              </ItemTable>
            ) : null}
            {itemSummaryRows.length > 10 && (
              <SeeMoreBtn
                type="button"
                onClick={() => setShowAllSummaryItems((prev) => !prev)}
              >
                {showAllSummaryItems
                  ? "Show less"
                  : `See more (${itemSummaryRows.length - 10})`}
              </SeeMoreBtn>
            )}
            <KV>
              <span>Amount</span>
              <b>₹ {subtotal.toFixed(2)}</b>
            </KV>
            {discountAmt > 0 ? (
              <KV $muted>
                <span>
                  Discount (
                  {appliedDiscount.mode === "amount"
                    ? `₹${Number(appliedDiscount.value || 0).toFixed(2)}`
                    : `${Number(appliedDiscount.value || 0)}%`}
                  )
                </span>
                <b>- ₹ {discountAmt.toFixed(2)}</b>
              </KV>
            ) : null}
            {deliveryEnabledSetting ? (
              <DeliveryRow>
                <DeliveryToggle>
                  <input
                    type="checkbox"
                    checked={deliverySelected}
                    onChange={(e) => handleDeliveryToggle(e.target.checked)}
                  />
                  <span>Delivery</span>
                </DeliveryToggle>
                <DeliveryInputWrap $disabled={!deliverySelected}>
                  <span>Rs</span>
                  <input
                    type="text"
                    placeholder="0"
                    value={deliveryChargeInput}
                    onChange={(e) => handleDeliveryChargeChange(e.target.value)}
                    onBlur={handleDeliveryChargeBlur}
                    disabled={!deliverySelected}
                    inputMode="decimal"
                    pattern="[0-9.]*"
                    maxLength={8}
                  />
                </DeliveryInputWrap>
              </DeliveryRow>
            ) : null}
            {deliveryChargeValue > 0 ? (
              <KV>
                <span>Delivery</span>
                <b>₹ {deliveryChargeValue.toFixed(2)}</b>
              </KV>
            ) : null}
            <Grand>
              <span>Total</span>
              <strong>₹ {grandTotal.toFixed(2)}</strong>
            </Grand>
            {showTaxBreakupOnBillSetting && hasTaxBreakup ? (
              <TaxBlock>
                <TaxTitle>Tax Breakup</TaxTitle>
                <TaxHeader>
                  <span>Taxable</span>
                  <span>CGST</span>
                  <span>SGST</span>
                  <span>Total</span>
                </TaxHeader>
                {taxBreakupRows.map((row, idx) => (
                  <TaxRow key={row?.key || `tax-${idx}`}>
                    <span>
                      ₹{" "}
                      {(
                        Number(
                          row?.taxableamount ??
                            row?.taxable ??
                            row?.taxableAmount ??
                            0
                        ) || 0
                      ).toFixed(2)}
                    </span>
                    <span>₹ {(Number(row?.cgst || 0) || 0).toFixed(2)}</span>
                    <span>₹ {(Number(row?.sgst || 0) || 0).toFixed(2)}</span>
                    <span>₹ {(Number(row?.total ?? 0) || 0).toFixed(2)}</span>
                  </TaxRow>
                ))}
                <TaxTotal>
                  <span>₹ {taxBreakupTotals.taxable.toFixed(2)}</span>
                  <span>₹ {taxBreakupTotals.cgst.toFixed(2)}</span>
                  <span>₹ {taxBreakupTotals.sgst.toFixed(2)}</span>
                  <span>₹ {taxBreakupTotals.total.toFixed(2)}</span>
                </TaxTotal>
              </TaxBlock>
              ) : null}
            <KV>
              <span>Payment</span>
              <b>{paymentMode}</b>
            </KV>
            {paymentMode === "Split" && splitPreview.total > 0 ? (
              <SplitSummary>
                <span>Split Total</span>
                <strong>₹ {splitPreview.total.toFixed(2)}</strong>
              </SplitSummary>
            ) : null}
              </SummaryBody>
<ActionCol>
              {/* <ActionBtn
                $tone="green"
                disabled={!hasItems || syncing}
                onClick={() => syncOrder()}
              >
                <FaSave />
                {syncing ? "Saving…" : "Quick Save"}
              </ActionBtn> */}
              {/* <ActionBtn
                $tone="green"
                disabled={!hasItems || creatingOrder}
                onClick={handlePrintBill}
              >
                <FaBolt />
                {creatingOrder ? "Saving…" : "Quick Bill"}
              </ActionBtn> */}
              <ActionBtn
                disabled={!hasItems || creatingOrder}
                onClick={handleSaveOrder}
              >
                <FaSave />
                <span>{creatingOrder ? "Saving…" : "Save Order"}</span>
                {!creatingOrder ? (
                  <ShortcutHint $inverse>{buttonShortcutLabels.save}</ShortcutHint>
                ) : null}
              </ActionBtn>
              <ActionBtn
                disabled={blePrinting}
                onClick={handleBlePrint}
                title="Connect via Web Bluetooth and send a quick test print"
              >
                <FaBluetoothB />
                <span>{blePrinting ? "Connecting…" : "Print Bill"}</span>
                {!blePrinting ? (
                  <ShortcutHint $inverse>{buttonShortcutLabels.print}</ShortcutHint>
                ) : null}
              </ActionBtn>
              <ActionBtn
                disabled={!hasItems || usbPrinting}
                onClick={handleUsbPrint}
                title="Connect via USB thermal printer and print bill"
              >
                <FaUsb />
                <span>{usbPrinting ? "Connecting…" : "USB Print"}</span>
                {!usbPrinting ? (
                  <ShortcutHint $inverse>{buttonShortcutLabels.usbPrint}</ShortcutHint>
                ) : null}
              </ActionBtn>
              <ActionBtn
                disabled={!hasItems || sendingKOT}
                onClick={openKOTModal}
              >
                <FaConciergeBell />
                <span>{sendingKOT ? "Sending…" : "Send to Kitchen"}</span>
                {!sendingKOT ? (
                  <ShortcutHint $inverse>{buttonShortcutLabels.kot}</ShortcutHint>
                ) : null}
              </ActionBtn>
              <DangerBtn disabled={!hasItems} onClick={clearEntireOrder}>
                <FaTrash />
                Clear Cart
              </DangerBtn>
              {syncing ? (
                <p style={{ margin: 0, color: "#6b7280", fontSize: 12 }}>
                  Saving…
                </p>
              ) : null}
              {bleStatus ? (
                <p style={{ margin: 0, color: "#6b7280", fontSize: 12 }}>
                  {bleStatus}
                </p>
              ) : null}
              {usbStatus ? (
                <p style={{ margin: 0, color: "#6b7280", fontSize: 12 }}>
                  {usbStatus}
                </p>
              ) : null}
            </ActionCol>
            {/* <SummaryFooter>
            
            </SummaryFooter> */}
          </Sticky>
          </RightSummary>
        ) : (
          <SummaryFloating onClick={() => setShowSummary(true)}>
            Show Summary
          </SummaryFloating>
        )}
      </DeskLayout>

      {postBillModal ? (
        <PostBillOverlay>
          <PostBillCard>
            <PostBillHead>
              <div>
                <h3>Bill #{postBillModal.orderId}</h3>
                <p>
                  {postBillModal.tablePrefix || "Table"} {postBillModal.table} • Payment: {postBillModal.paymentMode}
                </p>
              </div>
              <PostBillClose type="button" onClick={() => setPostBillModal(null)}>
                ×
              </PostBillClose>
            </PostBillHead>
            <PostBillMetaGrid>
              {postBillModal.customerName ? (
                <div>
                  <small>Customer</small>
                  <strong>{postBillModal.customerName}</strong>
                </div>
              ) : null}
              {postBillModal.customerMobile ? (
                <div>
                  <small>Mobile</small>
                  <strong>{postBillModal.customerMobile}</strong>
                </div>
              ) : null}
              {postBillModal.waiterName ? (
                <div>
                  <small>Waiter</small>
                  <strong>{postBillModal.waiterName}</strong>
                </div>
              ) : null}
              {postBillModal.createdAt ? (
                <div>
                  <small>Created</small>
                  <strong>{new Date(postBillModal.createdAt).toLocaleString("en-IN")}</strong>
                </div>
              ) : null}
            </PostBillMetaGrid>
            <PostBillBody>
              {postBillModal.items.map((item, idx) => (
                <PostBillRow key={`${item.name}-${idx}`}>
                  <span>{item.name}</span>
                  <span>x{Number(item.qty || 0)}</span>
                  <span>₹ {(Number(item.qty || 0) * Number(item.rate || 0)).toFixed(2)}</span>
                </PostBillRow>
              ))}
            </PostBillBody>
            <PostBillTotals>
              <div>Subtotal: ₹ {Number(postBillModal.subtotal || 0).toFixed(2)}</div>
              {Number(postBillModal.discount || 0) > 0 ? (
                <div>Discount: ₹ {Number(postBillModal.discount || 0).toFixed(2)}</div>
              ) : null}
              {Number(postBillModal.delivery || 0) > 0 ? (
                <div>Delivery: ₹ {Number(postBillModal.delivery || 0).toFixed(2)}</div>
              ) : null}
              <strong>Total: ₹ {Number(postBillModal.total || 0).toFixed(2)}</strong>
            </PostBillTotals>
            {postBillModal.qrValue ? (
              <PostBillQrWrap>
                <img
                  alt={`Bill ${postBillModal.orderId} QR`}
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=130x130&data=${encodeURIComponent(
                    postBillModal.qrValue
                  )}`}
                />
              </PostBillQrWrap>
            ) : null}
            <PostBillActions>
              <ActionBtn type="button" onClick={handlePostBillPrint}>
                Bill Print
              </ActionBtn>
              <ActionBtn type="button" onClick={handlePostBillDeliveryCopy}>
                Delivery Copy
              </ActionBtn>
              <ActionBtn type="button" onClick={handlePostBillNewOrder}>
                New Order
              </ActionBtn>
              <ActionBtn type="button" onClick={handlePostBillGoBack}>
                Go Back
              </ActionBtn>
              <ActionBtn type="button" onClick={handlePostBillKotPrint}>
                KOT Print
              </ActionBtn>
            </PostBillActions>
          </PostBillCard>
        </PostBillOverlay>
      ) : null}

      {/* Discount Modal */}
      {discountModal && (
        <Modal>
          <ModalCard>
            <h3>Apply Discount</h3>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <button
                onClick={() => setDiscountMode("percent")}
                style={{
                  ...tabBtnStyles,
                  background:
                    discountMode === "percent" ? "rgba(1,91,154,0.08)" : "#fff",
                  border:
                    discountMode === "percent"
                      ? "1px solid #015b9a"
                      : "1px solid #e5e7eb",
                }}
              >
                Percent %
              </button>
              <button
                onClick={() => setDiscountMode("amount")}
                style={{
                  ...tabBtnStyles,
                  background:
                    discountMode === "amount" ? "rgba(1,91,154,0.08)" : "#fff",
                  border:
                    discountMode === "amount"
                      ? "1px solid #015b9a"
                      : "1px solid #e5e7eb",
                }}
              >
                Amount ₹
              </button>
            </div>

            {discountMode === "percent" ? (
              <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                {[10, 20, 30].map((pct) => (
                  <button
                    key={pct}
                    onClick={() => setDiscountValue(pct)}
                    style={{
                      ...chipBtnStyles,
                      background:
                        Number(discountValue) === pct
                          ? "rgba(1,91,154,0.12)"
                          : "#f8fafc",
                      border:
                        Number(discountValue) === pct
                          ? "1px solid #015b9a"
                          : "1px solid #e5e7eb",
                    }}
                  >
                    {pct}%
                  </button>
                ))}
              </div>
            ) : null}

            <input
              ref={discountInputRef}
              type="number"
              min="0"
              max={discountMode === "percent" ? 100 : undefined}
              placeholder={discountMode === "percent" ? "Enter % (0-100)" : "Enter amount"}
              value={discountValue}
              onChange={(e) =>
                setDiscountValue(
                  discountMode === "percent"
                    ? Math.max(0, Math.min(100, Number(e.target.value || 0)))
                    : Math.max(0, Number(e.target.value || 0))
                )
              }
              style={{ marginBottom: 10 }}
            />

            <ModalRow>
              <Button
                onClick={() => {
                  setAppliedDiscount({
                    mode: discountMode,
                    value: Number(discountValue) || 0,
                  });
                  setDiscountModal(false);
                }}
              >
                Apply
              </Button>
              <GhostBtn
                onClick={() => {
                  setDiscountValue(0);
                  setAppliedDiscount({ mode: "percent", value: 0 });
                }}
              >
                Clear
              </GhostBtn>
              <GhostBtn onClick={() => setDiscountModal(false)}>Close</GhostBtn>
            </ModalRow>
          </ModalCard>
        </Modal>
      )}

      {/* Customer Modal */}
      {customerModal && (
        <Modal>
          <ModalCard>
            <h3>Choose Existing or Add New</h3>
            <p style={{ marginTop: 0, color: "#6b7280" }}>
              Pick a saved customer or create a new one for this bill.
            </p>

            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <button
                onClick={() => setCustomerTab("existing")}
                style={{
                  ...tabBtnStyles,
                  background:
                    customerTab === "existing" ? "rgba(1,91,154,0.08)" : "#fff",
                  border:
                    customerTab === "existing"
                      ? "1px solid #015b9a"
                      : "1px solid #e5e7eb",
                }}
              >
                Existing
              </button>
              <button
                onClick={() => setCustomerTab("add")}
                style={{
                  ...tabBtnStyles,
                  background:
                    customerTab === "add" ? "rgba(1,91,154,0.08)" : "#fff",
                  border:
                    customerTab === "add"
                      ? "1px solid #015b9a"
                      : "1px solid #e5e7eb",
                }}
              >
                Add New
              </button>
            </div>

            {customerTab === "existing" ? (
              <>
                <input
                  ref={customerSearchRef}
                  placeholder="Search name, company or mobile"
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                />

                <div
                  style={{
                    maxHeight: 180,
                    overflowY: "auto",
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    padding: 8,
                    marginBottom: 12,
                  }}
                >
                  {(partyList || [])
                    .filter((p) => {
                      const q = customerSearch.trim().toLowerCase();
                      if (!q) return true;
                      const phone = String(p?.partycontactpersonmobile || "");
                      const company = partyCompanyOf(p).toLowerCase();
                      return (
                        (p?.partyname || "").toLowerCase().includes(q) ||
                        company.includes(q) ||
                        phone.includes(q)
                      );
                    })
                    .slice(0, 15)
                    .map((p) => {
                      const phone = String(p?.partycontactpersonmobile || "");
                      const company = partyCompanyOf(p);
                      const active = String(selectedParty) === String(p.id);
                      return (
                        <button
                          key={p.id}
                          onClick={() => {
                            setSelectedParty(String(p.id));
                            setCustomerName(p?.partyname || "");
                            setCustomerCompany(company);
                            setMobile(phone.replace(/\D/g, "").slice(0, 10));
                            if (pendingPaymentMode === "Credit") {
                              setPaymentMode("Credit");
                              setPendingPaymentMode("");
                              setCustomerModal(false);
                              toast.success("Customer selected. Credit applied.");
                            } else if (splitApplyAfterCustomerRef.current) {
                              splitApplyAfterCustomerRef.current = false;
                              setCustomerModal(false);
                              openSplitPaymentModal({ reset: false });
                              toast.success("Customer selected. Press Apply to confirm split payment.");
                            }
                          }}
                          style={{
                            width: "100%",
                            textAlign: "left",
                            padding: "10px 12px",
                            borderRadius: 10,
                            border:
                              "1px solid " + (active ? "#015b9a" : "#e5e7eb"),
                            background: active ? "rgba(1,91,154,0.08)" : "#fff",
                            marginBottom: 8,
                            cursor: "pointer",
                          }}
                        >
                          <strong>{p?.partyname || "Unnamed"}</strong>
                          {company ? (
                            <div style={{ color: "#374151", fontSize: 12 }}>
                              {company}
                            </div>
                          ) : null}
                          <div style={{ color: "#6b7280", fontSize: 12 }}>
                            {phone || "No mobile"}
                          </div>
                        </button>
                      );
                    })}
                </div>
              </>
            ) : (
              <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
                <input
                  ref={customerNameRef}
                  placeholder="Customer name"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                />
                <input
                  placeholder="Company name (optional)"
                  value={customerCompany}
                  onChange={(e) => setCustomerCompany(e.target.value)}
                />
                <input
                  placeholder="10-digit mobile"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={10}
                />
              </div>
            )}

            <ModalRow style={{ marginTop: 10 }}>
              {customerTab === "existing" ? (
                <Button
                  onClick={() => {
                    if (!selectedParty) {
                      toast.info("Select an existing customer first.");
                      return;
                    }
                    setCustomerModal(false);
                    if (pendingPaymentMode === "Credit") {
                      setPaymentMode("Credit");
                      setPendingPaymentMode("");
                    } else if (splitApplyAfterCustomerRef.current) {
                      splitApplyAfterCustomerRef.current = false;
                      openSplitPaymentModal({ reset: false });
                    }
                    toast.success("Customer selected.");
                  }}
                  disabled={!selectedParty}
                >
                  Use Selected
                </Button>
              ) : (
                <Button
                  onClick={async () => {
                    if (!customerName.trim()) {
                      toast.error("Enter customer name");
                      return;
                    }
                    const clean = mobile.trim();
                    if (!/^\d{10}$/.test(clean)) {
                      toast.error("Enter valid 10-digit mobile");
                      return;
                    }
                    setSavingCustomer(true);
                    try {
                      const company = customerCompany.trim();
                      const body = {
                        restaurantid: restaurantId,
                        partyname: customerName.trim(),
                        companyname: company,
                        company_name: company,
                        partycompanyname: company,
                        partycompany: company,
                        company,
                        type: "Customer",
                        partycontactpersonmobile: clean,
                      };
                      const res = await fetch(
                        "https://qr.billsarthi.in/public/api/create/party",
                        {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify(body),
                        }
                      );
                      const result = await res.json().catch(() => ({}));
                      if (!res.ok || !result?.success) {
                        throw new Error(
                          result?.message || "Failed to save customer"
                        );
                      }

                      const refreshedParties = await fetchPartyList();
                      const norm = (s) =>
                        (s || "").toString().trim().toLowerCase();
                      const digits = (s) => (s || "").replace(/\D/g, "");
                      const match =
                        refreshedParties.find(
                          (p) => digits(p?.partycontactpersonmobile) === clean
                        ) ||
                        refreshedParties.find(
                          (p) => norm(p?.partyname) === norm(customerName)
                        );

                      if (match?.id) {
                        setSelectedParty(String(match.id));
                        toast.success("Customer saved & selected");
                      } else {
                        splitApplyAfterCustomerRef.current = false;
                        toast.success(result?.message || "Customer saved");
                      }
                      setCustomerName("");
                      setCustomerCompany("");
                      setMobile("");
                      setCustomerSearch("");
                      setCustomerTab("existing");
                      if (pendingPaymentMode === "Credit") {
                        setPaymentMode("Credit");
                        setPendingPaymentMode("");
                      } else if (splitApplyAfterCustomerRef.current && match?.id) {
                        splitApplyAfterCustomerRef.current = false;
                        openSplitPaymentModal({ reset: false });
                        toast.success("Customer selected. Press Apply to confirm split payment.");
                      }
                      setCustomerModal(false);
                    } catch (err) {
                      toast.error(err?.message || "Failed to save customer");
                    } finally {
                      setSavingCustomer(false);
                    }
                  }}
                  disabled={savingCustomer}
                >
                  {savingCustomer ? "Saving…" : "Save New"}
                </Button>
              )}
              <GhostBtn
                onClick={() => {
                  splitApplyAfterCustomerRef.current = false;
                  setCustomerModal(false);
                  setPendingPaymentMode("");
                }}
              >
                Close
              </GhostBtn>
            </ModalRow>
          </ModalCard>
        </Modal>
      )}

      {showAddWaiterModal && (
        <Modal>
          <ModalCard>
            <h3>Add Waiter</h3>
            <p style={{ marginTop: 0, color: "#6b7280" }}>
              Create a waiter without leaving the order screen.
            </p>
            <input
              placeholder="Waiter name"
              value={newWaiterName}
              onChange={(e) => setNewWaiterName(e.target.value)}
            />
            <input
              placeholder="10-digit phone"
              value={newWaiterPhone}
              inputMode="numeric"
              maxLength={10}
              onChange={(e) =>
                setNewWaiterPhone(e.target.value.replace(/\D/g, "").slice(0, 10))
              }
            />
            <input
              placeholder="Email (optional)"
              value={newWaiterEmail}
              onChange={(e) => setNewWaiterEmail(e.target.value)}
            />
            <input
              type="password"
              placeholder="Password"
              value={newWaiterPassword}
              onChange={(e) => setNewWaiterPassword(e.target.value)}
            />
            <ModalRow>
              <Button
                type="button"
                onClick={handleQuickAddWaiter}
                disabled={savingWaiter}
              >
                {savingWaiter ? "Saving..." : "Save Waiter"}
              </Button>
              <GhostBtn
                type="button"
                onClick={() => {
                  if (savingWaiter) return;
                  setShowAddWaiterModal(false);
                  resetQuickWaiterForm();
                }}
              >
                Close
              </GhostBtn>
            </ModalRow>
          </ModalCard>
        </Modal>
      )}

      {/* Split Payment Modal */}
      {showSplitPaymentModal && (
        <Modal>
          <ModalCard>
            <h3>Split Payment</h3>
            <SplitRow>
              <SplitLabel>Cash</SplitLabel>
              <SplitInput
                ref={splitCashInputRef}
                value={splitCashInput}
                onChange={(e) => {
                  const billTotal = Number(Number(payableTotal || 0).toFixed(2));
                  const upi = parseMoney(splitUpiInput);
                  const card = parseMoney(splitCardInput);
                  const credit = parseMoney(splitCreditInput);
                  const next = normalizeMoneyInput(e.target.value);
                  const clamped = clampSplitInput(next, upi + card + credit, billTotal);
                  setSplitCashInput(clamped);
                }}
                placeholder="0"
                inputMode="decimal"
              />
            </SplitRow>
            <SplitRow>
              <SplitLabel>UPI</SplitLabel>
              <SplitInput
                value={splitUpiInput}
                onChange={(e) => {
                  const billTotal = Number(Number(payableTotal || 0).toFixed(2));
                  const cash = parseMoney(splitCashInput);
                  const card = parseMoney(splitCardInput);
                  const credit = parseMoney(splitCreditInput);
                  const next = normalizeMoneyInput(e.target.value);
                  const clamped = clampSplitInput(next, cash + card + credit, billTotal);
                  setSplitUpiInput(clamped);
                }}
                placeholder="0"
                inputMode="decimal"
              />
            </SplitRow>
            <SplitRow>
              <SplitLabel>Card</SplitLabel>
              <SplitInput
                value={splitCardInput}
                onChange={(e) => {
                  const billTotal = Number(Number(payableTotal || 0).toFixed(2));
                  const cash = parseMoney(splitCashInput);
                  const upi = parseMoney(splitUpiInput);
                  const credit = parseMoney(splitCreditInput);
                  const next = normalizeMoneyInput(e.target.value);
                  const clamped = clampSplitInput(next, cash + upi + credit, billTotal);
                  setSplitCardInput(clamped);
                }}
                placeholder="0"
                inputMode="decimal"
              />
            </SplitRow>
            <SplitRow>
              <SplitLabel>Credit</SplitLabel>
              <SplitInput
                value={splitCreditInput}
                onChange={(e) => {
                  const billTotal = Number(Number(payableTotal || 0).toFixed(2));
                  const cash = parseMoney(splitCashInput);
                  const upi = parseMoney(splitUpiInput);
                  const card = parseMoney(splitCardInput);
                  const next = normalizeMoneyInput(e.target.value);
                  const clamped = clampSplitInput(next, cash + upi + card, billTotal);
                  setSplitCreditInput(clamped);
                  splitCreditAutoRef.current = clamped === "";
                }}
                placeholder="0"
                inputMode="decimal"
              />
            </SplitRow>

            <SplitMetaRow>
              <span>Remaining: ₹ {splitRemaining.toFixed(2)}</span>
              <span>Split: ₹ {splitPreview.total.toFixed(2)}</span>
              <span>Bill: ₹ {splitBillTotal.toFixed(2)}</span>
            </SplitMetaRow>

            {splitPreview.credit > 0 && !selectedParty ? (
              <SplitHint onClick={() => {
                splitApplyAfterCustomerRef.current = true;
                setShowSplitPaymentModal(false);
                setCustomerModal(true);
              }}>
                Select customer for credit
              </SplitHint>
            ) : null}

            <ModalRow>
              <Button onClick={applySplitPayment} disabled={!splitCanApply}>
                Apply
              </Button>
              <GhostBtn
                onClick={() => {
                  setShowSplitPaymentModal(false);
                }}
              >
                Cancel
              </GhostBtn>
            </ModalRow>
          </ModalCard>
        </Modal>
      )}

      {/* Options Modal */}
      {optionsOpen && (
        <Modal>
          <OptionsCard>
            <h3>{optionsProduct?.name || "Customize"}</h3>
            <p style={{ color: "#6b7280", marginTop: 0 }}>
              Choose a variant and add-ons.
            </p>

            {optionsVariants.length > 1 ? (
              <OptionGroup>
                <OptionTitle>Variants</OptionTitle>
                <OptionGrid>
                  {optionsVariants.map((variant) => {
                    const active =
                      String(variant?.id) ===
                      String(optionsSelectedVariantId);
                    const price = Number(
                      variant?.priceaftertax ?? variant?.price ?? 0
                    );
                    return (
                      <OptionBtn
                        key={`variant-${variant?.id ?? "base"}`}
                        $active={active}
                        onClick={() => setOptionsSelectedVariantId(variant?.id ?? null)}
                        type="button"
                      >
                        <div>{variant?.label || "Default"}</div>
                        <small>₹ {price.toFixed(2)}</small>
                      </OptionBtn>
                    );
                  })}
                </OptionGrid>
              </OptionGroup>
            ) : null}

            {optionsAddons.length > 0 ? (
              <OptionGroup>
                <OptionTitle>Add-ons</OptionTitle>
                <AddonList>
                  {optionsAddons.map((addon) => {
                    const checked = optionsSelectedAddonIds.includes(addon.id);
                    const price = Number(
                      addon?.priceaftertax ?? addon?.price ?? 0
                    );
                    return (
                      <AddonRow key={`addon-${addon.id}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleOptionAddon(addon.id)}
                        />
                        <span>{addon?.name}</span>
                        <small>₹ {price.toFixed(2)}</small>
                      </AddonRow>
                    );
                  })}
                </AddonList>
              </OptionGroup>
            ) : null}

            {optionsPreview ? (
              <OptionSummary>
                <div>
                  Unit: <strong>₹ {optionsPreview.unit.toFixed(2)}</strong>
                </div>
                <div>
                  Payable:{" "}
                  <strong>₹ {optionsPreview.payable.toFixed(2)}</strong>
                </div>
              </OptionSummary>
            ) : null}

            <ModalRow>
              <Button
                onClick={confirmOptionsAdd}
                disabled={optionsLoading}
                style={{ minWidth: 140 }}
              >
                {optionsLoading ? "Loading…" : "Add to Order"}
              </Button>
              <GhostBtn onClick={closeOptionsModal} disabled={optionsLoading}>
                Close
              </GhostBtn>
            </ModalRow>
          </OptionsCard>
        </Modal>
      )}

      {/* KOT Modal */}
      {kotModalOpen && (
        <Modal>
          <KotCard>
            <KotHeader>
              <div>
                <h3>Send to Kitchen (KOT)</h3>
                <KotSub>
                  Selected {kotSelectedItems.length}/{kotPreviewItems.length}
                </KotSub>
              </div>
              <KotHeaderBadge>{orderLocationPrefix} {orderLocationLabel}</KotHeaderBadge>
            </KotHeader>
            <KotToolbar>
              <span>Only unsent items are listed below.</span>
              <div>
                <KotMiniBtn
                  type="button"
                  onClick={selectAllKotItems}
                  disabled={allKotSelected}
                >
                  Select all
                </KotMiniBtn>
                <KotMiniBtn type="button" onClick={clearKotItems}>
                  Clear
                </KotMiniBtn>
              </div>
            </KotToolbar>

            <KotList>
              {kotPreviewItems.map((item) => {
                const checked = kotSelectedMap[String(item.key)] !== false;
                return (
                  <KotRow key={item.key} $inactive={!checked}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleKotItem(item.key)}
                    />
                    <div>
                      <div className="name">{item.name}</div>
                      <div className="meta">Qty: {item.qty}</div>
                    </div>
                    <div className="price">
                      ₹ {(item.price || 0).toFixed(2)}
                    </div>
                  </KotRow>
                );
              })}
            </KotList>

            <KotNotesWrap>
              <label>Notes for kitchen (optional)</label>
              <textarea
                ref={kotNotesRef}
                value={kotNotes}
                onChange={(e) => setKotNotes(e.target.value)}
                rows={3}
                placeholder="Add special instructions…"
              />
              {kotQuickTags.length > 0 ? (
                <KotTagRow>
                  {kotQuickTags.map((tag) => (
                    <KotTagBtn
                      key={tag}
                      type="button"
                      $active={kotActiveTags.includes(tag)}
                      onClick={() => toggleKotTag(tag)}
                    >
                      {tag}
                    </KotTagBtn>
                  ))}
                </KotTagRow>
              ) : null}
            </KotNotesWrap>

            {kotAlreadySent.length > 0 ? (
              <KotSentBox>
                <strong>Already sent</strong>
                <div>
                  {kotAlreadySent.map((d) => (
                    <span key={d.key}>
                      {d.name ||
                        productNameById.get(parseProductIdFromKey(d.key)) ||
                        d.key}{" "}
                      • {d.qty} sent
                    </span>
                  ))}
                </div>
              </KotSentBox>
            ) : null}

            <ModalRow style={{ marginTop: 10, flexWrap: "wrap" }}>
              <Button
                onClick={handleSendKOT}
                disabled={sendingKOT}
                style={{ minWidth: 140 }}
              >
                {sendingKOT ? "Sending…" : "Send"}
              </Button>
              <Button
                onClick={handleSendAndPrintKOT}
                disabled={sendingKOT}
                style={{ minWidth: 160 }}
              >
                {sendingKOT ? "Sending…" : "Send & Print"}
              </Button>
              <GhostBtn
                onClick={() => setKotModalOpen(false)}
                disabled={sendingKOT}
              >
                Close
              </GhostBtn>
            </ModalRow>
          </KotCard>
        </Modal>
      )}
    </Shell>
  );
};

export default OrderPage;

/* ===================== styled ===================== */

const Shell = styled.div`
  --rail-w: 240px; /* ~10–12% typical desktop width */
  --gap: 16px;
  --card-bg: #ffffff;
  --muted: #6b7280;
  --line: #e5e7eb;
  --blue: #015b9a;
  --blue-ghost: rgba(1, 91, 154, 0.08);
  --header-h: 0px;
  background: #f7fafc;
  // min-height: 100vh;
  // overflow: hidden;
`;

const DeskLayout = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: 16px;
  height: calc(100vh - var(--header-h));
  overflow: hidden;
  display: grid;
  grid-template-columns: ${({ $showSummary, $layout }) => {
    if ($layout === "vertical") {
      return $showSummary ? "1fr 320px" : "1fr";
    }
    return $showSummary ? "var(--rail-w) 1fr 320px" : "var(--rail-w) 1fr";
  }};
  gap: var(--gap);

  @media (max-width: 1024px) {
    height: auto;
    overflow: visible;
    grid-template-columns: 1fr;
  }
`;

const LeftRail = styled.aside`
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 12px;
  position: sticky;
  top: 12px;
  height: max-content;

  @media (max-width: 1024px) {
    display: none; /* mobile me upar pills aayengi */
  }
`;
const RailTitle = styled.div`
  font-weight: 700;
  margin-bottom: 8px;
`;
const SearchFieldWrap = styled.div`
  position: relative;
  margin-bottom: 10px;
`;
const RailSearch = styled.input`
  width: 100%;
  padding: 8px 46px 8px 10px;
  border: 1px solid var(--line);
  border-radius: 8px;
  font-size: 14px;
`;
const RailList = styled.div`
  display: grid;
  gap: 8px;
`;
const RailBtn = styled.button`
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid
    ${({ $active }) => ($active ? "var(--blue)" : "var(--line)")};
  background: ${({ $active }) => ($active ? "var(--blue-ghost)" : "#fff")};
  color: ${({ $active }) => ($active ? "var(--blue)" : "#111827")};
  cursor: pointer;
`;

const CenterPane = styled.main`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  padding-right: 4px;
  @media (max-width: 1024px) {
    height: auto;
    overflow: visible;
  }
`;

const TableTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const TableBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 800;
  color: #0f172a;
  padding: 6px 10px;
  border-radius: 10px;
  background: #eef2f7;
  border: 1px solid #e5e7eb;
  width: fit-content;
  flex-shrink: 0;
`;

const TableTopControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex: 1 1 680px;
  flex-wrap: wrap;

  @media (max-width: 1200px) {
    justify-content: flex-start;
  }
`;

const TopBarMobile = styled.div`
  display: ${({ $force }) => ($force ? "grid" : "none")};
  gap: 10px;
  margin-bottom: 10px;
  @media (max-width: 1024px) {
    display: grid;
  }
`;

const CatScroller = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 2px 2px 8px;
  &::-webkit-scrollbar {
    height: 6px;
  }
`;

const CatPill = styled.button`
  flex: 0 0 auto;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid
    ${({ $active }) => ($active ? "var(--blue)" : "var(--line)")};
  background: ${({ $active }) => ($active ? "var(--blue-ghost)" : "#fff")};
  color: ${({ $active }) => ($active ? "var(--blue)" : "#111827")};
  font-weight: 600;
`;

const MobileSearch = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 46px 10px 12px;
  border: 1px solid var(--line);
  border-radius: 10px;
  font-size: 14px;
`;
const SearchShortcutBadge = styled.span`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  padding: 4px 7px;
  border-radius: 999px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0.04em;
  pointer-events: none;
  white-space: nowrap;
`;

const Select = styled.select`
  height: 38px;
  padding: 0 10px;
  border: 1px solid var(--line);
  border-radius: 8px;
  min-width: 170px;
  @media (max-width: 768px) {
    min-width: 0;
    width: 100%;
  }
`;
const InlineSelectGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 768px) {
    width: 100%;
    flex-wrap: wrap;
  }
`;
const Button = styled.button`
  height: 38px;
  padding: 0 14px;
  border-radius: 8px;
  background: var(--blue);
  color: #fff;
  border: none;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
`;
const MiniGhostBtn = styled.button`
  height: 38px;
  padding: 0 12px;
  border-radius: 8px;
  background: #fff;
  border: 1px dashed #94a3b8;
  color: #0f172a;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
`;
const HoverHintButton = styled(Button)`
  position: relative;
  overflow: visible;
`;
const ShortcutHint = styled.span`
  padding: 3px 7px;
  border-radius: 999px;
  border: 1px solid
    ${({ $inverse }) => ($inverse ? "rgba(255,255,255,0.22)" : "#bfdbfe")};
  background: ${({ $inverse }) => ($inverse ? "rgba(255,255,255,0.12)" : "#eff6ff")};
  color: ${({ $inverse }) => ($inverse ? "#f8fafc" : "#1d4ed8")};
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0.04em;
  white-space: nowrap;
  flex-shrink: 0;
`;
const HoverShortcutHint = styled(ShortcutHint)`
  position: absolute;
  top: -8px;
  right: 8px;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  z-index: 2;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.22);
  transform: translateY(-100%) scale(0.96);
  transition: opacity 0.16s ease, transform 0.16s ease, visibility 0.16s ease;

  ${HoverHintButton}:hover &,
  ${HoverHintButton}:focus-visible &,
  ${HoverHintButton}:focus-within & {
    opacity: 1;
    visibility: visible;
    transform: translateY(-100%) scale(1);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
const GhostBtn = styled.button`
  height: 38px;
  padding: 0 14px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid var(--line);
  color: #111827;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;
const DangerBtn = styled(Button)`
  min-height: 56px;
  height: auto;
  border-radius: 14px;
  border: 1px solid #dc2626;
  background: linear-gradient(180deg, #ef4444 0%, #dc2626 100%);
  color: #fff;
  font-size: 15px;
  font-weight: 800;
  width: 100%;
  box-shadow: 0 10px 20px rgba(220, 38, 38, 0.2);
  transition: transform 0.15s ease, box-shadow 0.2s ease, filter 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 14px 24px rgba(220, 38, 38, 0.24);
    filter: saturate(1.04);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const Grid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  grid-auto-rows: max-content;
  align-content: start;
  align-items: start;
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 4px 2px 2px 0;
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const EmptyProductsCard = styled.div`
  grid-column: 1 / -1;
  border: 1px dashed #cbd5e1;
  border-radius: 18px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  padding: 28px 20px;
  text-align: center;
  display: grid;
  gap: 10px;
  justify-items: center;
`;

const EmptyProductsTitle = styled.h3`
  margin: 0;
  color: #0f172a;
  font-size: 20px;
  font-weight: 900;
`;

const EmptyProductsText = styled.p`
  margin: 0;
  max-width: 460px;
  color: #64748b;
  line-height: 1.6;
  font-size: 14px;
`;

const EmptyProductsActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
`;

const Card = styled.div`
  border: 1px solid ${({ $selected }) => ($selected ? "#015b9a" : "var(--line)")};
  border-radius: 12px;
  background: #fff;
  padding: 10px;
  text-align: center;
  cursor: pointer;
  user-select: none;
  transition: box-shadow 0.2s ease, transform 0.1s ease;
  box-shadow: ${({ $selected }) =>
    $selected ? "0 8px 24px rgba(0,0,0,.08)" : "none"};
  transform: none;
  box-shadow: ${({ $selected }) =>
    $selected ? "0 0 0 3px rgba(1,91,154,0.12), 0 8px 24px rgba(0,0,0,.08)" : "none"};
  position: relative;
  &:hover {
    border-color: #0b78c4;
    box-shadow: 0 0 0 3px rgba(11, 120, 196, 0.14),
      0 14px 28px rgba(2, 12, 33, 0.12);
    transform: translateY(-2px);
  }
  &:active {
    transform: translateY(0);
  }

  h4 {
    margin: 8px 0 2px;
    font-size: 14px;
    line-height: 1.2;
    min-height: 34px;
    overflow: hidden;
    cursor: pointer;
  }
`;
const OptionBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: #eef2ff;
  color: #1e3a8a;
  border: 1px solid #c7d2fe;
`;
const Thumb = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 10px;
  overflow: hidden;
  background: #f1f5f9;
  display: grid;
  place-items: center;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;
const FallbackAvatar = styled.div`
  width: 100%;
  height: 100%;
  display: ${({ $visible }) => ($visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 32px;
  color: #334155;
`;

const Price = styled.div`
  color: #111827;
  font-weight: 800;
  margin-bottom: 6px;
  cursor: pointer;
`;

const QtyBox = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;
const SmallBtn = styled.button`
  padding: 6px 10px;
  border-radius: 8px;
 border: 1px solid
    ${({ $variant }) =>
      $variant === "plus"
        ? "#13bb50"
        : $variant === "minus"
        ? "#c81f1f"
        : "var(--line)"};
  background: ${({ $variant }) =>
    $variant === "plus"
      ? "#13bb50"
      : $variant === "minus"
      ? "#c81f1f"
      : "#fff"};
  color: ${({ $variant }) =>
    $variant === "plus"
      ? "#ffffff"
      : $variant === "minus"
      ? "#ffffff"
      : "#111827"};
  font-weight: 800;
  cursor: pointer;
  box-shadow: ${({ $variant }) =>
    $variant === "plus"
      ? "0 6px 14px rgba(34,197,94,0.25)"
      : $variant === "minus"
      ? "0 6px 14px rgba(239,68,68,0.25)"
      : "none"};
  appearance: none;
`;
const Qty = styled.span`
  min-width: 20px;
  display: inline-block;
  text-align: center;
`;
const QtyInput = styled.input`
  width: 52px;
  height: 34px;
  text-align: center;
  border-radius: 8px;
  border: 1px solid var(--line);
  background: #fff;
  font-weight: 800;
  outline: none;
  cursor: pointer;
  appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const ClearLine = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  border: 1px solid #fca5a5;
  background: #fff5f5;
  color: #b91c1c;
  font-weight: 900;
  cursor: pointer;
  display: grid;
  place-items: center;
`;

const RightSummary = styled.aside`
  animation: slideIn 0.25s ease;
  position: sticky;
  top: 12px;
  align-self: start;
  max-height: calc(90vh - var(--header-h) - 24px);
  height: calc(90vh - var(--header-h) - 0px);
  overflow: hidden;
  @media (max-width: 1024px) {
    position: static;
    max-height: none;
    // height: auto;
    overflow: visible;
    margin-top: 12px;
  }

  @keyframes slideIn {
    from {
      transform: translateX(20px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;
const Sticky = styled.div`
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;
const SummaryBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-bottom: 12px;
`;
const SummaryFooter = styled.div`
  position: sticky;
  bottom: 0;
  background: #fff;
  // padding-top: 8px;
  border-top: 1px solid var(--line);
  margin-top: auto;
`;
const KV = styled.div`
  display: flex;
  justify-content: space-between;
  color: ${({ $muted }) => ($muted ? "var(--muted)" : "#111827")};
  margin: 6px 0;
  b {
    font-weight: 700;
  }
`;
const DeliveryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 8px 0;
`;
const DeliveryToggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  input {
    accent-color: var(--blue);
  }
`;
const DeliveryInputWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fff;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  span {
    font-weight: 700;
    color: var(--blue);
  }
  input {
    width: 80px;
    border: none;
    outline: none;
    font-size: 13px;
    background: transparent;
    text-align: right;
  }
`;
const Divider = styled.div`
  height: 1px;
  background: var(--line);
  margin: 6px 0 10px;
`;
const Grand = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 10px;
  span {
    color: #111827;
    font-weight: 700;
  }
  strong {
    font-size: 20px;
  }
`;
const ItemTable = styled.div`
  border: 1px solid var(--line);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 10px;
`;
const ItemHeader = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 0.6fr 0.8fr 1fr;
  padding: 8px 10px;
  background: #f3f4f6;
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  span {
    white-space: nowrap;
  }
`;
const ItemRow = styled(ItemHeader)`
  background: #fff;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  border-top: 1px solid var(--line);
  span {
    white-space: nowrap;
  }
`;
const ItemName = styled.div`
  white-space: normal;
  word-break: break-word;
  line-height: 1.3;
  max-height: 3.1em; /* two lines */
  overflow: hidden;
`;
const ItemTotal = styled.span`
  white-space: nowrap;
  overflow: visible;
  text-overflow: clip;
`;
const SeeMoreBtn = styled.button`
  width: 100%;
  margin-top: 8px;
  border: 1px solid #015b9a;
  background: #015b9a;
  color: #ffffff;
  border-radius: 10px;
  padding: 8px 10px;
  font-weight: 700;
  cursor: pointer;
`;
const TaxBlock = styled.div`
  margin: 12px 0;
  padding: 10px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #f9fafb;
`;
const TaxTitle = styled.div`
  font-weight: 700;
  margin-bottom: 6px;
  color: #111827;
`;
const TaxHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  font-size: 12px;
  color: #6b7280;
  font-weight: 700;
  margin-bottom: 4px;
`;
const TaxRow = styled(TaxHeader)`
  color: #111827;
  font-weight: 600;
  margin-bottom: 2px;
`;
const TaxTotal = styled(TaxHeader)`
  font-weight: 800;
  border-top: 1px solid var(--line);
  padding-top: 6px;
  margin-top: 6px;
`;
const SummaryHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;
const ToggleSummary = styled.button`
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid var(--line);
  background: #fff;
  cursor: pointer;
  font-weight: 600;
`;
const SummaryFloating = styled.button`
  position: fixed;
  right: 22px;
  top: 90px;
  z-index: 50;
  height: 38px;
  padding: 0 14px;
  border-radius: 10px;
  border: none;
  background: var(--blue);
  color: #fff;
  font-weight: 700;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
  cursor: pointer;

  @media (max-width: 1024px) {
    position: static;
    width: 100%;
    margin-bottom: 12px;
  }
`;
const PostBillOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.52);
  z-index: 75;
  display: grid;
  place-items: center;
  padding: 16px;
`;
const PostBillCard = styled.div`
  width: min(620px, calc(100vw - 24px));
  max-height: min(86vh, 760px);
  overflow: auto;
  background: #fff;
  border: 1px solid #dbe7f4;
  border-radius: 16px;
  box-shadow: 0 24px 48px rgba(2, 12, 33, 0.28);
  padding: 14px;
`;
const PostBillHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  h3 {
    margin: 0;
    color: #0f172a;
    font-size: 19px;
    font-weight: 900;
  }
  p {
    margin: 4px 0 0;
    color: #475569;
    font-size: 13px;
    font-weight: 700;
  }
`;
const PostBillMetaGrid = styled.div`
  margin-top: 10px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  div {
    border: 1px solid #dbe7f4;
    border-radius: 10px;
    padding: 7px 8px;
    background: #f8fbff;
  }
  small {
    display: block;
    color: #64748b;
    font-size: 11px;
    font-weight: 700;
    margin-bottom: 2px;
  }
  strong {
    color: #0f172a;
    font-size: 12px;
    font-weight: 800;
    word-break: break-word;
  }
`;
const PostBillQrWrap = styled.div`
  margin-top: 10px;
  border: 1px dashed #cbdff3;
  border-radius: 10px;
  padding: 8px;
  display: flex;
  justify-content: center;
  background: #f8fbff;
  img {
    width: 130px;
    height: 130px;
    object-fit: contain;
    border-radius: 6px;
  }
`;
const PostBillClose = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid #d1dbe8;
  background: #f8fafc;
  color: #334155;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
`;
const PostBillBody = styled.div`
  margin-top: 12px;
  border: 1px solid #dbe6f2;
  border-radius: 12px;
  overflow: hidden;
`;
const PostBillRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 10px;
  align-items: center;
  padding: 9px 10px;
  border-bottom: 1px solid #e5edf6;
  font-size: 13px;
  color: #0f172a;
  &:last-child {
    border-bottom: 0;
  }
  span:first-child {
    font-weight: 700;
  }
  span:nth-child(2) {
    color: #475569;
    font-weight: 700;
  }
  span:nth-child(3) {
    font-weight: 800;
  }
`;
const PostBillTotals = styled.div`
  margin-top: 10px;
  border: 1px solid #dbe6f2;
  border-radius: 12px;
  padding: 10px;
  display: grid;
  gap: 4px;
  color: #334155;
  font-size: 13px;
  font-weight: 700;
  strong {
    margin-top: 3px;
    color: #0f172a;
    font-size: 15px;
  }
`;
const PostBillActions = styled.div`
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
`;
const ActionCol = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;
  margin-bottom: 8px;
  position: sticky;
  bottom: 10px;
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
  padding: 12px;
  border-radius: 14px;
  border: 1px solid #dbe7f4;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.1);

  @media (max-width: 640px) {
    position: static;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;
const ActionBtn = styled(Button)`
  min-height: 56px;
  height: auto;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid #0d6aa9;
  background: linear-gradient(180deg, #0f77bc 0%, #015b9a 100%);
  color: #fff;
  font-size: 15px;
  font-weight: 750;
  letter-spacing: 0.1px;
  line-height: 1.2;
  text-align: center;
  white-space: normal;
  box-shadow: 0 10px 20px rgba(1, 91, 154, 0.2);
  transition: transform 0.15s ease, box-shadow 0.2s ease, filter 0.2s ease;

  svg {
    font-size: 15px;
    flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 14px 24px rgba(1, 91, 154, 0.24);
    filter: saturate(1.03);
  }

  &:disabled {
    opacity: 1;
    cursor: not-allowed;
    background: #7fa5c8;
    border-color: #7fa5c8;
    color: #eef5fc;
    box-shadow: none;
    transform: none;
  }
`;

/* ---------- Modal ---------- */
const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: grid;
  place-items: center;
  z-index: 60;
`;
const ModalCard = styled.div`
  background: #fff;
  width: min(440px, 92vw);
  border-radius: 14px;
  padding: 18px;
  border: 1px solid var(--line);

  h3 {
    margin: 0 0 10px;
  }
  input {
    width: 100%;
    padding: 10px 12px;
    border-radius: 10px;
    border: 1px solid var(--line);
    margin: 8px 0 12px;
    box-sizing: border-box;
  }
  textarea {
    box-sizing: border-box;
  }
`;
const KotCard = styled(ModalCard)`
  width: min(560px, 92vw);
  padding: 16px 18px 18px;
`;
const KotHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;

  h3 {
    margin: 0 0 4px;
  }
`;
const KotSub = styled.div`
  color: #64748b;
  font-weight: 600;
  font-size: 13px;
`;
const KotHeaderBadge = styled.div`
  background: #e0f2fe;
  color: #0f172a;
  font-weight: 700;
  font-size: 12px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid #bae6fd;
`;
const KotToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: #6b7280;
  font-size: 13px;
  margin: 8px 0 10px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;
const KotMiniBtn = styled.button`
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #0f172a;
  font-weight: 700;
  padding: 6px 10px;
  border-radius: 10px;
  cursor: pointer;
  margin-left: 6px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const KotList = styled.div`
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
`;
const KotRow = styled.label`
  display: grid;
  grid-template-columns: 20px 1fr auto;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
  opacity: ${({ $inactive }) => ($inactive ? 0.5 : 1)};

  &:last-child {
    border-bottom: none;
  }

  input {
    margin: 0;
    accent-color: var(--blue);
  }

  .name {
    font-weight: 700;
    color: #111827;
    margin-bottom: 2px;
  }

  .meta {
    color: #64748b;
    font-size: 12px;
    font-weight: 600;
  }

  .price {
    font-weight: 700;
    color: #0f172a;
  }
`;
const KotNotesWrap = styled.div`
  margin-top: 12px;

  label {
    display: block;
    font-weight: 700;
    color: #111827;
    margin-bottom: 6px;
  }

  textarea {
    width: 95%;
    border-radius: 12px;
    border: 1px solid var(--line);
    padding: 10px 12px;
    resize: vertical;
    margin-bottom: 8px;
  }
`;
const KotTagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
const KotTagBtn = styled.button`
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? "#015b9a" : "#e2e8f0")};
  background: ${({ $active }) =>
    $active ? "rgba(1,91,154,0.12)" : "#f8fafc"};
  color: ${({ $active }) => ($active ? "#015b9a" : "#0f172a")};
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
`;
const KotSentBox = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;

  strong {
    display: block;
    margin-bottom: 6px;
  }

  div {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  span {
    padding: 4px 8px;
    border-radius: 999px;
    background: #e0f2fe;
    color: #0f172a;
    font-weight: 700;
    font-size: 12px;
  }
`;
const OptionsCard = styled(ModalCard)`
  width: min(560px, 92vw);
  max-height: 82vh;
  overflow: auto;
`;
const OptionGroup = styled.div`
  margin: 14px 0 0;
`;
const OptionTitle = styled.div`
  font-weight: 700;
  margin-bottom: 8px;
`;
const OptionGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
`;
const OptionBtn = styled.button`
  text-align: left;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid
    ${({ $active }) => ($active ? "var(--blue)" : "var(--line)")};
  background: ${({ $active }) => ($active ? "var(--blue-ghost)" : "#fff")};
  color: ${({ $active }) => ($active ? "var(--blue)" : "#111827")};
  cursor: pointer;
  font-weight: 700;

  small {
    display: block;
    color: #64748b;
    font-weight: 600;
    margin-top: 2px;
  }
`;
const AddonList = styled.div`
  display: grid;
  gap: 8px;
`;
const AddonRow = styled.label`
  display: grid;
  grid-template-columns: 20px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid var(--line);
  background: #f8fafc;
  font-weight: 600;
  small {
    color: #64748b;
    font-weight: 600;
  }
`;
const OptionSummary = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px dashed #cbd5f5;
  background: #f8fafc;
  margin: 12px 0;
`;
const ModalRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;
const SplitRow = styled.div`
  display: grid;
  grid-template-columns: 90px 1fr;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
`;
const SplitLabel = styled.span`
  font-weight: 700;
  color: #111827;
`;
const SplitInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid var(--line);
  box-sizing: border-box;
`;
const SplitMetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin: 6px 0 12px;
  font-size: 12px;
  color: #64748b;
  flex-wrap: wrap;
`;
const SplitHint = styled.button`
  width: 100%;
  border: 1px solid #fde68a;
  background: #fffbeb;
  color: #92400e;
  font-weight: 700;
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  margin-bottom: 10px;
`;
const SplitSummary = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #64748b;
  margin-top: 6px;
`;

const tabBtnStyles = {
  flex: 1,
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 700,
};

const chipBtnStyles = {
  padding: "8px 12px",
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  background: "#f8fafc",
  cursor: "pointer",
  fontWeight: 700,
};
